function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
(function () {
  'use strict';

  // ── src/lib/bread/core.mjs ──
  // Sangak Club — the shared pure core of the bread subscription.
  //
  // ONE source of truth for prices, dates, cutoffs, windows, capacity and rules, used by BOTH:
  //   • the server (imported as an ES module by src/lib/bread/*.ts and the API routes), and
  //   • the browser bundles (scripts/compile-app.mjs inlines this file into bread.js / bread-plan.js
  //     after stripping the `export ` keywords, inside an IIFE).
  // So: NO imports, NO default export, every export written as `export function` / `export const`
  // at the start of a line, plain JS only (no TypeScript), no DOM / Node APIs.
  //
  // Money is handled in integer FILS (1 AED = 100 fils) and only turned back into AED at the edges.
  // Dates are Dubai wall-clock 'YYYY-MM-DD' strings (UTC+4 all year, no DST).
  //
  // Model (v3, after the 2026-10-16 critique panel):
  //   • A plan runs in 28-day CYCLES from its start date ("monthly plan · 4 weeks"); renewal = next cycle.
  //     ROLLING (2026-10-17): it continues every 4 weeks until stopped — a pay-only link before each cycle, the card
  //     is charged only when the customer taps Pay; unpaid by the first new day's cutoff → the plan pauses.
  //   • Delivery is priced per FEE BAND (the area's base fee 15/20/25/30/40/50 from the 141-area table), with
  //     a subscriber fee per band and a minimum number of breads per band (far drops must be worth the trip).
  //   • Deliveries arrive in owner-defined WINDOWS ("07:00–07:30" for slot "07:30"); capacity is per window,
  //     halved on weekend mornings, plus a daily bread cap — sized to the real oven (~120 loaves/day).
  const BREAD_DEFAULTS = {
    enabled: false,
    // master switch for the PUBLIC builder (existing plans always work)
    accepting_new: true,
    // false → /bread shows "full — message us" instead of the builder
    fulfillments: ['delivery', 'pickup'],
    breads: ['sangak', 'sangak-x'],
    extras: ['torshi-mix', 'torshi-seer', 'doogh', 'omelette', 'dish-1'],
    bread_discount_pct: 10,
    fee_by_band: {
      15: 0,
      20: 5,
      25: 7.5,
      30: 10,
      40: 15,
      50: 20
    },
    min_breads_by_band: {
      15: 3,
      20: 3,
      25: 4,
      30: 4,
      40: 5,
      50: 6
    },
    min_breads_pickup: 3,
    max_breads_drop: 20,
    max_extras_qty: 6,
    lead_by_band: {
      15: 50,
      20: 55,
      25: 65,
      30: 70,
      40: 80,
      50: 90
    },
    lead_pickup: 30,
    windows: ['07:30', '09:00', '10:30', '13:00', '17:00', '19:30'],
    window_len_min: 30,
    cap_breads_per_window: 25,
    cap_drops_per_window: 6,
    weekend_days: [5, 6, 0],
    // Fri, Sat, Sun — the busiest kitchen mornings
    weekend_morning_until: '11:00',
    weekend_morning_factor: 0.5,
    cap_breads_per_day: 80,
    max_active_plans: 40,
    cycle_days: 28,
    min_drops_new_plan: 2,
    max_plan_aed: 2000,
    cutoff_hour: 20,
    checkout_margin_min: 30,
    hold_min: 20,
    hold_max_min: 40,
    // an unpaid checkout never holds capacity longer than this after it was created
    renewal_open_days_before: 7,
    renewal_remind_days_before: 3,
    reminder_hour: 17,
    reminder_until_hour: 19,
    digest_hour: 20,
    digest_minute: 25,
    blackout_dates: [],
    reject_credit_pct: 100,
    min_topup_aed: 20,
    auto_partial_refunds: true,
    idle_credit_refund_days: 30,
    max_pause_days: 21,
    // a customer pause covers at most this many days (rolling plan)
    paused_autostop_days: 60 // an UNPAID pause older than this stops the plan (balance back to the card)
  };
  const VAT_PCT = 5;
  const BANDS = [15, 20, 25, 30, 40, 50];
  const DUBAI_OFFSET_MS = 4 * 3600 * 1000;
  const DAY_MS = 86400000;

  // ─── small utils ──────────────────────────────────────────────────────────────
  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }
  function clampNum(v, lo, hi, dflt) {
    const n = Number(v);
    if (v === null || v === undefined || v === '' || !Number.isFinite(n)) return dflt;
    return Math.min(hi, Math.max(lo, n));
  }
  function clampInt(v, lo, hi, dflt) {
    const n = clampNum(v, lo, hi, dflt);
    return Math.round(n);
  }
  function isHHMM(s) {
    return typeof s === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
  }
  function isYmd(s) {
    return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z')) && new Date(s + 'T00:00:00Z').toISOString().slice(0, 10) === s;
  }
  function slugList(v, dflt) {
    if (!Array.isArray(v)) return dflt.slice();
    const out = [];
    for (const x of v) if (typeof x === 'string' && /^[a-z0-9-]{1,40}$/.test(x) && out.indexOf(x) < 0) out.push(x);
    return out;
  }
  function bandMap(v, dflt, lo, hi, isInt) {
    const out = {};
    for (const b of BANDS) {
      const raw = v && typeof v === 'object' ? v[b] : undefined;
      const x = clampNum(raw, lo, hi, dflt[b]);
      out[b] = isInt ? Math.round(x) : Math.round(x * 100) / 100;
    }
    return out;
  }

  /** Merge stored (owner-edited) settings over the defaults, clamping every value into a sane range. */
  function mergeSettings(raw) {
    const r = raw && typeof raw === 'object' ? raw : {};
    const d = BREAD_DEFAULTS;
    const s = {};
    s.enabled = typeof r.enabled === 'boolean' ? r.enabled : d.enabled;
    s.accepting_new = typeof r.accepting_new === 'boolean' ? r.accepting_new : d.accepting_new;
    const ful = Array.isArray(r.fulfillments) ? r.fulfillments.filter(x => x === 'delivery' || x === 'pickup') : d.fulfillments.slice();
    s.fulfillments = ful.length ? ful.filter((x, i) => ful.indexOf(x) === i) : d.fulfillments.slice();
    s.breads = slugList(r.breads, d.breads);
    if (!s.breads.length) s.breads = d.breads.slice();
    s.extras = (Array.isArray(r.extras) ? slugList(r.extras, []) : d.extras.slice()).filter(x => s.breads.indexOf(x) < 0);
    s.bread_discount_pct = clampInt(r.bread_discount_pct, 0, 30, d.bread_discount_pct);
    s.fee_by_band = bandMap(r.fee_by_band, d.fee_by_band, 0, 60, false);
    s.min_breads_by_band = bandMap(r.min_breads_by_band, d.min_breads_by_band, 1, 20, true);
    s.min_breads_pickup = clampInt(r.min_breads_pickup, 1, 20, d.min_breads_pickup);
    s.max_breads_drop = clampInt(r.max_breads_drop, 3, 50, d.max_breads_drop);
    s.max_extras_qty = clampInt(r.max_extras_qty, 0, 20, d.max_extras_qty);
    s.lead_by_band = bandMap(r.lead_by_band, d.lead_by_band, 20, 240, true);
    s.lead_pickup = clampInt(r.lead_pickup, 10, 240, d.lead_pickup);
    const wins = Array.isArray(r.windows) ? r.windows.filter(isHHMM) : d.windows.slice();
    s.windows = (wins.length ? wins : d.windows.slice()).filter((x, i, a) => a.indexOf(x) === i).sort().slice(0, 16);
    s.window_len_min = clampInt(r.window_len_min, 15, 120, d.window_len_min);
    s.cap_breads_per_window = clampInt(r.cap_breads_per_window, 1, 500, d.cap_breads_per_window);
    s.cap_drops_per_window = clampInt(r.cap_drops_per_window, 1, 100, d.cap_drops_per_window);
    s.weekend_days = Array.isArray(r.weekend_days) ? r.weekend_days.map(Number).filter(x => Number.isInteger(x) && x >= 0 && x <= 6).filter((x, i, a) => a.indexOf(x) === i) : d.weekend_days.slice();
    s.weekend_morning_until = isHHMM(r.weekend_morning_until) ? r.weekend_morning_until : d.weekend_morning_until;
    s.weekend_morning_factor = clampNum(r.weekend_morning_factor, 0.1, 1, d.weekend_morning_factor);
    s.cap_breads_per_day = clampInt(r.cap_breads_per_day, 1, 2000, d.cap_breads_per_day);
    s.max_active_plans = clampInt(r.max_active_plans, 1, 5000, d.max_active_plans);
    s.cycle_days = clampInt(r.cycle_days, 14, 35, d.cycle_days);
    s.min_drops_new_plan = clampInt(r.min_drops_new_plan, 1, 28, d.min_drops_new_plan);
    s.max_plan_aed = clampInt(r.max_plan_aed, 100, 20000, d.max_plan_aed);
    s.cutoff_hour = clampInt(r.cutoff_hour, 0, 23, d.cutoff_hour);
    s.checkout_margin_min = clampInt(r.checkout_margin_min, 0, 180, d.checkout_margin_min);
    s.hold_min = clampInt(r.hold_min, 5, 120, d.hold_min);
    s.hold_max_min = clampInt(r.hold_max_min, Math.max(20, s.hold_min), 120, Math.max(d.hold_max_min, s.hold_min));
    s.renewal_open_days_before = clampInt(r.renewal_open_days_before, 2, 14, d.renewal_open_days_before);
    s.renewal_remind_days_before = clampInt(r.renewal_remind_days_before, 1, s.renewal_open_days_before, Math.min(d.renewal_remind_days_before, s.renewal_open_days_before));
    s.reminder_hour = clampInt(r.reminder_hour, 6, 21, d.reminder_hour);
    s.reminder_until_hour = clampInt(r.reminder_until_hour, s.reminder_hour, 22, Math.max(s.reminder_hour, d.reminder_until_hour));
    s.digest_hour = clampInt(r.digest_hour, 0, 23, d.digest_hour);
    s.digest_minute = clampInt(r.digest_minute, 0, 59, d.digest_minute);
    s.blackout_dates = Array.isArray(r.blackout_dates) ? r.blackout_dates.filter(isYmd).filter((x, i, a) => a.indexOf(x) === i).sort().slice(0, 200) : [];
    s.reject_credit_pct = clampInt(r.reject_credit_pct, 0, 100, d.reject_credit_pct);
    s.min_topup_aed = clampInt(r.min_topup_aed, 1, 500, d.min_topup_aed);
    s.auto_partial_refunds = typeof r.auto_partial_refunds === 'boolean' ? r.auto_partial_refunds : d.auto_partial_refunds;
    s.idle_credit_refund_days = clampInt(r.idle_credit_refund_days, 7, 365, d.idle_credit_refund_days);
    s.max_pause_days = clampInt(r.max_pause_days, 7, 35, d.max_pause_days);
    s.paused_autostop_days = clampInt(r.paused_autostop_days, 30, 180, d.paused_autostop_days);
    return s;
  }

  /** Settings safe to send to any browser (no operational secrets exist in settings, but keep the payload lean). */
  function publicSettings(s) {
    return {
      enabled: s.enabled,
      accepting_new: s.accepting_new,
      fulfillments: s.fulfillments,
      breads: s.breads,
      extras: s.extras,
      bread_discount_pct: s.bread_discount_pct,
      fee_by_band: s.fee_by_band,
      min_breads_by_band: s.min_breads_by_band,
      min_breads_pickup: s.min_breads_pickup,
      max_breads_drop: s.max_breads_drop,
      max_extras_qty: s.max_extras_qty,
      windows: s.windows,
      window_len_min: s.window_len_min,
      cycle_days: s.cycle_days,
      min_drops_new_plan: s.min_drops_new_plan,
      max_plan_aed: s.max_plan_aed,
      cutoff_hour: s.cutoff_hour,
      checkout_margin_min: s.checkout_margin_min,
      blackout_dates: s.blackout_dates,
      weekend_days: s.weekend_days,
      min_topup_aed: s.min_topup_aed,
      lead_by_band: s.lead_by_band,
      lead_pickup: s.lead_pickup,
      max_pause_days: s.max_pause_days,
      // the rolling-plan copy says "{days} before each new 4 weeks…" / "paused for {days}, so we closed it"
      renewal_open_days_before: s.renewal_open_days_before,
      paused_autostop_days: s.paused_autostop_days
    };
  }

  // ─── money ────────────────────────────────────────────────────────────────────
  function toFils(aed) {
    const n = Number(aed);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }
  function filsToAed(f) {
    return Math.round(f) / 100;
  }
  /** "12.50" style string for storage / display. */
  function filsStr(f) {
    return (Math.round(f) / 100).toFixed(2);
  }
  /** Subscriber unit price in fils from a menu price in fils. */
  function subUnitFils(menuFils, pct) {
    return Math.round(menuFils * (100 - pct) / 100);
  }
  /** VAT in fils, fils-exact (5% of goods, half-up). */
  function vatFils(goodsFils) {
    return Math.floor((goodsFils * VAT_PCT + 50) / 100);
  }

  // ─── Dubai calendar ───────────────────────────────────────────────────────────
  function hhmmToMin(s) {
    const p = String(s).split(':');
    return Number(p[0]) * 60 + Number(p[1]);
  }
  function minToHHMM(m) {
    const x = (m % 1440 + 1440) % 1440;
    return pad2(Math.floor(x / 60)) + ':' + pad2(x % 60);
  }
  /** Dubai calendar date of an instant. */
  function dubaiYmd(ms) {
    const d = new Date(ms + DUBAI_OFFSET_MS);
    return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate());
  }
  /** Dubai wall-clock minutes since midnight of an instant. */
  function dubaiMinuteOfDay(ms) {
    const d = new Date(ms + DUBAI_OFFSET_MS);
    return d.getUTCHours() * 60 + d.getUTCMinutes();
  }
  function ymdAdd(ymd, n) {
    const d = new Date(Date.parse(ymd + 'T00:00:00Z') + n * DAY_MS);
    return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate());
  }
  /** 0 = Sunday … 6 = Saturday (the Dubai calendar day itself). */
  function ymdWeekday(ymd) {
    return new Date(ymd + 'T00:00:00Z').getUTCDay();
  }
  /** UTC epoch ms of a Dubai wall-clock date + 'HH:MM'. */
  function ymdToMs(ymd, hhmm) {
    return Date.parse(ymd + 'T' + (hhmm || '00:00') + ':00Z') - DUBAI_OFFSET_MS;
  }
  /** ISO with explicit +04:00 offset (what the ERP and Directus expect). */
  function ymdIso(ymd, hhmm) {
    return ymd + 'T' + (hhmm || '00:00') + ':00+04:00';
  }
  function ymdDiff(a, b) {
    return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / DAY_MS);
  }
  function monthOf(ymd) {
    return ymd.slice(0, 7);
  }
  function daysInMonth(ym) {
    const y = +ym.slice(0, 4),
      m = +ym.slice(5, 7);
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
  }
  function monthDays(ym) {
    const n = daysInMonth(ym),
      out = [];
    for (let i = 1; i <= n; i++) out.push(ym + '-' + pad2(i));
    return out;
  }
  function nextMonth(ym) {
    const y = +ym.slice(0, 4),
      m = +ym.slice(5, 7);
    return m === 12 ? y + 1 + '-01' : y + '-' + pad2(m + 1);
  }
  function prevMonth(ym) {
    const y = +ym.slice(0, 4),
      m = +ym.slice(5, 7);
    return m === 1 ? y - 1 + '-12' : y + '-' + pad2(m - 1);
  }
  function isValidYmd(s) {
    return isYmd(s);
  }
  function isValidHHMM(s) {
    return isHHMM(s);
  }

  // ─── windows, bands, leads ────────────────────────────────────────────────────
  /** Normalise any base fee to its band key (15/20/25/30/40/50); unknown → the next band up (never cheaper). */
  function bandOf(baseFeeAed) {
    const f = Number(baseFeeAed);
    for (const b of BANDS) if (f <= b) return b;
    return BANDS[BANDS.length - 1];
  }
  function isValidWindow(slot, s) {
    return s.windows.indexOf(slot) >= 0;
  }
  /** The arrival window that ENDS at `slot`: {from:'07:00', to:'07:30'}. */
  function windowRange(slot, s) {
    return {
      from: minToHHMM(hhmmToMin(slot) - s.window_len_min),
      to: slot
    };
  }
  /** Minutes before the slot that the order is released to the kitchen (scheduled_fire_at = slot − lead). */
  function leadMin(fulfillment, band, s) {
    return fulfillment === 'pickup' ? s.lead_pickup : s.lead_by_band[bandOf(band)] || 90;
  }
  function minBreads(fulfillment, band, s) {
    return fulfillment === 'pickup' ? s.min_breads_pickup : s.min_breads_by_band[bandOf(band)] || 3;
  }
  /** Subscriber delivery fee for a band, in fils (pickup = 0). */
  function feeFilsFor(fulfillment, band, s) {
    return fulfillment === 'pickup' ? 0 : toFils(s.fee_by_band[bandOf(band)]);
  }
  function isWeekendMorning(ymd, slot, s) {
    return s.weekend_days.indexOf(ymdWeekday(ymd)) >= 0 && hhmmToMin(slot) <= hhmmToMin(s.weekend_morning_until);
  }
  /** Capacity of one window on one day (weekend mornings shrink — the counter is busiest then). */
  function windowCap(ymd, slot, s) {
    const f = isWeekendMorning(ymd, slot, s) ? s.weekend_morning_factor : 1;
    return {
      breads: Math.max(1, Math.floor(s.cap_breads_per_window * f)),
      drops: Math.max(1, Math.floor(s.cap_drops_per_window * f))
    };
  }

  // ─── cutoffs, plannable days, cycles, patterns ───────────────────────────────
  /** Edits/skips for a drop on `ymd` close at cutoff_hour:00 Dubai on the day BEFORE. */
  function cutoffMs(ymd, s) {
    return ymdToMs(ymdAdd(ymd, -1), pad2(s.cutoff_hour) + ':00');
  }
  function isBlackout(ymd, s) {
    return s.blackout_dates.indexOf(ymd) >= 0;
  }
  /**
   * Can a NEW drop be planned on `ymd` right now? marginMin keeps checkout honest: a plan paid at 19:58 must not
   * contain a drop whose 20:00 cutoff passes while the customer is still on the card page.
   */
  function plannableDay(ymd, nowMs, s, marginMin) {
    if (!isYmd(ymd)) return {
      ok: false,
      reason: 'bad_date'
    };
    const margin = (marginMin == null ? 0 : marginMin) * 60000;
    if (cutoffMs(ymd, s) <= nowMs + margin) return {
      ok: false,
      reason: 'too_soon'
    };
    if (ymdDiff(dubaiYmd(nowMs), ymd) > s.cycle_days * 2 + 7) return {
      ok: false,
      reason: 'too_far'
    };
    if (isBlackout(ymd, s)) return {
      ok: false,
      reason: 'blackout'
    };
    return {
      ok: true
    };
  }
  /** First date a new plan can start on. */
  function earliestPlannableYmd(nowMs, s, marginMin) {
    let y = dubaiYmd(nowMs);
    for (let i = 0; i < 400; i++) {
      if (plannableDay(y, nowMs, s, marginMin).ok) return y;
      y = ymdAdd(y, 1);
    }
    return y;
  }
  /** A cycle is [start, start + cycle_days − 1]. */
  function cycleRange(startYmd, s) {
    return {
      start: startYmd,
      end: ymdAdd(startYmd, s.cycle_days - 1)
    };
  }
  /**
   * Dates in [fromYmd, toYmd] matching a weekly pattern. every_weeks=2 keeps weeks 0,2,4… counted from `anchorYmd`
   * (the plan's first day), so "every other Friday" stays on the same Fridays across renewals.
   * Only plannable days are returned when nowMs is given.
   */
  function patternDays(fromYmd, toYmd, pattern, nowMs, s, marginMin) {
    const out = [];
    if (!isYmd(fromYmd) || !isYmd(toYmd) || !pattern || !Array.isArray(pattern.weekdays)) return out;
    const every = pattern.every_weeks === 2 ? 2 : 1;
    const anchor = isYmd(pattern.anchor) ? pattern.anchor : fromYmd;
    let y = fromYmd,
      guard = 0;
    while (y <= toYmd && guard++ < 400) {
      const wk = Math.floor(ymdDiff(anchor, y) / 7);
      const weekOk = every === 1 || (wk % 2 + 2) % 2 === 0;
      if (weekOk && pattern.weekdays.indexOf(ymdWeekday(y)) >= 0 && (nowMs == null || plannableDay(y, nowMs, s, marginMin).ok)) out.push(y);
      y = ymdAdd(y, 1);
    }
    return out;
  }

  // ─── pricing ──────────────────────────────────────────────────────────────────
  /**
   * catalog: { [slug]: { price: <menu AED>, kind: 'bread'|'extra', available?: bool } }
   * units (optional): { [slug]: <locked subscriber unit price AED> } — the plan's price lock.
   */
  function unitPriceFils(slug, catalog, s, units) {
    if (units && units[slug] != null && Number.isFinite(Number(units[slug])) && Number(units[slug]) > 0) return toFils(units[slug]);
    const c = catalog[slug];
    if (!c) return null;
    const menu = toFils(c.price);
    return c.kind === 'bread' ? subUnitFils(menu, s.bread_discount_pct) : menu;
  }

  /** Normalise [{slug,qty}] → merged, integer qty>0, known slugs only (order kept). */
  function normItems(items, catalog) {
    const out = [],
      idx = {};
    if (!Array.isArray(items)) return out;
    for (const it of items) {
      if (!it || typeof it.slug !== 'string' || !catalog[it.slug]) continue;
      const q = Math.floor(Number(it.qty));
      if (!Number.isFinite(q) || q <= 0) continue;
      if (idx[it.slug] != null) out[idx[it.slug]].qty += q;else {
        idx[it.slug] = out.length;
        out.push({
          slug: it.slug,
          qty: q
        });
      }
    }
    return out;
  }
  function countBreads(items, catalog) {
    let n = 0;
    for (const it of normItems(items, catalog)) if (catalog[it.slug].kind === 'bread') n += it.qty;
    return n;
  }

  /** Validation errors for one drop's contents ([] = ok). Codes are i18n keys on the client. */
  function validateDropItems(items, fulfillment, band, s, catalog) {
    const errs = [];
    const n = normItems(items, catalog);
    let breads = 0;
    for (const it of n) {
      const c = catalog[it.slug];
      if (c.kind === 'bread') breads += it.qty;else if (it.qty > s.max_extras_qty) errs.push('too_many_extras');
      if (c.available === false) errs.push('unavailable:' + it.slug);
    }
    if (breads < minBreads(fulfillment, band, s)) errs.push('min_breads');
    if (breads > s.max_breads_drop) errs.push('max_breads');
    return errs;
  }

  /**
   * Price one drop. `band` = the area's base-fee band (ignored for pickup). Optional `units` (price lock) and
   * `feeAed` (frozen per-drop delivery fee) override the live tables so a plan's prices never move under it.
   */
  function priceDrop(o) {
    const s = o.s,
      catalog = o.catalog;
    const n = normItems(o.items, catalog);
    const lines = [];
    let goods = 0,
      breads = 0,
      menuGoods = 0;
    for (const it of n) {
      const c = catalog[it.slug];
      const unit = unitPriceFils(it.slug, catalog, s, o.units);
      const menu = toFils(c.price);
      const line = unit * it.qty;
      goods += line;
      menuGoods += menu * it.qty;
      if (c.kind === 'bread') breads += it.qty;
      lines.push({
        slug: it.slug,
        qty: it.qty,
        kind: c.kind,
        unit_fils: unit,
        menu_fils: menu,
        line_fils: line
      });
    }
    const fee = o.fulfillment === 'pickup' ? 0 : o.feeAed != null && Number.isFinite(Number(o.feeAed)) ? toFils(o.feeAed) : feeFilsFor('delivery', o.band, s);
    const vat = vatFils(goods);
    return {
      lines,
      breads,
      goods_fils: goods,
      menu_goods_fils: menuGoods,
      fee_fils: fee,
      vat_fils: vat,
      total_fils: goods + fee + vat
    };
  }

  /**
   * What the same drop would cost as a normal website order today (create.ts rules): menu prices, VAT rounded to
   * whole AED, delivery = base × (1 − promo%), free when the food subtotal ≥ AED 300. Used only for "you save".
   */
  function regularCostFils(o) {
    const n = normItems(o.items, o.catalog);
    let sub = 0;
    for (const it of n) sub += toFils(o.catalog[it.slug].price) * it.qty;
    const vat = Math.round(sub / 100 * VAT_PCT / 100) * 100;
    let fee = 0;
    if (o.fulfillment !== 'pickup') {
      fee = Math.round(toFils(o.baseFeeAed || 0) * (100 - (Number(o.promoPct) || 0)) / 100);
      if (fee > 0 && sub >= 30000) fee = 0;
    }
    return sub + vat + fee;
  }

  /** Sum a list of priced drops ({goods_fils, fee_fils, vat_fils, total_fils, breads}). */
  function sumDrops(priced) {
    const t = {
      drops: 0,
      breads: 0,
      goods_fils: 0,
      fee_fils: 0,
      vat_fils: 0,
      total_fils: 0
    };
    for (const p of priced) {
      t.drops++;
      t.breads += p.breads || 0;
      t.goods_fils += p.goods_fils;
      t.fee_fils += p.fee_fils;
      t.vat_fils += p.vat_fils;
      t.total_fils += p.total_fils;
    }
    return t;
  }
  /** Amount due after applying wallet credit (never negative). */
  function amountDueFils(totalFils, creditFils) {
    return Math.max(0, totalFils - Math.max(0, creditFils));
  }
  /** Price lock for a plan: subscriber unit price (AED) of every catalog slug. */
  function buildUnits(catalog, s) {
    const b = {};
    for (const slug in catalog) {
      const u = unitPriceFils(slug, catalog, s, null);
      if (u != null) b[slug] = filsToAed(u);
    }
    return b;
  }

  /** "SC-" refs: unambiguous uppercase alphabet (no 0/O/1/I). */
  const REF_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  /** Free text that will travel into WhatsApp template params / POS notes: one line, no control chars, capped. */
  function isCtrlCode(c) {
    return c < 32 || c === 127 || c === 0x2028 || c === 0x2029;
  }
  function cleanLine(v, max) {
    const str = String(v == null ? '' : v);
    let out = '';
    for (let i = 0; i < str.length; i++) out += isCtrlCode(str.charCodeAt(i)) ? ' ' : str.charAt(i);
    return out.replace(/ {2,}/g, ' ').trim().slice(0, max || 200);
  }
  /** Persian/Arabic-Indic digits to ASCII (phones typed on FA/AR keyboards). */
  function asciiDigits(v) {
    const str = String(v == null ? '' : v);
    let out = '';
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      if (c >= 0x06F0 && c <= 0x06F9) out += String(c - 0x06F0);else if (c >= 0x0660 && c <= 0x0669) out += String(c - 0x0660);else out += str.charAt(i);
    }
    return out;
  }
  /** UAE mobile → canonical +9715XXXXXXXX or null (accepts 05X…, 5X…, 9715…, +9715…, 009715…). */
  function canonUaeMobile(v) {
    let d = asciiDigits(v).replace(/[^\d+]/g, '');
    if (d.indexOf('00') === 0) d = '+' + d.slice(2);
    if (d.charAt(0) !== '+') {
      if (d.indexOf('971') === 0) d = '+' + d;else if (d.indexOf('05') === 0) d = '+971' + d.slice(1);else if (d.indexOf('5') === 0 && d.length === 9) d = '+971' + d;
    }
    return /^\+9715[024568]\d{7}$/.test(d) ? d : null;
  }

  // ── src/client/bread-common.babel.js ──
  // ═══════════════════════════════════════════════════════════════════════════
  // Sangak Club — shared client UI (bread-common.babel.js)
  //
  // scripts/compile-app.mjs compiles this file into BOTH browser bundles:
  //   bread.js      = (function(){ 'use strict'; core.mjs + bread-common + bread-builder })();
  //   bread-plan.js = (function(){ 'use strict'; core.mjs + bread-common + bread-plan    })();
  // so every top-level name here is visible to the page file and to nothing else on the site (app.js globals
  // never collide with it). core.mjs functions (priceDrop, patternDays, windowRange, canonUaeMobile, …) are
  // plain functions in the same scope.
  //
  // RULES for this file and the page files that consume it:
  //   • classic React 18 UMD globals (React, ReactDOM); no imports/exports; JSX → React.createElement.
  //   • This file does NOT destructure React hooks at top level (it writes React.useState …) so each page
  //     file can declare its own `const { useState, useEffect, … } = React;` without a duplicate-declaration
  //     SyntaxError.
  //   • Never type backslash-u escape sequences; build special characters with String.fromCharCode.
  //   • Every user-visible string goes through t() with EN + FA + AR. Prices: Western digits in
  //     <bdi dir="ltr" class="ltr-num">. FA counts in Persian digits (t() localises number vars).
  //
  // Exported (top-level) API — see the lead's report for signatures:
  //   BC, BC_LANG, BC_RTL, useLang, bcNow, bcSettings, bcCatalog, bcAreas, bcFindArea, bcRestaurant,
  //   I18N, addI18n, t, tn, digits, fmtAed, aedStr, fmtDay, fmtWindow, fmtWindowText, bcClock, bcHourWord,
  //   fmtPhone, phoneE164, itemName, itemsSummary, api, uuid, savePlanLocal, loadPlansLocal, removePlanLocal,
  //   planHref, sfTrack, bcAttribution, bcWaLink, injectCss, useBackLayer, useDialog, useToast, Toast,
  //   Sheet, Stepper, Chip, Btn, Spinner, ErrorBoundary, PhoneInput, AreaPicker, MapPicker, PinField,
  //   AddressForm, addressErrors, SavedPlansCard, FindPlanSheet, ensureLeaflet.
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Page data (window.__BREAD__, injected by bread.astro / bread/plan.astro) ──
  const BC = typeof window !== 'undefined' && window.__BREAD__ && typeof window.__BREAD__ === 'object' ? window.__BREAD__ : null;

  // Language: the SSR <html lang> wins (PaperLayout renders it from resolveLang); __BREAD__.lang is the fallback.
  // Never detect from the browser (owner rule) and never from localStorage (SSR/SPA mismatch trap).
  const BC_LANG = function () {
    try {
      const l = String(document.documentElement.getAttribute('lang') || '').slice(0, 2).toLowerCase();
      if (l === 'en' || l === 'fa' || l === 'ar') return l;
    } catch (e) {}
    const b = BC && BC.lang;
    return b === 'fa' || b === 'ar' ? b : 'en';
  }();
  const BC_RTL = function () {
    try {
      const d = document.documentElement.getAttribute('dir');
      if (d === 'rtl' || d === 'ltr') return d === 'rtl';
    } catch (e) {}
    return BC_LANG !== 'en';
  }();
  function useLang() {
    return BC_LANG;
  }

  // Server clock: __BREAD__.now (ms or ISO) corrects a wrong phone clock. A difference above 15 minutes is
  // more likely a stale cached page than a wrong clock, so then the device clock is trusted.
  const BC_SKEW = function () {
    if (!BC || BC.now == null) return 0;
    const n = typeof BC.now === 'number' ? BC.now : Date.parse(BC.now);
    if (!Number.isFinite(n)) return 0;
    const d = n - Date.now();
    return Math.abs(d) < 15 * 60000 ? d : 0;
  }();
  function bcNow() {
    return Date.now() + BC_SKEW;
  }
  let _bcSettings = null;
  /** Full settings object (public settings merged over core defaults, so every core helper has its keys). */
  function bcSettings() {
    if (!_bcSettings) _bcSettings = mergeSettings(Object.assign({
      enabled: true
    }, BC && BC.settings || {}));
    return _bcSettings;
  }
  /** { slug: { price (menu AED), kind 'bread'|'extra', available, name_en, name_fa, name_ar, image } } */
  function bcCatalog() {
    return BC && BC.catalog && typeof BC.catalog === 'object' ? BC.catalog : {};
  }
  let _bcAreas = null;
  /** [{key, en, fa, ar, band}] sorted by band, then English name. */
  function bcAreas() {
    if (_bcAreas) return _bcAreas;
    const list = BC && Array.isArray(BC.areas) ? BC.areas.filter(a => a && typeof a.key === 'string') : [];
    _bcAreas = list.map(a => ({
      key: a.key,
      en: a.en || a.key,
      fa: a.fa || '',
      ar: a.ar || '',
      band: bandOf(a.band)
    })).sort((a, b) => a.band - b.band || a.en.localeCompare(b.en));
    return _bcAreas;
  }
  function bcFindArea(key) {
    if (!key) return null;
    return bcAreas().find(a => a.key === key) || null;
  }
  function bcRestaurant() {
    const r = BC && BC.restaurant;
    return r && Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lng)) ? {
      lat: Number(r.lat),
      lng: Number(r.lng)
    } : {
      lat: 25.2314011,
      lng: 55.2626267
    };
  }
  const BC_PHONE_DISPLAY = '04 321 8882';
  const BC_PHONE_TEL = '+97143218882';
  const BC_SIGN_EN = 'Shaun the Sheep';

  // ── i18n ─────────────────────────────────────────────────────────────────────
  // Entry: { en, fa, ar } where a value may be a plural object { one, two, few, many, other } chosen with
  // Intl.PluralRules for vars.n. Missing language → en; missing key → the key itself (visible in QA).
  const I18N = {
    'c.close': {
      en: 'Close',
      fa: 'بستن',
      ar: 'إغلاق'
    },
    'c.save': {
      en: 'Save',
      fa: 'ذخیره',
      ar: 'حفظ'
    },
    'c.cancel': {
      en: 'Cancel',
      fa: 'انصراف',
      ar: 'إلغاء'
    },
    'c.back': {
      en: 'Back',
      fa: 'بازگشت',
      ar: 'رجوع'
    },
    'c.undo': {
      en: 'Undo',
      fa: 'برگردان',
      ar: 'تراجع'
    },
    'c.retry': {
      en: 'Try again',
      fa: 'دوباره امتحان کنید',
      ar: 'حاول مجدداً'
    },
    'c.loading': {
      en: 'Loading…',
      fa: 'در حال بارگذاری…',
      ar: 'جارٍ التحميل…'
    },
    'c.edit': {
      en: 'Change',
      fa: 'تغییر',
      ar: 'تغيير'
    },
    'c.remove': {
      en: 'Remove',
      fa: 'حذف',
      ar: 'إزالة'
    },
    'c.optional': {
      en: '(optional)',
      fa: '(اختیاری)',
      ar: '(اختياري)'
    },
    'c.free': {
      en: 'Free',
      fa: 'رایگان',
      ar: 'مجاناً'
    },
    'c.yes': {
      en: 'Yes',
      fa: 'بله',
      ar: 'نعم'
    },
    'c.no': {
      en: 'No',
      fa: 'نه',
      ar: 'لا'
    },
    'c.dubaiTime': {
      en: '(Dubai time)',
      fa: '(به وقت دبی)',
      ar: '(بتوقيت دبي)'
    },
    'c.network': {
      en: 'No connection — check your internet and try again.',
      fa: 'اتصال برقرار نشد — اینترنت را بررسی کنید و دوباره امتحان کنید.',
      ar: 'لا يوجد اتصال — تحقّق من الإنترنت وحاول مجدداً.'
    },
    'c.server': {
      en: 'Something went wrong on our side. Try again in a minute — or message us on WhatsApp.',
      fa: 'مشکلی از طرف ما پیش آمد. یک دقیقهٔ دیگر دوباره امتحان کنید — یا در واتس‌اپ پیام بدهید.',
      ar: 'حدث خلل من جهتنا. حاول بعد دقيقة — أو راسلنا على واتساب.'
    },
    'c.rate': {
      en: 'Too many tries — please wait a few minutes and try again.',
      fa: 'تعداد تلاش‌ها زیاد شد — چند دقیقه صبر کنید و دوباره امتحان کنید.',
      ar: 'محاولات كثيرة — انتظر بضع دقائق ثم حاول مجدداً.'
    },
    'c.minus': {
      en: 'One less {name}',
      fa: 'یکی کمتر: {name}',
      ar: 'واحد أقل: {name}'
    },
    'c.plus': {
      en: 'One more {name}',
      fa: 'یکی بیشتر: {name}',
      ar: 'واحد إضافي: {name}'
    },
    'c.wa': {
      en: 'WhatsApp us',
      fa: 'پیام در واتس‌اپ',
      ar: 'راسلنا على واتساب'
    },
    'n.deliveries': {
      en: {
        one: '{n} delivery',
        other: '{n} deliveries'
      },
      fa: '{n} نوبت تحویل',
      ar: {
        zero: 'لا توصيلات',
        one: 'توصيلة واحدة',
        two: 'توصيلتان',
        few: '{n} توصيلات',
        many: '{n} توصيلة',
        other: '{n} توصيلة'
      }
    },
    'n.breads': {
      en: {
        one: '{n} bread',
        other: '{n} breads'
      },
      fa: '{n} نان',
      ar: {
        zero: 'لا خبز',
        one: 'رغيف واحد',
        two: 'رغيفان',
        few: '{n} أرغفة',
        many: '{n} رغيفاً',
        other: '{n} رغيف'
      }
    },
    'n.days': {
      en: {
        one: '{n} day',
        other: '{n} days'
      },
      fa: '{n} روز',
      ar: {
        one: 'يوم واحد',
        two: 'يومان',
        few: '{n} أيام',
        many: '{n} يوماً',
        other: '{n} يوم'
      }
    },
    'n.weeks': {
      en: {
        one: '{n} week',
        other: '{n} weeks'
      },
      fa: '{n} هفته',
      ar: {
        one: 'أسبوع واحد',
        two: 'أسبوعان',
        few: '{n} أسابيع',
        many: '{n} أسبوعاً',
        other: '{n} أسبوع'
      }
    },
    // Items — short names for summaries ("3 Sangak + 2 Special"), long names + one-line descriptions for rows.
    'item.sangak': {
      en: 'Sangak',
      fa: 'سنگک',
      ar: 'سنكك'
    },
    'item.sangak-x': {
      en: 'Special',
      fa: 'سنگک ویژه',
      ar: 'سنكك مميّز'
    },
    'item.sangak.long': {
      en: 'Sangak',
      fa: 'نان سنگک',
      ar: 'خبز سنكك'
    },
    'item.sangak-x.long': {
      en: 'Special Sangak',
      fa: 'سنگک ویژه',
      ar: 'سنكك مميّز'
    },
    'item.sangak.desc': {
      en: 'The classic, baked on hot pebbles',
      fa: 'سنگکِ ساده، پخته روی ریگِ داغ',
      ar: 'الكلاسيكي، مخبوز على الحصى الساخن'
    },
    'item.sangak-x.desc': {
      en: 'With sesame & nigella seeds',
      fa: 'با کنجد و سیاه‌دانه',
      ar: 'بالسمسم وحبة البركة'
    },
    'item.each': {
      en: '{price} each',
      fa: 'هر عدد {price}',
      ar: '{price} للواحد'
    },
    'item.menuPrice': {
      en: 'Menu price',
      fa: 'قیمت منو',
      ar: 'سعر القائمة'
    },
    // Phone
    'ph.label': {
      en: 'Mobile number (WhatsApp)',
      fa: 'شمارهٔ موبایل (واتس‌اپ)',
      ar: 'رقم الجوال (واتساب)'
    },
    'ph.err': {
      en: 'Enter a UAE mobile number, like {ex}',
      fa: 'یک شمارهٔ موبایل امارات وارد کنید، مثلاً {ex}',
      ar: 'أدخل رقم جوال إماراتي، مثل {ex}'
    },
    // Saved plans + find (shared by /bread and /bread/plan)
    'saved.title': {
      en: {
        one: 'Your Sangak Club plan',
        other: 'Your Sangak Club plans'
      },
      fa: 'برنامهٔ باشگاه سنگکِ شما',
      ar: {
        one: 'خطتك في نادي السنكك',
        other: 'خططك في نادي السنكك'
      }
    },
    'saved.open': {
      en: 'Open',
      fa: 'باز کردن',
      ar: 'افتح'
    },
    'find.q': {
      en: 'Already in the Club? Find my plan',
      fa: 'عضو باشگاه هستید؟ برنامه‌ام را پیدا کن',
      ar: 'مشترك بالفعل؟ اعثر على خطتي'
    },
    'find.other': {
      en: 'Another plan? Find it by phone',
      fa: 'برنامهٔ دیگری دارید؟ با شماره پیدایش کنید',
      ar: 'خطة أخرى؟ ابحث عنها برقم الجوال'
    },
    'find.title': {
      en: 'Find my plan',
      fa: 'پیدا کردن برنامه‌ام',
      ar: 'العثور على خطتي'
    },
    'find.body': {
      en: "Enter the mobile number you joined with. We'll send your plan link to that WhatsApp.",
      fa: 'شماره موبایلی را که با آن عضو شدید وارد کنید؛ لینک برنامه را به همان واتس‌اپ می‌فرستیم.',
      ar: 'أدخل رقم الجوال الذي اشتركت به، وسنرسل رابط خطتك إلى واتساب على الرقم نفسه.'
    },
    'find.send': {
      en: 'Send my link',
      fa: 'لینکم را بفرست',
      ar: 'أرسل الرابط'
    },
    'find.sent': {
      en: 'Done. If a plan exists for this number, the link is on its way to WhatsApp — it can take a minute.',
      fa: 'انجام شد. اگر برنامه‌ای با این شماره باشد، لینکش تا یک دقیقهٔ دیگر در واتس‌اپ می‌رسد.',
      ar: 'تم. إذا كانت هناك خطة بهذا الرقم فسيصلك الرابط على واتساب خلال دقيقة.'
    },
    'find.call': {
      en: 'No WhatsApp? Call us on {phone}',
      fa: 'واتس‌اپ ندارید؟ با {phone} تماس بگیرید',
      ar: 'ليس لديك واتساب؟ اتصل بنا على {phone}'
    },
    // Area picker / map / GPS — texts copied from app.babel.js T (area.*, map.*, geo.*), primary language only.
    'area.ph': {
      en: 'Type your area — e.g. Marina',
      fa: 'منطقه را تایپ کنید — مثلاً مارینا',
      ar: 'اكتب منطقتك — مثلاً مارينا'
    },
    'area.label': {
      en: 'Delivery area',
      fa: 'منطقه‌ی ارسال',
      ar: 'منطقة التوصيل'
    },
    'area.change': {
      en: 'Change area',
      fa: 'تغییر منطقه',
      ar: 'تغيير المنطقة'
    },
    'area.noMatch': {
      en: "No area matches. Try another spelling — or message us and we'll sort it out.",
      fa: 'منطقه‌ای پیدا نشد — املای دیگری را امتحان کنید یا به ما پیام بدهید.',
      ar: 'لم نجد هذه المنطقة — جرّب كتابة الاسم بطريقة أخرى، أو راسلنا ونحن نتكفّل بالباقي.'
    },
    'area.group': {
      en: '{fee} per delivery · min {breads}',
      fa: 'هر نوبت {fee}، دست‌کم {breads}',
      ar: '{fee} لكل توصيلة · {breads} على الأقل'
    },
    'area.groupFree': {
      en: 'Free delivery · min {breads}',
      fa: 'ارسال رایگان، دست‌کم {breads}',
      ar: 'توصيل مجاني · {breads} على الأقل'
    },
    'map.fallback': {
      en: "Map couldn't load — tap “Use my GPS”, or just write your address and we'll call to confirm.",
      fa: 'نقشه بارگذاری نشد — از دکمه‌ی موقعیت استفاده کنید یا آدرس را بنویسید.',
      ar: 'تعذّر تحميل الخريطة — اضغط «حدّد موقعي» أو اكتب عنوانك وسنتصل بك للتأكيد.'
    },
    'map.toRestaurant': {
      en: 'to the restaurant',
      fa: 'تا رستوران',
      ar: 'إلى المطعم'
    },
    'map.hint': {
      en: 'Drag the map so the pin is on your door',
      fa: 'پین را روی درِ خانه‌تان بگذارید',
      ar: 'حرّك الخريطة حتى يقع الدبوس على بابك'
    },
    'map.twoFinger': {
      en: 'Use two fingers to move the map',
      fa: 'برای حرکت نقشه از دو انگشت استفاده کنید',
      ar: 'استخدم إصبعين لتحريك الخريطة'
    },
    'map.setPin': {
      en: 'Set pin here',
      fa: 'ثبت پین همین‌جا',
      ar: 'ثبّت موقعي هنا'
    },
    'geo.label': {
      en: 'Your door on the map',
      fa: 'درِ خانه‌تان روی نقشه',
      ar: 'بابك على الخريطة'
    },
    'geo.useGps': {
      en: 'Use my GPS',
      fa: 'موقعیت من',
      ar: 'حدّد موقعي'
    },
    'geo.locating': {
      en: 'Locating…',
      fa: 'در حال مکان‌یابی…',
      ar: 'جارٍ تحديد الموقع…'
    },
    'geo.pinned': {
      en: 'Pinned ✓',
      fa: 'پین شد ✓',
      ar: 'تم تحديد الموقع ✓'
    },
    'geo.hint': {
      en: 'Required for delivery. Drag the map onto your door — or tap here to use GPS.',
      fa: 'برای ارسال الزامی است. نقشه را جابه‌جا کنید تا پین روی درِ شما بیفتد — یا همین‌جا بزنید تا موقعیت‌تان با GPS پیدا شود.',
      ar: 'مطلوب للتوصيل. حرّك الخريطة حتى يقع الدبوس على بابك — أو اضغط هنا لتحديد موقعك تلقائياً.'
    },
    'geo.noSupport': {
      en: 'Your browser does not support location.',
      fa: 'مرورگر شما از مکان‌یابی پشتیبانی نمی‌کند.',
      ar: 'متصفحك لا يدعم تحديد الموقع.'
    },
    'geo.denied': {
      en: 'Permission denied — enable location in your browser and try again.',
      fa: 'اجازه داده نشد — مکان‌یابی مرورگر را روشن کنید و دوباره امتحان کنید.',
      ar: 'تم رفض الإذن — فعّل الموقع في المتصفح وحاول مجدداً.'
    },
    'geo.unavailable': {
      en: "Couldn't get your position. Make sure device location is on.",
      fa: 'موقعیت پیدا نشد. مطمئن شوید مکان‌یابی دستگاه روشن است.',
      ar: 'تعذّر تحديد موقعك. تأكد أن خدمة الموقع مفعّلة.'
    },
    'geo.timeout': {
      en: 'Took too long. Try again.',
      fa: 'خیلی طول کشید. دوباره امتحان کنید.',
      ar: 'استغرق وقتاً طويلاً. حاول مجدداً.'
    },
    'geo.failed': {
      en: "Couldn't get location.",
      fa: 'موقعیت پیدا نشد.',
      ar: 'تعذّر تحديد الموقع.'
    },
    'geo.slow': {
      en: 'Location is taking too long. Try again — or drag the map and tap “Set pin here”.',
      fa: 'مکان‌یابی طول کشید. دوباره امتحان کنید — یا نقشه را جابه‌جا کنید و «ثبت پین همین‌جا» را بزنید.',
      ar: 'تحديد الموقع يتأخر. حاول مجدداً — أو حرّك الخريطة واضغط «ثبّت موقعي هنا».'
    },
    'geo.check': {
      en: 'We used your saved pin — is it still right?',
      fa: 'پینِ ذخیره‌شده‌تان را گذاشتیم — هنوز درست است؟',
      ar: 'استخدمنا موقعك المحفوظ — هل ما زال صحيحاً؟'
    },
    'geo.checkYes': {
      en: "Yes, that's my door",
      fa: 'بله، همین‌جاست',
      ar: 'نعم، هذا بابي'
    },
    // Address form (shared: builder step "Where" + plan page "change address")
    'ad.line': {
      en: 'Building or villa, street',
      fa: 'ساختمان یا ویلا، خیابان',
      ar: 'المبنى أو الفيلا، الشارع'
    },
    'ad.linePh': {
      en: 'e.g. Villa 12, Street 3B',
      fa: 'مثلاً ویلا ۱۲، خیابان ۳ب',
      ar: 'مثلاً: فيلا 12، شارع 3ب'
    },
    'ad.unit': {
      en: 'Flat or office no.',
      fa: 'شمارهٔ واحد یا دفتر',
      ar: 'رقم الشقة أو المكتب'
    },
    'ad.handover': {
      en: 'How should we hand it over?',
      fa: 'نان را چطور تحویل بدهیم؟',
      ar: 'كيف نسلّمك الخبز؟'
    },
    'ad.ho.door': {
      en: 'Leave at the door',
      fa: 'پشت در بگذارید',
      ar: 'اتركه عند الباب'
    },
    'ad.ho.hand': {
      en: 'Hand it to me',
      fa: 'به دست خودم بدهید',
      ar: 'سلّمه لي باليد'
    },
    'ad.ho.reception': {
      en: 'Reception or guard',
      fa: 'پذیرش یا نگهبانی',
      ar: 'الاستقبال أو الحارس'
    },
    'ad.bell': {
      en: 'Ring the bell?',
      fa: 'زنگ بزنیم؟',
      ar: 'نرنّ الجرس؟'
    },
    'ad.bellYes': {
      en: 'Yes, ring',
      fa: 'بله، زنگ بزنید',
      ar: 'نعم، رنّ الجرس'
    },
    'ad.bellNo': {
      en: "Don't ring",
      fa: 'زنگ نزنید',
      ar: 'لا ترنّ الجرس'
    },
    'ad.gate': {
      en: 'Gate code or how to find the door',
      fa: 'رمز درِ ورودی یا نشانیِ دقیق',
      ar: 'رمز البوابة أو وصف المدخل'
    },
    'ad.notes': {
      en: 'Note for the rider',
      fa: 'یادداشت برای پیک',
      ar: 'ملاحظة للمندوب'
    },
    'ad.fee': {
      en: 'Delivery to {area}: {fee} each time · at least {breads} per delivery',
      fa: 'ارسال به {area}: هر نوبت {fee}؛ دست‌کم {breads} در هر نوبت',
      ar: 'التوصيل إلى {area}: {fee} في كل مرة · {breads} على الأقل لكل توصيلة'
    },
    'ad.feeFree': {
      en: 'Free delivery to {area} · at least {breads} per delivery',
      fa: 'ارسال رایگان به {area}؛ دست‌کم {breads} در هر نوبت',
      ar: 'توصيل مجاني إلى {area} · {breads} على الأقل لكل توصيلة'
    },
    'ad.err.area': {
      en: 'Choose your area — it sets the delivery price.',
      fa: 'منطقه را انتخاب کنید — هزینهٔ ارسال به آن بستگی دارد.',
      ar: 'اختر منطقتك — سعر التوصيل يعتمد عليها.'
    },
    'ad.err.pin': {
      en: 'Set your pin on the map: tap “Use my GPS”, or drag the map onto your door and tap “Set pin here”.',
      fa: 'پین را روی نقشه بگذارید: «موقعیت من» را بزنید، یا نقشه را روی درِ خانه بکشید و «ثبت پین همین‌جا» را بزنید.',
      ar: 'ثبّت موقعك على الخريطة: اضغط «حدّد موقعي»، أو حرّك الخريطة إلى بابك واضغط «ثبّت موقعي هنا».'
    },
    'ad.err.pinCheck': {
      en: 'Tap “Yes, that\'s my door” to confirm your saved pin — or move the map.',
      fa: 'برای تأیید پینِ ذخیره‌شده «بله، همین‌جاست» را بزنید — یا نقشه را جابه‌جا کنید.',
      ar: 'اضغط «نعم، هذا بابي» لتأكيد موقعك المحفوظ — أو حرّك الخريطة.'
    },
    'ad.err.line': {
      en: 'Add your building or villa so the rider finds you.',
      fa: 'ساختمان یا ویلا را بنویسید تا پیک پیدایتان کند.',
      ar: 'أضف المبنى أو الفيلا ليجدك المندوب.'
    }
  };
  /** Page files register their own keys (same entry shape). Later keys win. */
  function addI18n(dict) {
    if (dict && typeof dict === 'object') Object.keys(dict).forEach(k => {
      I18N[k] = dict[k];
    });
  }
  const BC_FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
  /** Persian digits for FA (counts, dates); Western everywhere else. Prices never go through this. */
  function digits(n, lang) {
    const s = String(n == null ? '' : n);
    if ((lang || BC_LANG) !== 'fa') return s;
    return s.replace(/(\d)\.(\d)/g, '$1٫$2').replace(/\d/g, d => BC_FA_DIGITS[+d]);
  }
  function bcPlural(L, n) {
    try {
      return new Intl.PluralRules(L).select(Number(n) || 0);
    } catch (e) {
      return Number(n) === 1 ? 'one' : 'other';
    }
  }
  function bcPick(key, L, n) {
    const e = I18N[key];
    if (!e) return key;
    let v = e[L] != null ? e[L] : e.en;
    if (v && typeof v === 'object') {
      const c = bcPlural(L, n);
      v = v[c] != null ? v[c] : v.other != null ? v.other : v.one;
    }
    return v == null ? key : String(v);
  }
  /** Plain-string translation. Number vars are localised (Persian digits on FA). vars.n picks the plural. */
  function t(key, vars, lang) {
    const L = lang || BC_LANG;
    let s = bcPick(key, L, vars && vars.n);
    if (vars) s = s.replace(/\{(\w+)\}/g, (m, k) => vars[k] == null ? m : typeof vars[k] === 'number' ? digits(vars[k], L) : String(vars[k]));
    return s;
  }
  /** Translation with React-node vars (prices, phone numbers…): returns an array usable as JSX children. */
  function tn(key, vars, lang) {
    const L = lang || BC_LANG;
    const s = bcPick(key, L, vars && vars.n);
    const out = [];
    const re = /\{(\w+)\}/g;
    let last = 0,
      m,
      i = 0;
    while (m = re.exec(s)) {
      if (m.index > last) out.push(s.slice(last, m.index));
      const v = vars ? vars[m[1]] : undefined;
      if (v == null) out.push(m[0]);else if (React.isValidElement(v)) out.push(React.cloneElement(v, {
        key: 'v' + i++
      }));else out.push(typeof v === 'number' ? digits(v, L) : String(v));
      last = re.lastIndex;
    }
    if (last < s.length) out.push(s.slice(last));
    return out;
  }

  // ── Formatting ───────────────────────────────────────────────────────────────
  /** "AED 12.50" (always Western digits, two decimals). */
  function aedStr(n) {
    const x = Math.round(Number(n || 0) * 100) / 100;
    return 'AED ' + x.toFixed(2);
  }
  /** Price node: Western digits, LTR-isolated, never reversed inside RTL text. opts.cls adds classes. */
  function fmtAed(n, opts) {
    const o = opts || {};
    return /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: 'ltr-num bc-aed' + (o.cls ? ' ' + o.cls : '')
    }, aedStr(n));
  }
  function bcLocale(L, persian) {
    if (L === 'fa') return persian ? 'fa-IR-u-ca-persian' : 'fa-IR-u-ca-gregory';
    if (L === 'ar') return 'ar-AE-u-nu-latn';
    return 'en-GB';
  }
  /**
   * 'YYYY-MM-DD' → "Fri 24 Oct" · "جمعه ۲۴ اکتبر" · "الجمعة، 24 أكتوبر" (Gregorian everywhere).
   * opts: { weekday:'short'|'long'|false, month:'short'|'long', year:boolean, persian:boolean (FA solar date) }
   */
  function fmtDay(ymd, lang, opts) {
    const L = lang || BC_LANG,
      o = opts || {};
    const f = {
      timeZone: 'UTC',
      day: 'numeric',
      month: o.month || (L === 'en' ? 'short' : 'long')
    };
    if (o.weekday !== false) f.weekday = o.weekday || (L === 'en' ? 'short' : 'long');
    if (o.year) f.year = 'numeric';
    try {
      return new Intl.DateTimeFormat(bcLocale(L, !!o.persian), f).format(new Date(ymd + 'T12:00:00Z'));
    } catch (e) {
      return ymd;
    }
  }
  /** Weekday name for 0..6 (0 = Sunday). width: 'short'|'long'|'narrow'. */
  function bcWeekdayName(wd, lang, width) {
    const L = lang || BC_LANG;
    const base = Date.UTC(2026, 0, 4); // a Sunday
    try {
      return new Intl.DateTimeFormat(bcLocale(L), {
        timeZone: 'UTC',
        weekday: width || (L === 'en' ? 'short' : 'long')
      }).format(new Date(base + wd * 86400000));
    } catch (e) {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][wd];
    }
  }
  function bcPeriod(h, L) {
    if (L === 'fa') return h < 12 ? 'صبح' : h < 14 ? 'ظهر' : h < 17 ? 'بعدازظهر' : h < 19 ? 'عصر' : 'شب';
    if (L === 'ar') return h < 12 ? 'ص' : 'م';
    return h < 12 ? 'AM' : 'PM';
  }
  function bcH12(h) {
    const x = h % 12;
    return x === 0 ? 12 : x;
  }
  function bcPad(n) {
    return (n < 10 ? '0' : '') + n;
  }
  /** "7:30 PM" · "۷:۳۰ شب" · "7:30 مساءً" — a clock time inside a sentence. */
  function bcClock(hhmm, lang) {
    const L = lang || BC_LANG,
      m = hhmmToMin(hhmm),
      h = Math.floor(m / 60),
      mi = m % 60;
    const core = bcH12(h) + ':' + bcPad(mi);
    if (L === 'fa') return digits(core, 'fa') + ' ' + bcPeriod(h, 'fa');
    if (L === 'ar') return core + ' ' + (h < 12 ? 'صباحاً' : 'مساءً');
    return core + ' ' + bcPeriod(h, 'en');
  }
  /** "8 PM" · "۸ شب" · "8 مساءً" — a whole hour (the cutoff). */
  function bcHourWord(hour, lang) {
    const L = lang || BC_LANG,
      h = Number(hour) || 0;
    if (L === 'fa') return digits(bcH12(h), 'fa') + ' ' + bcPeriod(h, 'fa');
    if (L === 'ar') return bcH12(h) + ' ' + (h < 12 ? 'صباحاً' : 'مساءً');
    return bcH12(h) + ' ' + bcPeriod(h, 'en');
  }
  // FA names five day-parts; a window inside the afternoon/evening takes the END part only ("۴:۳۰ تا ۵:۰۰ عصر").
  function bcSamePeriod(p, L) {
    return L === 'fa' ? p.ha >= 12 === p.hb >= 12 : bcPeriod(p.ha, L) === bcPeriod(p.hb, L);
  }
  function bcWinParts(slot, s) {
    const w = windowRange(slot, s || bcSettings());
    const a = hhmmToMin(w.from),
      b = hhmmToMin(w.to);
    return {
      a,
      b,
      ha: Math.floor(a / 60),
      hb: Math.floor(b / 60),
      fa: bcH12(Math.floor(a / 60)) + ':' + bcPad(a % 60),
      fb: bcH12(Math.floor(b / 60)) + ':' + bcPad(b % 60)
    };
  }
  /**
   * Arrival window for a slot END time → node: "6:30–7:00 AM" · "۶:۳۰ تا ۷:۰۰ صبح" · "6:30–7:00 ص".
   * Arabic ranges are LTR-isolated (the RTL range-reversal bug class).
   */
  function fmtWindow(slot, lang, s) {
    const L = lang || BC_LANG;
    if (!isValidHHMM(slot)) return '';
    const p = bcWinParts(slot, s);
    const same = bcSamePeriod(p, L);
    if (L === 'fa') {
      return /*#__PURE__*/React.createElement("span", {
        className: "bc-win"
      }, same ? digits(p.fa, 'fa') + ' تا ' + digits(p.fb, 'fa') + ' ' + bcPeriod(p.hb, 'fa') : digits(p.fa, 'fa') + ' ' + bcPeriod(p.ha, 'fa') + ' تا ' + digits(p.fb, 'fa') + ' ' + bcPeriod(p.hb, 'fa'));
    }
    if (L === 'ar') {
      return same ? /*#__PURE__*/React.createElement("span", {
        className: "bc-win"
      }, /*#__PURE__*/React.createElement("bdi", {
        dir: "ltr",
        className: "ltr-num"
      }, p.fa + '–' + p.fb), " ", bcPeriod(p.hb, 'ar')) : /*#__PURE__*/React.createElement("span", {
        className: "bc-win"
      }, /*#__PURE__*/React.createElement("bdi", {
        dir: "ltr",
        className: "ltr-num"
      }, p.fa), " ", bcPeriod(p.ha, 'ar'), " \u2013 ", /*#__PURE__*/React.createElement("bdi", {
        dir: "ltr",
        className: "ltr-num"
      }, p.fb), " ", bcPeriod(p.hb, 'ar'));
    }
    return /*#__PURE__*/React.createElement("span", {
      className: "bc-win"
    }, same ? p.fa + '–' + p.fb + ' ' + bcPeriod(p.hb, 'en') : p.fa + ' ' + bcPeriod(p.ha, 'en') + '–' + p.fb + ' ' + bcPeriod(p.hb, 'en'));
  }
  const BC_LRI = String.fromCharCode(0x2066),
    BC_PDI = String.fromCharCode(0x2069);
  /** Same as fmtWindow but a plain string (aria-labels, WhatsApp texts); AR range wrapped in LRI…PDI. */
  function fmtWindowText(slot, lang, s) {
    const L = lang || BC_LANG;
    if (!isValidHHMM(slot)) return '';
    const p = bcWinParts(slot, s);
    const same = bcSamePeriod(p, L);
    if (L === 'fa') return same ? digits(p.fa, 'fa') + ' تا ' + digits(p.fb, 'fa') + ' ' + bcPeriod(p.hb, 'fa') : digits(p.fa, 'fa') + ' ' + bcPeriod(p.ha, 'fa') + ' تا ' + digits(p.fb, 'fa') + ' ' + bcPeriod(p.hb, 'fa');
    if (L === 'ar') return same ? BC_LRI + p.fa + '–' + p.fb + BC_PDI + ' ' + bcPeriod(p.hb, 'ar') : BC_LRI + p.fa + BC_PDI + ' ' + bcPeriod(p.ha, 'ar') + ' – ' + BC_LRI + p.fb + BC_PDI + ' ' + bcPeriod(p.hb, 'ar');
    return same ? p.fa + '–' + p.fb + ' ' + bcPeriod(p.hb, 'en') : p.fa + ' ' + bcPeriod(p.ha, 'en') + '–' + p.fb + ' ' + bcPeriod(p.hb, 'en');
  }
  /** Canonical +9715XXXXXXXX from whatever the customer typed (Persian/Arabic digits ok), or null. */
  function phoneE164(raw) {
    const v = String(raw == null ? '' : raw).trim();
    return v ? canonUaeMobile(v) : null;
  }
  /** "+971 50 123 4567" as an LTR-isolated node. */
  function fmtPhone(e164) {
    const d = String(e164 || '').replace(/^\+971/, '');
    const txt = /^\d{9}$/.test(d) ? '+971 ' + d.slice(0, 2) + ' ' + d.slice(2, 5) + ' ' + d.slice(5) : String(e164 || '');
    return /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: "ltr-num"
    }, txt);
  }
  /** Display name of a catalog slug: our short/long bread names, else the catalog's own (per language). */
  function itemName(slug, opts) {
    const o = opts || {};
    const L = o.lang || BC_LANG;
    const k = 'item.' + slug + (o.long ? '.long' : '');
    if (I18N[k]) return t(k, null, L);
    const c = bcCatalog()[slug];
    if (!c) return slug;
    return (L === 'fa' ? c.name_fa : L === 'ar' ? c.name_ar : c.name_en) || c.name_en || slug;
  }
  /** [{slug,qty}] → "3 Sangak + 2 Special + 1 Doogh" (FA digits on FA). */
  function itemsSummary(items, lang) {
    const L = lang || BC_LANG;
    const list = (Array.isArray(items) ? items : []).filter(it => it && Number(it.qty) > 0);
    if (!list.length) return '—';
    return list.map(it => digits(Number(it.qty), L) + ' ' + itemName(it.slug, {
      lang: L
    })).join(' + ');
  }

  // ── Network ──────────────────────────────────────────────────────────────────
  /**
   * POST JSON → parsed body. Never throws. Always returns an object with `ok`:
   *   network failure / timeout → { ok:false, code:'network' }; HTTP 429 → code 'rate_limited';
   *   non-JSON or 5xx without a code → code 'server'. `status` carries the HTTP status when there was one.
   */
  function api(path, body, opts) {
    const o = opts || {};
    let ctl = null;
    try {
      ctl = new AbortController();
    } catch (e) {
      ctl = null;
    }
    const timer = setTimeout(() => {
      try {
        if (ctl) ctl.abort();
      } catch (e) {}
    }, o.timeout || 15000);
    let p;
    try {
      p = fetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body || {}),
        credentials: 'same-origin',
        cache: 'no-store',
        signal: ctl ? ctl.signal : undefined
      });
    } catch (e) {
      clearTimeout(timer);
      return Promise.resolve({
        ok: false,
        code: 'network'
      });
    }
    return p.then(r => r.text().then(txt => {
      let j = null;
      try {
        j = txt ? JSON.parse(txt) : null;
      } catch (e) {
        j = null;
      }
      if (j && typeof j === 'object' && !Array.isArray(j)) {
        if (j.ok == null) j.ok = r.ok;
        if (!j.ok && !j.code) j.code = r.status === 429 ? 'rate_limited' : 'server';
        j.status = r.status;
        return j;
      }
      return {
        ok: false,
        code: r.status === 429 ? 'rate_limited' : 'server',
        status: r.status
      };
    })).catch(() => ({
      ok: false,
      code: 'network'
    })).then(res => {
      clearTimeout(timer);
      return res;
    });
  }
  /** RFC-4122 v4 id (action_id / checkout_id). */
  function uuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    } catch (e) {}
    const b = new Uint8Array(16);
    try {
      window.crypto.getRandomValues(b);
    } catch (e) {
      for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    }
    b[6] = b[6] & 15 | 64;
    b[8] = b[8] & 63 | 128;
    const h = Array.prototype.map.call(b, x => (x < 16 ? '0' : '') + x.toString(16)).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  // ── Device-local plan links (localStorage 'sf-bread-plans') ──────────────────
  // Shape: [{ ref:'SC-XXXXX', token, name, at }] newest first, max 10, one per ref. Read by /bread, /bread/plan
  // and /me ("My Sangak Club" card). The token IS the key to the plan — never send it anywhere but our API.
  const BC_PLANS_KEY = 'sf-bread-plans';
  function loadPlansLocal() {
    try {
      const a = JSON.parse(localStorage.getItem(BC_PLANS_KEY) || '[]');
      return Array.isArray(a) ? a.filter(x => x && typeof x.ref === 'string' && /^SC-[A-Z0-9]{3,12}$/.test(x.ref) && typeof x.token === 'string' && x.token.length > 8 && x.token.length < 200).slice(0, 10) : [];
    } catch (e) {
      return [];
    }
  }
  function savePlanLocal(p) {
    if (!p || typeof p.ref !== 'string' || typeof p.token !== 'string') return;
    try {
      const list = loadPlansLocal().filter(x => x.ref !== p.ref);
      list.unshift({
        ref: p.ref,
        token: p.token,
        name: String(p.name || '').slice(0, 60),
        at: Date.now()
      });
      localStorage.setItem(BC_PLANS_KEY, JSON.stringify(list.slice(0, 10)));
    } catch (e) {/* private mode — the WhatsApp link is the backup */}
  }
  function removePlanLocal(ref) {
    try {
      localStorage.setItem(BC_PLANS_KEY, JSON.stringify(loadPlansLocal().filter(x => x.ref !== ref)));
    } catch (e) {}
  }
  /** The manage link (the head script on /bread/plan strips token from the address bar before anything reads it). */
  function planHref(token, lang) {
    return '/bread/plan?token=' + encodeURIComponent(token) + '&lang=' + (lang || BC_LANG);
  }

  // ── Analytics / attribution ──────────────────────────────────────────────────
  /** Passthrough to PaperLayout's window.sfTrack (GA/Meta/Plausible/Snap fan-out). Never throws. */
  function sfTrack(name, props) {
    try {
      if (typeof window.sfTrack === 'function') window.sfTrack(name, props || {});
    } catch (e) {}
  }
  function bcClarity(name) {
    try {
      if (window.clarity) window.clarity('event', name);
    } catch (e) {}
  }
  // copied from app.babel.js:951-1012 (readCookie + captureAttribution), same 'sf-attr' key and 30-day
  // first-touch rules, so a visitor who arrived on /bread from an ad keeps the credit through checkout.
  const BC_ATTR_KEY = 'sf-attr';
  const BC_ATTR_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const BC_CLICK_PARAMS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'ScCid', 'msclkid', 'twclid'];
  const BC_UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  function bcReadCookie(name) {
    try {
      const m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[2]) : '';
    } catch (e) {
      return '';
    }
  }
  function bcAttribution() {
    if (typeof window === 'undefined') return null;
    let stored = null;
    try {
      const raw = localStorage.getItem(BC_ATTR_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && p.t && Date.now() - p.t < BC_ATTR_TTL_MS) stored = p;
      }
    } catch (e) {}
    let fresh = null;
    try {
      const q = new URLSearchParams(window.location.search);
      const hit = {};
      BC_CLICK_PARAMS.concat(BC_UTM_PARAMS).forEach(k => {
        const v = q.get(k);
        if (v) hit[k] = String(v).slice(0, 512);
      });
      const stamp = () => Object.assign({}, hit, {
        t: Date.now(),
        lp: String(window.location.pathname).slice(0, 120),
        lq: String(window.location.search || '').slice(0, 200),
        ref: String(document.referrer || '').slice(0, 200)
      });
      if (BC_CLICK_PARAMS.some(k => hit[k])) fresh = stamp();else if (!stored && Object.keys(hit).length) fresh = stamp();
    } catch (e) {}
    const base = fresh || stored;
    if (fresh) {
      try {
        localStorage.setItem(BC_ATTR_KEY, JSON.stringify(fresh));
      } catch (e) {}
    }
    const fbp = bcReadCookie('_fbp'),
      fbc = bcReadCookie('_fbc'),
      scid = bcReadCookie('_scid');
    if (!base) {
      if (!fbp && !fbc && !scid) return null;
      return {
        fbp: fbp || undefined,
        fbc: fbc || undefined,
        sc_cookie1: scid || undefined
      };
    }
    return Object.assign({}, base, {
      fbp: fbp || undefined,
      fbc: fbc || undefined,
      sc_cookie1: scid || undefined
    });
  }
  /** wa.me link to the restaurant with a prefilled text (null when no number is configured). */
  function bcWaLink(text) {
    const n = String(BC && BC.wa_number || '').replace(/\D/g, '');
    if (!n) return null;
    return 'https://wa.me/' + n + (text ? '?text=' + encodeURIComponent(text) : '');
  }

  // ── CSS injection ────────────────────────────────────────────────────────────
  function injectCss(id, css) {
    try {
      if (document.getElementById(id)) return;
      const el = document.createElement('style');
      el.id = id;
      el.appendChild(document.createTextNode(css));
      document.head.appendChild(el);
    } catch (e) {}
  }

  // ── Back button + dialog behaviour ───────────────────────────────────────────
  // copied from app.babel.js:1427-1576 (BACK_LAYERS/useBackLayer + DIALOG_STACK/useDialog and their module
  // listeners), minus the checkout CO_SUBMITTING flag. Each open sheet owns one history entry, so the phone's
  // Back button closes the sheet instead of leaving the page; Escape closes the top dialog; Tab stays inside.
  const BC_BACK_LAYERS = [];
  let bcBackPending = false,
    bcBackPendingTimer = 0,
    bcBackTimer = 0,
    bcBackLeaving = false;
  const bcBackDepth = () => {
    try {
      const s = window.history.state;
      return (s && s.sfDepth) | 0;
    } catch (e) {
      return 0;
    }
  };
  function bcBackSync() {
    bcBackTimer = 0;
    if (bcBackPending || bcBackLeaving) return;
    const want = BC_BACK_LAYERS.length,
      have = bcBackDepth();
    try {
      if (want > have) {
        for (let d = have + 1; d <= want; d++) window.history.pushState({
          sfLayer: BC_BACK_LAYERS[d - 1].name,
          sfDepth: d
        }, '');
      } else if (want < have) {
        bcBackPending = true;
        clearTimeout(bcBackPendingTimer);
        bcBackPendingTimer = setTimeout(() => {
          bcBackPending = false;
        }, 1500);
        window.history.go(want - have);
      }
    } catch (e) {
      bcBackPending = false;
    }
  }
  const bcBackSchedule = () => {
    if (!bcBackTimer) bcBackTimer = setTimeout(bcBackSync, 0);
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', () => {
      if (bcBackPending) {
        bcBackPending = false;
        clearTimeout(bcBackPendingTimer);
        bcBackSchedule();
        return;
      }
      const have = bcBackDepth();
      if (BC_BACK_LAYERS.slice(have).some(l => l.hold && l.hold())) {
        bcBackSchedule();
        return;
      }
      while (BC_BACK_LAYERS.length > have) {
        const top = BC_BACK_LAYERS.pop();
        try {
          top.close();
        } catch (e) {}
      }
      bcBackSchedule();
    });
    document.addEventListener('click', e => {
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target && a.target !== '_self') return;
      const href = a.getAttribute('href') || '';
      if (/^(#|tel:|mailto:|javascript:)/i.test(href)) return;
      bcBackLeaving = true;
      setTimeout(() => {
        bcBackLeaving = false;
      }, 3000);
    }, true);
  }
  function useBackLayer(open, name, onClose, hold) {
    const closeRef = React.useRef(onClose);
    closeRef.current = onClose;
    const holdRef = React.useRef(hold);
    holdRef.current = hold;
    React.useEffect(() => {
      if (!open) return undefined;
      const entry = {
        name,
        close: () => {
          if (closeRef.current) closeRef.current();
        },
        hold: () => !!(holdRef.current && holdRef.current())
      };
      BC_BACK_LAYERS.push(entry);
      bcBackSchedule();
      return () => {
        const i = BC_BACK_LAYERS.indexOf(entry);
        if (i >= 0) {
          BC_BACK_LAYERS.splice(i, 1);
          bcBackSchedule();
        }
      };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  }
  const BC_DIALOGS = [];
  const BC_FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
  function bcFocusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(BC_FOCUSABLE), el => {
      if (el.getClientRects().length === 0) return false;
      const h = el.closest('[aria-hidden="true"]');
      return !h || !root.contains(h) || h === root;
    });
  }
  function bcFocusSoft(el) {
    if (!el || typeof el.focus !== 'function') return false;
    try {
      el.focus({
        preventScroll: true
      });
    } catch (e) {
      try {
        el.focus();
      } catch (e2) {
        return false;
      }
    }
    return document.activeElement === el;
  }
  function useDialog(open, onClose, opts) {
    const o = opts || {};
    const own = React.useRef(null);
    const ref = o.ref || own;
    const closeRef = React.useRef(onClose);
    closeRef.current = onClose;
    const holdRef = React.useRef(o.hold);
    holdRef.current = o.hold;
    React.useEffect(() => {
      if (!open) return undefined;
      const opener = document.activeElement;
      const entry = {
        ref,
        noFocus: !!o.noFocus,
        el: ref.current,
        close: () => {
          if (closeRef.current) closeRef.current();
        },
        hold: () => !!(holdRef.current && holdRef.current())
      };
      BC_DIALOGS.push(entry);
      const timers = [];
      if (!entry.noFocus) {
        const into = () => {
          const el = ref.current;
          if (el && !el.contains(document.activeElement)) bcFocusSoft(el);
        };
        timers.push(setTimeout(into, 0), setTimeout(into, 320));
      }
      return () => {
        timers.forEach(clearTimeout);
        const i = BC_DIALOGS.indexOf(entry);
        if (i >= 0) BC_DIALOGS.splice(i, 1);
        if (entry.noFocus) return;
        const dlg = entry.el || ref.current;
        setTimeout(() => {
          const a = document.activeElement;
          const lost = !a || a === document.body || !document.contains(a) || dlg && dlg.contains(a);
          if (!lost) return;
          const top = BC_DIALOGS[BC_DIALOGS.length - 1];
          const topEl = top && !top.noFocus ? top.ref.current : null;
          if (opener && opener !== document.body && document.contains(opener) && (!topEl || topEl.contains(opener)) && bcFocusSoft(opener)) return;
          if (topEl) bcFocusSoft(topEl);
        }, 0);
      };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    return ref;
  }
  if (typeof window !== 'undefined') {
    document.addEventListener('keydown', e => {
      if (!BC_DIALOGS.length) return;
      const top = BC_DIALOGS[BC_DIALOGS.length - 1];
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (e.defaultPrevented || e.isComposing) return;
        e.preventDefault();
        if (!top.hold()) top.close();
        return;
      }
      if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
      const root = top.ref.current;
      if (!root) return;
      const list = bcFocusables(root);
      const a = document.activeElement;
      if (!list.length) {
        e.preventDefault();
        bcFocusSoft(root);
        return;
      }
      const first = list[0],
        last = list[list.length - 1];
      if (!root.contains(a) || a === root) {
        e.preventDefault();
        bcFocusSoft(e.shiftKey ? last : first);
      } else if (e.shiftKey && a === first) {
        e.preventDefault();
        bcFocusSoft(last);
      } else if (!e.shiftKey && a === last) {
        e.preventDefault();
        bcFocusSoft(first);
      }
    });
  }

  // ── Small UI pieces ──────────────────────────────────────────────────────────
  function Spinner({
    label,
    small
  }) {
    return /*#__PURE__*/React.createElement("span", {
      className: 'bc-spin' + (small ? ' bc-spin--sm' : ''),
      role: "status",
      "aria-label": label || t('c.loading')
    });
  }
  /** kind: 'primary' (seal) | 'ghost' (outlined) | 'link' (inline text) | 'quiet'. busy → spinner + disabled. */
  function Btn(props) {
    const {
      kind,
      className,
      children,
      busy,
      disabled,
      type,
      ...rest
    } = props;
    return /*#__PURE__*/React.createElement("button", _extends({
      type: type || 'button',
      className: 'bc-btn bc-btn--' + (kind || 'primary') + (className ? ' ' + className : ''),
      disabled: !!(disabled || busy),
      "aria-busy": busy ? 'true' : undefined
    }, rest), busy ? /*#__PURE__*/React.createElement(Spinner, {
      small: true
    }) : null, children);
  }
  /** Toggle chip (aria-pressed), or a radio when radio=true. `sub` = a second line, `badge` = a small tag. */
  function Chip(props) {
    const {
      on,
      onClick,
      children,
      sub,
      badge,
      badgeKind,
      disabled,
      className,
      radio,
      ...rest
    } = props;
    return /*#__PURE__*/React.createElement("button", _extends({
      type: "button",
      className: 'bc-chip' + (on ? ' on' : '') + (sub ? ' bc-chip--2' : '') + (className ? ' ' + className : ''),
      role: radio ? 'radio' : undefined,
      "aria-checked": radio ? !!on : undefined,
      "aria-pressed": radio ? undefined : !!on,
      disabled: disabled,
      onClick: onClick
    }, rest), /*#__PURE__*/React.createElement("span", {
      className: "bc-chip-main"
    }, children), sub ? /*#__PURE__*/React.createElement("span", {
      className: "bc-chip-sub"
    }, sub) : null, badge ? /*#__PURE__*/React.createElement("span", {
      className: 'bc-badge' + (badgeKind ? ' bc-badge--' + badgeKind : '')
    }, badge) : null);
  }
  /** − n + with 44px targets; `name` feeds the trilingual aria-labels. */
  function Stepper({
    value,
    onChange,
    min,
    max,
    name,
    compact
  }) {
    const v = Number(value) || 0;
    const lo = min == null ? 0 : min,
      hi = max == null ? 99 : max;
    return /*#__PURE__*/React.createElement("div", {
      className: 'bc-step' + (compact ? ' bc-step--sm' : '') + (v > 0 ? ' has-val' : ''),
      role: "group",
      "aria-label": name
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-step-b",
      "aria-label": t('c.minus', {
        name
      }),
      disabled: v <= lo,
      onClick: () => onChange(Math.max(lo, v - 1))
    }, "\u2212"), /*#__PURE__*/React.createElement("output", {
      className: "bc-step-val",
      "aria-live": "polite"
    }, digits(v)), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-step-b",
      "aria-label": t('c.plus', {
        name
      }),
      disabled: v >= hi,
      onClick: () => onChange(Math.min(hi, v + 1))
    }, "+"));
  }
  /** const [toastNode, showToast, hideToast] = useToast(); showToast(msg, { action, label, ms }) */
  function useToast() {
    const [st, setSt] = React.useState(null);
    const timer = React.useRef(0);
    const hide = React.useCallback(() => {
      clearTimeout(timer.current);
      setSt(null);
    }, []);
    const show = React.useCallback((msg, opts) => {
      const o = opts || {};
      clearTimeout(timer.current);
      setSt({
        msg,
        action: o.action || null,
        label: o.label || '',
        id: Date.now(),
        kind: o.kind || ''
      });
      timer.current = setTimeout(() => setSt(null), o.ms || (o.action ? 6500 : 3800));
    }, []);
    React.useEffect(() => () => clearTimeout(timer.current), []);
    return [/*#__PURE__*/React.createElement(Toast, {
      key: "bc-toast",
      toast: st,
      onClose: hide
    }), show, hide];
  }
  function Toast({
    toast,
    onClose
  }) {
    return /*#__PURE__*/React.createElement("div", {
      className: 'bc-toast' + (toast ? ' is-on' : '') + (toast && toast.kind ? ' bc-toast--' + toast.kind : ''),
      role: "status",
      "aria-live": "polite"
    }, toast ? /*#__PURE__*/React.createElement("div", {
      className: "bc-toast-in",
      key: toast.id
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-toast-msg"
    }, toast.msg), toast.action ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-toast-act",
      onClick: () => {
        try {
          toast.action();
        } catch (e) {}
        onClose();
      }
    }, toast.label || t('c.undo')) : null) : null);
  }
  let bcSheetSeq = 0;
  /** Bottom sheet: focus trap, Escape, and the phone's Back button close it (one history entry). */
  function Sheet({
    open,
    onClose,
    title,
    children,
    footer,
    className,
    hold
  }) {
    const ref = React.useRef(null);
    const idRef = React.useRef(null);
    if (!idRef.current) idRef.current = 'bc-sheet-' + ++bcSheetSeq;
    useBackLayer(!!open, 'bread-sheet', onClose, hold);
    useDialog(!!open, onClose, {
      ref,
      hold
    });
    React.useEffect(() => {
      if (!open) return undefined;
      const b = document.body,
        prev = b.style.overflow;
      b.style.overflow = 'hidden';
      return () => {
        b.style.overflow = prev;
      };
    }, [open]);
    if (!open) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-sheet-back",
      onClick: e => {
        if (e.target === e.currentTarget && !(hold && hold())) onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      ref: ref,
      className: 'bc-sheet' + (className ? ' ' + className : ''),
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": idRef.current,
      tabIndex: -1
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-sheet-grip",
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("div", {
      className: "bc-sheet-head"
    }, /*#__PURE__*/React.createElement("h2", {
      id: idRef.current,
      className: "bc-sheet-title"
    }, title), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-sheet-x",
      onClick: onClose,
      "aria-label": t('c.close')
    }, "\u2715")), /*#__PURE__*/React.createElement("div", {
      className: "bc-sheet-body"
    }, children), footer ? /*#__PURE__*/React.createElement("div", {
      className: "bc-sheet-foot"
    }, footer) : null));
  }
  // copied from app.babel.js:8318-8360 (ErrorBoundary) — same Sentry hook, bread wording.
  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        hasError: false
      };
    }
    static getDerivedStateFromError() {
      return {
        hasError: true
      };
    }
    componentDidCatch(error, info) {
      console.error('[bread ErrorBoundary]', error, info);
      try {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') window.Sentry.captureException(error, {
          extra: info,
          tags: {
            area: 'bread'
          }
        });
      } catch (e) {}
    }
    render() {
      if (!this.state.hasError) return this.props.children;
      return /*#__PURE__*/React.createElement("div", {
        className: "bc-app"
      }, /*#__PURE__*/React.createElement("div", {
        className: "bc-card bc-crash"
      }, /*#__PURE__*/React.createElement("h2", {
        className: "bc-h2"
      }, "Something went wrong."), /*#__PURE__*/React.createElement("div", {
        dir: "rtl",
        className: "bc-crash-rtl"
      }, "\u0645\u0634\u06A9\u0644\u06CC \u067E\u06CC\u0634 \u0622\u0645\u062F \xB7 \u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627"), /*#__PURE__*/React.createElement("p", null, "The kitchen is fine \u2014 the page got confused. Reload and your choices will still be here."), /*#__PURE__*/React.createElement(Btn, {
        onClick: () => {
          try {
            window.location.reload();
          } catch (e) {}
        }
      }, "Reload \xB7 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \xB7 \u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u062D\u0645\u064A\u0644")));
    }
  }

  /** UAE mobile: fixed +971, local digits typed (Persian/Arabic digits accepted). Value = what was typed. */
  function PhoneInput({
    id,
    value,
    onChange,
    onBlur,
    invalid,
    autoFocus,
    describedBy
  }) {
    return /*#__PURE__*/React.createElement("div", {
      className: 'bc-phone' + (invalid ? ' is-err' : ''),
      dir: "ltr"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-phone-cc",
      "aria-hidden": "true"
    }, "\uD83C\uDDE6\uD83C\uDDEA +971"), /*#__PURE__*/React.createElement("input", {
      id: id,
      className: 'bc-input bc-phone-in' + (invalid ? ' is-err' : ''),
      type: "tel",
      inputMode: "tel",
      autoComplete: "tel-national",
      placeholder: "50 123 4567",
      value: value || '',
      maxLength: 18,
      autoFocus: autoFocus,
      "aria-invalid": invalid ? 'true' : undefined,
      "aria-describedby": describedBy,
      onChange: e => onChange(e.target.value),
      onBlur: onBlur
    }));
  }
  function bcPhoneErr() {
    return tn('ph.err', {
      ex: /*#__PURE__*/React.createElement("bdi", {
        dir: "ltr",
        className: "ltr-num"
      }, "050 123 4567")
    });
  }

  // ── Geo helpers ──────────────────────────────────────────────────────────────
  // copied from app.babel.js:3690-3725 (ensureLeaflet + haversineKmJS): same-origin /vendor/leaflet, lazy.
  let _bcLeafletPromise = null;
  function ensureLeaflet() {
    if (typeof window !== 'undefined' && window.L) return Promise.resolve(window.L);
    if (_bcLeafletPromise) return _bcLeafletPromise;
    _bcLeafletPromise = new Promise((resolve, reject) => {
      try {
        if (!document.querySelector('link[data-leaflet]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = '/vendor/leaflet/leaflet.css';
          link.setAttribute('data-leaflet', '1');
          document.head.appendChild(link);
        }
        const s = document.createElement('script');
        s.src = '/vendor/leaflet/leaflet.js';
        s.async = true;
        s.onload = () => resolve(window.L);
        s.onerror = () => {
          _bcLeafletPromise = null;
          reject(new Error('Leaflet failed to load'));
        };
        document.head.appendChild(s);
      } catch (e) {
        reject(e);
      }
    });
    return _bcLeafletPromise;
  }
  function bcHaversineKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }
  const BC_IS_TOUCH = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
  // copied from app.babel.js:539-543 (foldArabic): ی/ک/ه/ا folding so either keyboard matches either spelling.
  const BC_ZW_RE = new RegExp('[' + String.fromCharCode(0x200C, 0x200F, 0x200E) + ']', 'g');
  const BC_DIAC_RE = new RegExp('[' + String.fromCharCode(0x064B) + '-' + String.fromCharCode(0x0652) + String.fromCharCode(0x0670, 0x0640) + ']', 'g');
  function bcFoldArabic(s) {
    return String(s || '').replace(/[يى]/g, 'ی').replace(/ك/g, 'ک').replace(/ة/g, 'ه').replace(/[أإآ]/g, 'ا').replace(BC_ZW_RE, '').replace(BC_DIAC_RE, '');
  }
  function bcAreaName(L, a) {
    return (L === 'ar' ? a.ar || a.en : L === 'fa' ? a.fa || a.en : a.en) || '';
  }
  function bcAreaNameA(L, a) {
    return L === 'en' ? a.fa || '' : a.en || '';
  }
  // copied from app.babel.js:1039-1068 (reverseGeocodeArea / geocodeAreaCenter): Nominatim, one call per choice.
  async function bcReverseGeocodeArea(lat, lng) {
    try {
      const r = await fetch('https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=14&accept-language=en&lat=' + lat + '&lon=' + lng);
      if (!r.ok) return null;
      const j = await r.json();
      const a = j.address || {};
      const cands = [a.suburb, a.neighbourhood, a.quarter, a.city_district, a.residential, a.district].filter(Boolean);
      for (const raw of cands) {
        const name = String(raw).toLowerCase();
        const hit = bcAreas().find(o => {
          const en = (o.en || '').toLowerCase();
          return en.length > 3 && (name.includes(en) || en.includes(name));
        });
        if (hit) return hit.key;
      }
      return null;
    } catch (e) {
      return null;
    }
  }
  async function bcGeocodeAreaCenter(areaKey) {
    try {
      const a = bcFindArea(areaKey);
      if (!a || !a.en) return null;
      const r = await fetch('https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&accept-language=en&q=' + encodeURIComponent(a.en + ', Dubai, United Arab Emirates'));
      if (!r.ok) return null;
      const j = await r.json();
      if (Array.isArray(j) && j[0]) return {
        lat: Number(j[0].lat),
        lng: Number(j[0].lon)
      };
      return null;
    } catch (e) {
      return null;
    }
  }
  /** Subscriber delivery fee (AED) for an area band, and the minimum breads per drop there. */
  function bcBandFee(band) {
    const s = bcSettings();
    return Number(s.fee_by_band[bandOf(band)]) || 0;
  }
  function bcBandMin(band) {
    return minBreads('delivery', band, bcSettings());
  }

  // ── MapPicker ────────────────────────────────────────────────────────────────
  // copied from app.babel.js:3734-3863 (MapPicker). Same behaviour: fixed centre pin, the map never commits a
  // pin by panning (only the "Set pin here" chip or GPS), two-finger pan on touch, and the canvas carries
  // data-clarity-mask="True" so session replay never records a doorstep. DO NOT remove that attribute.
  function MapPicker({
    restaurant,
    coords,
    onPick,
    focusPoint
  }) {
    const elRef = React.useRef(null);
    const mapRef = React.useRef(null);
    const lineRef = React.useRef(null);
    const [loadError, setLoadError] = React.useState(false);
    const [liveDist, setLiveDist] = React.useState(null);
    const onPickRef = React.useRef(onPick);
    onPickRef.current = onPick;
    const coordsRef = React.useRef(coords);
    coordsRef.current = coords;
    const [pinOffer, setPinOffer] = React.useState(null);
    React.useEffect(() => {
      let cancelled = false;
      ensureLeaflet().then(L => {
        if (cancelled || mapRef.current || !elRef.current) return;
        const start = coords || restaurant;
        const map = L.map(elRef.current, {
          zoomControl: true,
          attributionControl: true,
          dragging: !BC_IS_TOUCH
        }).setView([start.lat, start.lng], coords ? 16 : 13);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap'
        }).addTo(map);
        const restIcon = L.divIcon({
          className: 'mp-rest-wrap',
          html: '<span class="mp-rest-dot">🍲</span><span class="mp-rest-lbl">' + BC_SIGN_EN + '</span>',
          iconSize: [0, 0]
        });
        L.marker([restaurant.lat, restaurant.lng], {
          icon: restIcon,
          interactive: false,
          keyboard: false
        }).addTo(map);
        const redraw = () => {
          const c = map.getCenter();
          const d = bcHaversineKm(restaurant.lat, restaurant.lng, c.lat, c.lng);
          if (d < 0.06) {
            if (lineRef.current) {
              map.removeLayer(lineRef.current);
              lineRef.current = null;
            }
            setLiveDist(null);
          } else {
            const pts = [[restaurant.lat, restaurant.lng], [c.lat, c.lng]];
            if (lineRef.current) lineRef.current.setLatLngs(pts);else lineRef.current = L.polyline(pts, {
              color: '#8a1c1c',
              weight: 3,
              dashArray: '2 7',
              opacity: 0.9
            }).addTo(map);
            setLiveDist(d);
          }
        };
        redraw();
        map.on('move', redraw);
        map.on('moveend', () => {
          const c = map.getCenter();
          const anchor = coordsRef.current || restaurant;
          const moved = bcHaversineKm(anchor.lat, anchor.lng, c.lat, c.lng) > 0.06;
          setPinOffer(moved ? {
            lat: c.lat,
            lng: c.lng
          } : null);
        });
        mapRef.current = map;
        setTimeout(() => {
          try {
            map.invalidateSize();
          } catch (e) {}
        }, 90);
      }).catch(() => {
        if (!cancelled) setLoadError(true);
      });
      return () => {
        cancelled = true;
        if (mapRef.current) {
          try {
            mapRef.current.remove();
          } catch (e) {}
          mapRef.current = null;
        }
      };
    }, []); // init once
    React.useEffect(() => {
      const map = mapRef.current;
      if (!map || !coords) return;
      const c = map.getCenter();
      if (Math.abs(c.lat - coords.lat) < 5e-5 && Math.abs(c.lng - coords.lng) < 5e-5) return;
      map.setView([coords.lat, coords.lng], Math.max(map.getZoom(), 16));
      setPinOffer(null);
    }, [coords]);
    React.useEffect(() => {
      const map = mapRef.current;
      if (!map || !focusPoint || coordsRef.current) return;
      try {
        map.setView([focusPoint.lat, focusPoint.lng], 14);
      } catch (e) {}
    }, [focusPoint]);
    const L = BC_LANG;
    if (loadError) return /*#__PURE__*/React.createElement("div", {
      className: "map-fallback"
    }, /*#__PURE__*/React.createElement("span", null, t('map.fallback')));
    return /*#__PURE__*/React.createElement("div", {
      className: "map-wrap bc-map"
    }, /*#__PURE__*/React.createElement("div", {
      ref: elRef,
      className: "map-canvas",
      "data-clarity-mask": "True"
    }), /*#__PURE__*/React.createElement("div", {
      className: "map-centerpin",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("span", {
      className: "mcp-head"
    }), /*#__PURE__*/React.createElement("span", {
      className: "mcp-stem"
    })), liveDist != null && /*#__PURE__*/React.createElement("div", {
      className: "map-dist"
    }, /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: "ltr-num"
    }, L === 'fa' ? digits(liveDist.toFixed(1), 'fa') : liveDist.toFixed(1)), " ", L === 'fa' ? 'کیلومتر' : L === 'ar' ? 'كم' : 'km', " ", /*#__PURE__*/React.createElement("span", null, t('map.toRestaurant'))), pinOffer && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "map-setpin",
      onClick: () => {
        onPickRef.current(pinOffer);
        setPinOffer(null);
      }
    }, "\uD83D\uDCCD ", /*#__PURE__*/React.createElement("span", null, t('map.setPin'))), /*#__PURE__*/React.createElement("div", {
      className: "map-hint"
    }, /*#__PURE__*/React.createElement("span", null, t(BC_IS_TOUCH ? 'map.twoFinger' : 'map.hint'))));
  }

  // ── AreaPicker ───────────────────────────────────────────────────────────────
  // copied from app.babel.js:3868-4017 (AreaPicker): searchable, Arabic/Persian-keyboard folding, full-screen
  // sheet on phones (paper.css .area-combo.open ≤720px) with Back/Escape closing it. Bread changes: areas come
  // from __BREAD__.areas, groups are the subscriber fee BANDS (fee + minimum breads), no promo strike-through.
  function AreaPicker({
    value,
    onChange,
    openRef,
    invalid
  }) {
    const L = BC_LANG;
    const areas = bcAreas();
    const selected = bcFindArea(value);
    const [query, setQuery] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [hi, setHi] = React.useState(0);
    const wrapRef = React.useRef(null);
    const inputRef = React.useRef(null);
    if (openRef) openRef.current = () => {
      const el = inputRef.current;
      if (!el) return;
      setOpen(true);
      try {
        el.focus();
      } catch (e) {}
    };
    React.useEffect(() => {
      if (!open) return undefined;
      const onDoc = e => {
        if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('pointerdown', onDoc);
      return () => document.removeEventListener('pointerdown', onDoc);
    }, [open]);
    React.useEffect(() => {
      setHi(0);
    }, [query]);
    const sheetMode = open && (() => {
      try {
        return window.matchMedia('(max-width:720px)').matches;
      } catch (e) {
        return false;
      }
    })();
    useBackLayer(sheetMode, 'area', () => setOpen(false));
    useDialog(sheetMode, () => setOpen(false), {
      noFocus: true,
      ref: wrapRef
    });
    const qRaw = query.trim(),
      q = qRaw.toLowerCase(),
      qF = bcFoldArabic(qRaw);
    const matches = q ? areas.filter(a => a.en.toLowerCase().indexOf(q) !== -1 || a.fa && bcFoldArabic(a.fa).indexOf(qF) !== -1 || a.ar && bcFoldArabic(a.ar).indexOf(qF) !== -1 || a.key.indexOf(q) !== -1) : areas;
    const pick = a => {
      if (!a) return;
      onChange(a.key);
      setQuery('');
      setOpen(false);
    };
    const onKey = e => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        setHi(h => Math.min(h + 1, matches.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHi(h => Math.max(h - 1, 0));
      } else if (e.key === 'Enter') {
        if (open && matches[hi]) {
          e.preventDefault();
          pick(matches[hi]);
        }
      } else if (e.key === 'Escape') {
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
      }
    };
    const groups = [];
    let cur = null;
    matches.forEach((a, i) => {
      if (a.band !== cur) {
        groups.push({
          band: a.band,
          items: []
        });
        cur = a.band;
      }
      groups[groups.length - 1].items.push({
        a,
        i
      });
    });
    const feeTxt = band => {
      const f = bcBandFee(band);
      return f > 0 ? aedStr(f) : t('c.free');
    };
    const inputVal = !open && selected ? bcAreaName(L, selected) + ' — ' + feeTxt(selected.band) : query;
    return /*#__PURE__*/React.createElement("div", {
      className: 'area-combo bc-area' + (open ? ' open' : '') + (invalid ? ' area-required' : ''),
      ref: wrapRef,
      role: sheetMode ? 'dialog' : undefined,
      "aria-modal": sheetMode ? 'true' : undefined,
      "aria-labelledby": sheetMode ? 'bc-area-sheet-title' : undefined
    }, sheetMode && /*#__PURE__*/React.createElement("span", {
      id: "bc-area-sheet-title",
      className: "sf-vh"
    }, t('area.label')), /*#__PURE__*/React.createElement("div", {
      className: "area-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "area-search-ic",
      "aria-hidden": "true"
    }, "\u2315"), /*#__PURE__*/React.createElement("input", {
      ref: inputRef,
      className: "area-input",
      type: "text",
      autoComplete: "off",
      placeholder: t('area.ph'),
      value: inputVal,
      onChange: e => {
        if (selected) onChange('');
        setQuery(e.target.value);
        setOpen(true);
      },
      onFocus: () => setOpen(true),
      onKeyDown: onKey,
      "aria-label": t('area.label'),
      "aria-invalid": invalid ? 'true' : undefined
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "area-sheet-close",
      "aria-label": t('c.close'),
      onMouseDown: e => {
        e.preventDefault();
        setOpen(false);
      }
    }, "\u2715"), selected && !open ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "area-clear",
      "aria-label": t('area.change'),
      onMouseDown: e => {
        e.preventDefault();
        onChange('');
        setQuery('');
        setOpen(true);
        if (inputRef.current) inputRef.current.focus();
      }
    }, "\u2715") : /*#__PURE__*/React.createElement("span", {
      className: "area-caret",
      "aria-hidden": "true"
    }, "\u25BE")), open && /*#__PURE__*/React.createElement("div", {
      className: "area-menu",
      role: "listbox"
    }, matches.length === 0 ? /*#__PURE__*/React.createElement("div", {
      className: "area-empty"
    }, /*#__PURE__*/React.createElement("span", null, t('area.noMatch'))) : groups.map(g => /*#__PURE__*/React.createElement("div", {
      key: g.band,
      className: "area-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "area-group-head bc-area-gh"
    }, bcBandFee(g.band) > 0 ? tn('area.group', {
      fee: /*#__PURE__*/React.createElement("bdi", {
        dir: "ltr",
        className: "ltr-num"
      }, aedStr(bcBandFee(g.band))),
      breads: t('n.breads', {
        n: bcBandMin(g.band)
      })
    }) : t('area.groupFree', {
      breads: t('n.breads', {
        n: bcBandMin(g.band)
      })
    })), g.items.map(({
      a,
      i
    }) => /*#__PURE__*/React.createElement("div", {
      key: a.key,
      role: "option",
      "aria-selected": value === a.key,
      className: 'area-opt' + (i === hi ? ' on' : '') + (value === a.key ? ' sel' : ''),
      onMouseEnter: () => setHi(i),
      onClick: () => pick(a)
    }, /*#__PURE__*/React.createElement("span", {
      className: "area-opt-name"
    }, bcAreaName(L, a)), bcAreaNameA(L, a) ? /*#__PURE__*/React.createElement("span", {
      className: "area-opt-fa"
    }, bcAreaNameA(L, a)) : /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", {
      className: "area-opt-fee"
    }, feeTxt(a.band))))))));
  }

  // ── PinField: map + GPS + saved-pin confirmation ─────────────────────────────
  // GPS body copied from app.babel.js:4730-4772 (pinLocation): low accuracy (fast, never hangs on desktops),
  // 12 s timeout + a 15 s hard fail-safe so the button can never stay stuck on "Locating…".
  // Props: value {lat,lng}|null, onChange(coords), needsConfirm, onConfirm(), areaKey (map glides there while no
  // pin), onAreaGuess(key) (a deliberate pin pre-selects the matching area), error (show the required message).
  function PinField({
    value,
    onChange,
    needsConfirm,
    onConfirm,
    areaKey,
    onAreaGuess,
    error
  }) {
    const [busy, setBusy] = React.useState(false);
    const [gErr, setGErr] = React.useState('');
    const [focus, setFocus] = React.useState(null);
    const restaurant = bcRestaurant();
    const alive = React.useRef(true);
    React.useEffect(() => () => {
      alive.current = false;
    }, []);
    React.useEffect(() => {
      if (!areaKey || value) return undefined;
      let off = false;
      bcGeocodeAreaCenter(areaKey).then(p => {
        if (!off && p) setFocus(p);
      });
      return () => {
        off = true;
      };
    }, [areaKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const picked = (c, how) => {
      onChange({
        lat: c.lat,
        lng: c.lng
      });
      setGErr('');
      bcClarity('pin_set');
      if (!areaKey && onAreaGuess) bcReverseGeocodeArea(c.lat, c.lng).then(k => {
        if (k && alive.current) onAreaGuess(k);
      });
    };
    const gps = () => {
      setGErr('');
      if (!navigator.geolocation) {
        setGErr('geo.noSupport');
        return;
      }
      setBusy(true);
      navigator.geolocation.getCurrentPosition(pos => {
        if (!alive.current) return;
        setBusy(false);
        picked({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        }, 'gps');
      }, err => {
        if (!alive.current) return;
        setGErr(err.code === 1 ? 'geo.denied' : err.code === 2 ? 'geo.unavailable' : err.code === 3 ? 'geo.timeout' : 'geo.failed');
        setBusy(false);
      }, {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 60000
      });
      setTimeout(() => {
        if (!alive.current) return;
        setBusy(b => {
          if (b) {
            setGErr('geo.slow');
            return false;
          }
          return b;
        });
      }, 15000);
    };
    const ok = !!value && !needsConfirm;
    return /*#__PURE__*/React.createElement("div", {
      className: 'bc-pin full geo' + (ok ? ' geo-pinned-ok' : ' geo-required') + (error ? ' field-err' : '')
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-label"
    }, t('geo.label'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-req",
      "aria-hidden": "true"
    }, "*")), /*#__PURE__*/React.createElement(MapPicker, {
      restaurant: restaurant,
      coords: value,
      focusPoint: focus,
      onPick: c => picked(c, 'map')
    }), /*#__PURE__*/React.createElement("div", {
      className: "geo-row bc-geo-row"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: 'geo-btn bc-geo-btn' + (value ? '' : ' geo-btn-required'),
      onClick: gps,
      disabled: busy
    }, busy ? /*#__PURE__*/React.createElement("span", null, t('geo.locating')) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "geo-pin",
      "aria-hidden": "true"
    }, "\u2316"), " ", /*#__PURE__*/React.createElement("span", null, t('geo.useGps')))), value && !needsConfirm ? /*#__PURE__*/React.createElement("span", {
      className: "bc-pinned"
    }, t('geo.pinned')) : null), value && needsConfirm ? /*#__PURE__*/React.createElement("div", {
      className: "bc-pin-check"
    }, /*#__PURE__*/React.createElement("span", null, t('geo.check')), /*#__PURE__*/React.createElement(Btn, {
      kind: "ghost",
      onClick: () => {
        onConfirm && onConfirm();
        bcClarity('pin_set');
      }
    }, "\u2713 ", t('geo.checkYes'))) : null, !value ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-geo-hint",
      disabled: busy,
      onClick: () => {
        bcClarity('pin_hint_gps');
        gps();
      }
    }, t('geo.hint')) : null, gErr ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t(gErr)) : null, error ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t(error)) : null);
  }

  // ── AddressForm (delivery address; shared by the builder and the plan page) ──
  // value: { area_key, line, unit, handover:'door'|'hand'|'reception', ring_bell:boolean, gate_note, notes,
  //          lat, lng, pin_ok:boolean }  — pin_ok false means "a saved pin that still needs one confirming tap".
  // errors: { area?, pin?, line? } → i18n keys (from addressErrors()). Pickup is NOT handled here.
  const BC_HANDOVERS = [['door', 'ad.ho.door'], ['hand', 'ad.ho.hand'], ['reception', 'ad.ho.reception']];
  function addressErrors(a) {
    const e = {};
    if (!a || !bcFindArea(a.area_key)) e.area = 'ad.err.area';
    if (!a || !Number.isFinite(Number(a.lat)) || !Number.isFinite(Number(a.lng)) || a.lat == null || a.lng == null) e.pin = 'ad.err.pin';else if (a.lat < 22.5 || a.lat > 26.5 || a.lng < 51 || a.lng > 56.5) e.pin = 'ad.err.pin'; // = the server's UAE box
    else if (a.pin_ok === false) e.pin = 'ad.err.pinCheck';
    if (!a || cleanLine(a.line, 200).length < 2) e.line = 'ad.err.line';
    return e;
  }
  function AddressForm({
    value,
    onChange,
    errors,
    showErrors
  }) {
    const a = value || {};
    const err = showErrors ? errors || {} : {};
    const set = patch => onChange(Object.assign({}, a, patch));
    const area = bcFindArea(a.area_key);
    const coords = a.lat != null && a.lng != null && Number.isFinite(Number(a.lat)) ? {
      lat: Number(a.lat),
      lng: Number(a.lng)
    } : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-addr"
    }, /*#__PURE__*/React.createElement("div", {
      className: 'bc-field' + (err.area ? ' is-err' : ''),
      id: "bc-f-area"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label"
    }, t('area.label'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-req",
      "aria-hidden": "true"
    }, "*")), /*#__PURE__*/React.createElement(AreaPicker, {
      value: a.area_key || '',
      onChange: k => set({
        area_key: k
      }),
      invalid: !!err.area
    }), area ? /*#__PURE__*/React.createElement("div", {
      className: "bc-note bc-note--fee"
    }, bcBandFee(area.band) > 0 ? tn('ad.fee', {
      area: bcAreaName(BC_LANG, area),
      fee: fmtAed(bcBandFee(area.band)),
      breads: t('n.breads', {
        n: bcBandMin(area.band)
      })
    }) : t('ad.feeFree', {
      area: bcAreaName(BC_LANG, area),
      breads: t('n.breads', {
        n: bcBandMin(area.band)
      })
    })) : null, err.area ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t(err.area)) : null), /*#__PURE__*/React.createElement("div", {
      id: "bc-f-pin"
    }, /*#__PURE__*/React.createElement(PinField, {
      value: coords,
      needsConfirm: !!coords && a.pin_ok === false,
      areaKey: a.area_key,
      onChange: c => set({
        lat: c.lat,
        lng: c.lng,
        pin_ok: true
      }),
      onConfirm: () => set({
        pin_ok: true
      }),
      onAreaGuess: k => {
        if (!a.area_key) set({
          area_key: k
        });
      },
      error: err.pin || ''
    })), /*#__PURE__*/React.createElement("div", {
      className: 'bc-field' + (err.line ? ' is-err' : ''),
      id: "bc-f-line"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-ad-line"
    }, t('ad.line'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-req",
      "aria-hidden": "true"
    }, "*")), /*#__PURE__*/React.createElement("input", {
      id: "bc-ad-line",
      className: 'bc-input' + (err.line ? ' is-err' : ''),
      type: "text",
      autoComplete: "address-line1",
      maxLength: 160,
      placeholder: t('ad.linePh'),
      value: a.line || '',
      onChange: e => set({
        line: e.target.value
      }),
      "aria-invalid": err.line ? 'true' : undefined
    }), err.line ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t(err.line)) : null), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-ad-unit"
    }, t('ad.unit'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-opt"
    }, t('c.optional'))), /*#__PURE__*/React.createElement("input", {
      id: "bc-ad-unit",
      className: "bc-input",
      type: "text",
      autoComplete: "address-line2",
      maxLength: 40,
      value: a.unit || '',
      onChange: e => set({
        unit: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-label",
      id: "bc-ad-ho"
    }, t('ad.handover')), /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-chips--3",
      role: "radiogroup",
      "aria-labelledby": "bc-ad-ho"
    }, BC_HANDOVERS.map(([k, key]) => /*#__PURE__*/React.createElement(Chip, {
      key: k,
      radio: true,
      on: (a.handover || 'door') === k,
      onClick: () => set({
        handover: k
      })
    }, t(key))))), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-label",
      id: "bc-ad-bell"
    }, t('ad.bell')), /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-chips--2",
      role: "radiogroup",
      "aria-labelledby": "bc-ad-bell"
    }, /*#__PURE__*/React.createElement(Chip, {
      radio: true,
      on: !!a.ring_bell,
      onClick: () => set({
        ring_bell: true
      })
    }, t('ad.bellYes')), /*#__PURE__*/React.createElement(Chip, {
      radio: true,
      on: !a.ring_bell,
      onClick: () => set({
        ring_bell: false
      })
    }, t('ad.bellNo')))), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-ad-gate"
    }, t('ad.gate'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-opt"
    }, t('c.optional'))), /*#__PURE__*/React.createElement("input", {
      id: "bc-ad-gate",
      className: "bc-input",
      type: "text",
      maxLength: 120,
      value: a.gate_note || '',
      onChange: e => set({
        gate_note: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-ad-notes"
    }, t('ad.notes'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-opt"
    }, t('c.optional'))), /*#__PURE__*/React.createElement("input", {
      id: "bc-ad-notes",
      className: "bc-input",
      type: "text",
      maxLength: 200,
      value: a.notes || '',
      onChange: e => set({
        notes: e.target.value
      })
    })));
  }

  // ── Saved plans + "Find my plan" (shared) ────────────────────────────────────
  function SavedPlansCard({
    onFind,
    plans
  }) {
    const list = plans || loadPlansLocal();
    if (!list.length) {
      return /*#__PURE__*/React.createElement("div", {
        className: "bc-find-line"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bc-btn bc-btn--link",
        onClick: onFind
      }, t('find.q')));
    }
    // The token never goes into the DOM (/bread runs Clarity/Meta/GA, which record hrefs): the click hands it to
    // /bread/plan through sessionStorage 'sf-bread-token', like /me. Storage blocked → navigate by script instead.
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-card bc-saved",
      "data-clarity-mask": "True"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-saved-h"
    }, t('saved.title', {
      n: list.length
    })), list.map(p => /*#__PURE__*/React.createElement("a", {
      key: p.ref,
      className: "bc-saved-row",
      href: '/bread/plan?lang=' + BC_LANG,
      onClick: e => {
        try {
          sessionStorage.setItem('sf-bread-token', p.token);
        } catch (err) {
          e.preventDefault();
          window.location.assign(planHref(p.token));
        }
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-saved-ref"
    }, /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: "ltr-num"
    }, p.ref), p.name ? /*#__PURE__*/React.createElement("span", {
      className: "bc-saved-name"
    }, " \xB7 ", p.name) : null), /*#__PURE__*/React.createElement("span", {
      className: "bc-saved-go"
    }, t('saved.open'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-arrow",
      "aria-hidden": "true"
    }, "\u2192")))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-btn bc-btn--link bc-saved-find",
      onClick: onFind
    }, t('find.other')));
  }
  function FindPlanSheet({
    open,
    onClose
  }) {
    const [phone, setPhone] = React.useState('');
    const [touched, setTouched] = React.useState(false);
    const [state, setState] = React.useState('idle'); // idle | busy | sent | error
    const [err, setErr] = React.useState('');
    React.useEffect(() => {
      if (open) {
        setState('idle');
        setErr('');
        setTouched(false);
      }
    }, [open]);
    const e164 = phoneE164(phone);
    const send = () => {
      setTouched(true);
      if (!e164 || state === 'busy') return;
      setState('busy');
      setErr('');
      api('/api/bread/find', {
        phone: e164
      }).then(r => {
        if (r && r.ok) {
          setState('sent');
          sfTrack('bread_find', {});
          return;
        }
        setState('error');
        setErr(r && r.code === 'network' ? 'c.network' : r && r.code === 'rate_limited' ? 'c.rate' : 'c.server');
      });
    };
    const call = /*#__PURE__*/React.createElement("a", {
      href: 'tel:' + BC_PHONE_TEL,
      className: "bc-a"
    }, /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: "ltr-num"
    }, BC_PHONE_DISPLAY));
    return /*#__PURE__*/React.createElement(Sheet, {
      open: open,
      onClose: onClose,
      title: t('find.title'),
      footer: state === 'sent' ? /*#__PURE__*/React.createElement(Btn, {
        kind: "ghost",
        className: "bc-grow",
        onClick: onClose
      }, t('c.close')) : /*#__PURE__*/React.createElement(Btn, {
        className: "bc-grow",
        busy: state === 'busy',
        onClick: send
      }, t('find.send'))
    }, state === 'sent' ? /*#__PURE__*/React.createElement("div", {
      className: "bc-ok-box",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-ok-ic",
      "aria-hidden": "true"
    }, "\u2713"), /*#__PURE__*/React.createElement("p", null, t('find.sent'))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      className: "bc-p"
    }, t('find.body')), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-find-phone"
    }, t('ph.label')), /*#__PURE__*/React.createElement(PhoneInput, {
      id: "bc-find-phone",
      value: phone,
      onChange: setPhone,
      onBlur: () => setTouched(true),
      invalid: touched && !e164,
      describedBy: "bc-find-err"
    }), touched && !e164 ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      id: "bc-find-err",
      role: "alert"
    }, bcPhoneErr()) : null), err ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t(err)) : null), /*#__PURE__*/React.createElement("p", {
      className: "bc-small"
    }, tn('find.call', {
      phone: call
    })));
  }

  // ── Styles (injected once; every class bc- prefixed; reuses the site tokens) ──
  const BC_CSS = `
.bc-app{max-width:640px;margin:0 auto;padding:6px 16px 32px;color:var(--ink,#221408);font-family:"Cormorant Garamond",Georgia,serif;font-size:17px;line-height:1.45;overflow-wrap:break-word;-webkit-tap-highlight-color:rgba(138,28,28,.12)}
html[dir="rtl"] .bc-app{font-family:"Vazirmatn",Tahoma,sans-serif;font-size:15.5px;line-height:1.75}
html[dir="rtl"] .bc-app *, html[dir="rtl"] .bc-sheet *, html[dir="rtl"] .bc-bar *{letter-spacing:0!important}
.bc-app *, .bc-sheet *, .bc-bar *{box-sizing:border-box}
.bc-app button, .bc-sheet button, .bc-bar button{font-family:inherit}
.bc-aed{font-family:"JetBrains Mono","Vazirmatn",ui-monospace,monospace;font-size:.92em;letter-spacing:-.01em;white-space:nowrap}
.bc-h2{font-family:"IM Fell English",Georgia,serif;font-weight:400;font-size:24px;line-height:1.2;margin:0 0 8px}
html[dir="rtl"] .bc-h2{font-family:"Vazirmatn",sans-serif;font-weight:700;font-size:20px}
html[lang="ar"] .bc-h2{font-family:"Amiri","Vazirmatn",serif}
.bc-p{margin:0 0 12px}
.bc-small{font-size:14.5px;color:var(--ink-soft,#553418);margin:10px 0 0}
.bc-a{color:var(--seal,#8a1c1c);text-decoration:underline;text-underline-offset:2px}
.bc-card{background:rgba(255,250,238,.82);border:1px solid rgba(70,40,15,.22);border-radius:14px;box-shadow:0 1px 2px rgba(70,40,15,.08);padding:14px 16px;margin:12px 0}
.bc-note{margin-top:8px;padding:8px 12px;border-inline-start:3px solid var(--gold,#a07b2f);background:rgba(160,123,47,.08);border-radius:0 8px 8px 0;font-size:15px;color:var(--ink,#221408)}
html[dir="rtl"] .bc-note{border-radius:8px 0 0 8px}
.bc-note--fee{border-inline-start-color:var(--seal,#8a1c1c);background:rgba(138,28,28,.05)}
.bc-err{margin-top:6px;color:var(--seal-deep,#5b1010);font-size:14.5px;font-weight:600;line-height:1.4}
.bc-err::before{content:"! ";font-weight:800}
.bc-req{color:var(--seal,#8a1c1c);font-weight:700}
.bc-opt{font-weight:400;color:var(--ink-soft,#553418);font-size:.92em}
.bc-arrow{display:inline-block}
html[dir="rtl"] .bc-arrow{transform:scaleX(-1)}
/* buttons */
.bc-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:48px;padding:10px 20px;border-radius:999px;border:1.5px solid var(--seal,#8a1c1c);background:var(--seal,#8a1c1c);color:#f7ecd2;cursor:pointer;font-size:16px;font-weight:600;line-height:1.2;text-align:center;text-decoration:none;box-shadow:0 -1px 0 var(--seal-deep,#5b1010) inset,0 2px 6px rgba(70,40,15,.14);touch-action:manipulation;transition:transform .12s,background .12s}
.bc-btn:active:not(:disabled){transform:translateY(1px)}
.bc-btn:disabled{opacity:.5;cursor:not-allowed}
.bc-btn--ghost{background:#fffaf0;color:var(--ink,#221408);border-color:rgba(70,40,15,.45);box-shadow:none}
.bc-btn--quiet{background:transparent;color:var(--ink-soft,#553418);border-color:transparent;box-shadow:none}
.bc-btn--link{background:none;border:0;box-shadow:none;color:var(--seal,#8a1c1c);padding:8px 4px;min-height:44px;text-decoration:underline;text-underline-offset:3px;font-weight:600}
.bc-grow{flex:1 1 auto}
.bc-spin{display:inline-block;width:22px;height:22px;border:2.5px solid rgba(138,28,28,.25);border-top-color:var(--seal,#8a1c1c);border-radius:50%;animation:bcSpin .8s linear infinite;vertical-align:middle}
.bc-spin--sm{width:16px;height:16px;border-width:2px;border-color:rgba(247,236,210,.35);border-top-color:#f7ecd2}
@keyframes bcSpin{to{transform:rotate(360deg)}}
/* chips */
.bc-chips{display:flex;flex-wrap:wrap;gap:8px}
.bc-chips--2{display:grid;grid-template-columns:1fr 1fr}
.bc-chips--3{display:grid;grid-template-columns:repeat(3,1fr)}
.bc-chip{position:relative;display:inline-flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;min-height:48px;min-width:44px;padding:8px 12px;border:1.5px solid rgba(70,40,15,.38);border-radius:12px;background:#fffaf0;color:var(--ink,#221408);font-size:15.5px;line-height:1.2;cursor:pointer;text-align:center;touch-action:manipulation;transition:background .12s,border-color .12s,color .12s}
.bc-chip.on{background:var(--ink,#221408);border-color:var(--ink,#221408);color:var(--paper-1,#ece0c4)}
.bc-chip:disabled{opacity:.42;cursor:not-allowed}
.bc-chip:focus-visible, .bc-btn:focus-visible, .bc-step-b:focus-visible, .bc-row-main:focus-visible, .bc-sec-head:focus-visible{outline:3px solid rgba(160,123,47,.85);outline-offset:2px}
.bc-chip-sub{font-size:12.5px;opacity:.8}
.bc-badge{display:inline-block;margin-top:2px;padding:1px 8px;border-radius:999px;font-size:11.5px;font-weight:700;background:rgba(160,123,47,.18);color:#6b4f14;line-height:1.5}
.bc-chip.on .bc-badge{background:rgba(236,224,196,.2);color:#f5dca0}
.bc-badge--full{background:rgba(138,28,28,.12);color:var(--seal-deep,#5b1010)}
.bc-badge--ok{background:rgba(47,107,42,.12);color:#2f5a22}
/* stepper */
.bc-step{display:inline-flex;align-items:center;flex:0 0 auto;border:1.5px solid rgba(70,40,15,.45);border-radius:999px;background:#fffaf0}
.bc-step.has-val{border-color:var(--ink,#221408)}
.bc-step-b{width:44px;height:44px;border:0;border-radius:50%;background:transparent;color:var(--ink,#221408);font-size:24px;line-height:1;cursor:pointer;touch-action:manipulation;display:flex;align-items:center;justify-content:center}
.bc-step-b:disabled{opacity:.28;cursor:default}
.bc-step-b:active:not(:disabled){background:rgba(70,40,15,.08)}
.bc-step-val{min-width:30px;text-align:center;font-size:19px;font-weight:700;font-variant-numeric:tabular-nums}
.bc-step--sm .bc-step-b{width:40px;height:40px;font-size:21px}
/* fields */
.bc-field{margin:14px 0 0}
.bc-label{display:block;font-size:15px;font-weight:600;color:var(--ink-soft,#553418);margin:0 0 6px;line-height:1.35}
.bc-input{display:block;width:100%;min-height:48px;padding:10px 12px;border:1.5px solid rgba(70,40,15,.38);border-radius:10px;background:#fffaf0;color:var(--ink,#221408);font:inherit;font-size:16px;line-height:1.3;-webkit-appearance:none;appearance:none}
.bc-input::placeholder{color:#8a6a44;opacity:.85}
.bc-input:focus{outline:none;border-color:var(--seal,#8a1c1c);box-shadow:0 0 0 3px rgba(138,28,28,.14)}
.bc-input.is-err{border-color:var(--seal,#8a1c1c);background:#fff6f0}
.bc-phone{display:flex;align-items:stretch;width:100%}
.bc-phone-cc{display:flex;align-items:center;flex:0 0 auto;padding:0 10px;border:1.5px solid rgba(70,40,15,.38);border-right:0;border-radius:10px 0 0 10px;background:rgba(210,189,145,.35);font-family:"JetBrains Mono","Vazirmatn",ui-monospace,monospace;font-size:15px;white-space:nowrap;color:var(--ink,#221408)}
.bc-phone .bc-phone-in{border-radius:0 10px 10px 0;font-family:"JetBrains Mono","Vazirmatn",ui-monospace,monospace;letter-spacing:.02em;min-width:0;flex:1 1 auto;text-align:left}
.bc-phone.is-err .bc-phone-cc{border-color:var(--seal,#8a1c1c)}
/* sheet */
.bc-sheet-back{position:fixed;inset:0;z-index:1200;background:rgba(34,20,8,.5);display:flex;align-items:flex-end;justify-content:center;animation:bcFade .18s ease-out}
.bc-sheet{position:relative;width:100%;max-width:560px;max-height:90vh;max-height:90dvh;display:flex;flex-direction:column;background:var(--paper-1,#ece0c4);border-radius:18px 18px 0 0;box-shadow:0 -10px 30px rgba(34,20,8,.3);animation:bcUp .24s cubic-bezier(.16,1,.3,1);outline:none;color:var(--ink,#221408);font-family:"Cormorant Garamond",Georgia,serif;font-size:17px;line-height:1.45}
html[dir="rtl"] .bc-sheet{font-family:"Vazirmatn",Tahoma,sans-serif;font-size:15.5px;line-height:1.7}
.bc-sheet-grip{width:42px;height:4px;border-radius:4px;background:rgba(70,40,15,.25);margin:8px auto 0}
.bc-sheet-head{display:flex;align-items:center;gap:8px;padding:2px 6px 6px 16px;border-bottom:1px solid rgba(70,40,15,.18)}
html[dir="rtl"] .bc-sheet-head{padding:2px 16px 6px 6px}
.bc-sheet-title{flex:1 1 auto;margin:0;font-family:"IM Fell English",Georgia,serif;font-weight:400;font-size:21px;line-height:1.25}
html[dir="rtl"] .bc-sheet-title{font-family:"Vazirmatn",sans-serif;font-weight:700;font-size:18px}
.bc-sheet-x{flex:0 0 44px;width:44px;height:44px;border:0;border-radius:50%;background:transparent;font-size:20px;color:var(--ink-soft,#553418);cursor:pointer}
.bc-sheet-body{flex:1 1 auto;overflow:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding:12px 16px 16px}
.bc-sheet-foot{display:flex;gap:10px;padding:10px 16px calc(10px + env(safe-area-inset-bottom,0px));border-top:1px solid rgba(70,40,15,.18);background:rgba(236,224,196,.98)}
@keyframes bcFade{from{opacity:0}to{opacity:1}}
@keyframes bcUp{from{transform:translateY(40px);opacity:.4}to{transform:none;opacity:1}}
/* toast */
.bc-toast{position:fixed;left:12px;right:12px;bottom:calc(var(--bc-toast-bottom,16px) + env(safe-area-inset-bottom,0px));z-index:1400;display:flex;justify-content:center;pointer-events:none}
.bc-toast-in{pointer-events:auto;display:flex;align-items:center;gap:12px;max-width:520px;padding:8px 8px 8px 16px;border-radius:12px;background:var(--ink,#221408);color:var(--paper-1,#ece0c4);box-shadow:0 8px 24px rgba(34,20,8,.35);font-size:15.5px;animation:bcUp .2s ease-out}
html[dir="rtl"] .bc-toast-in{padding:8px 16px 8px 8px}
.bc-toast-msg{flex:1 1 auto;padding:6px 0}
.bc-toast-act{flex:0 0 auto;min-height:44px;padding:0 14px;border:1px solid rgba(245,220,160,.6);border-radius:999px;background:transparent;color:#f5dca0;font-weight:700;font-size:15px;cursor:pointer}
/* saved plans / find */
.bc-find-line{text-align:center;margin:4px 0 0}
.bc-saved{padding:10px 14px}
.bc-saved-h{font-size:14px;font-weight:700;color:var(--ink-soft,#553418);margin-bottom:4px}
.bc-saved-row{display:flex;align-items:center;justify-content:space-between;gap:12px;min-height:48px;padding:6px 0;border-bottom:1px dotted rgba(70,40,15,.25);color:var(--ink,#221408);text-decoration:none}
.bc-saved-ref{font-weight:700;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.bc-saved-name{font-weight:400;color:var(--ink-soft,#553418)}
.bc-saved-go{flex:0 0 auto;color:var(--seal,#8a1c1c);font-weight:700}
.bc-saved-find{margin-top:2px}
.bc-ok-box{display:flex;gap:12px;align-items:flex-start;padding:12px 14px;border-radius:12px;background:rgba(160,123,47,.1);border:1px solid rgba(160,123,47,.45)}
.bc-ok-box p{margin:0}
.bc-ok-ic{flex:0 0 28px;height:28px;border-radius:50%;background:var(--gold,#a07b2f);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800}
/* area + map (paper.css supplies .area-* / .map-* / .geo-*; these only adapt them) */
.bc-area .area-input{font-size:17px}
@media (max-width:720px){.bc-area .area-input{font-size:16px}}
.bc-area-gh{text-transform:none!important;letter-spacing:.04em;font-size:11.5px}
html[dir="rtl"] .bc-area-gh{font-family:"Vazirmatn",sans-serif;font-size:12.5px}
html[dir="rtl"] .bc-area .area-opt-name{font-family:"Vazirmatn",sans-serif;font-size:16px}
html[dir="rtl"] .bc-area .area-opt-fee{margin-left:0;margin-right:auto}
.bc-area .area-opt-fee{direction:ltr;unicode-bidi:isolate}
/* "رایگان"/"مجاناً" in the mono fee column: let Arabic script fall to Vazirmatn, never to the system monospace
   (Courier New draws it as disconnected letters) */
.bc-area .area-opt-fee{font-family:"JetBrains Mono","Vazirmatn",ui-monospace,monospace}
/* full-screen phone sheet: the close ✕ sits in the 52px gap beside the field (paper.css anchors it to the field) */
@media (max-width:720px){.bc-area.open .area-sheet-close{top:50%;transform:translateY(-50%);inset-inline-end:-50px}}
.bc-map{margin-top:0;isolation:isolate;z-index:0}
/* ^ Leaflet gives its panes/controls z-index 400-1000. Without a stacking context of its own the map painted OVER
   the area list (z-index 60) that drops down above it on desktop/tablet, hiding the results. isolation keeps every
   Leaflet layer inside the map box (phones use the full-screen sheet, z-index 1300, and were never affected). */
.bc-pin{margin-top:16px}
.bc-geo-row{display:flex;align-items:center;flex-wrap:wrap;gap:10px;margin-top:6px}
.bc-geo-btn{min-height:44px}
.bc-pinned{font-weight:700;color:#2f5a22}
.bc-geo-hint{display:block;width:100%;margin-top:8px;padding:10px 12px;border:1px dashed rgba(138,28,28,.5);border-radius:10px;background:rgba(138,28,28,.04);color:var(--ink,#221408);font-size:15px;text-align:start;cursor:pointer;line-height:1.4}
.bc-pin-check{display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px;margin-top:8px;padding:10px 12px;border-radius:10px;background:rgba(160,123,47,.1);font-size:15px}
.bc-pin-check .bc-btn{min-height:44px;padding:8px 14px}
html[dir="rtl"] .bc-app .map-hint, html[dir="rtl"] .bc-app .map-dist, html[dir="rtl"] .bc-app .map-setpin{font-family:"Vazirmatn",sans-serif;letter-spacing:0;text-transform:none}
/* crash */
.bc-crash{text-align:center;margin-top:40px}
.bc-crash-rtl{font-family:"Vazirmatn",sans-serif;color:var(--ink-soft,#553418);margin-bottom:10px}
@media (prefers-reduced-motion:reduce){.bc-sheet,.bc-sheet-back,.bc-toast-in{animation:none}.bc-spin{animation-duration:2s}}
`;
  injectCss('bc-common-css', BC_CSS);

  // ── src/client/bread-builder.babel.js ──
  // ═══════════════════════════════════════════════════════════════════════════
  // Sangak Club — the PLAN BUILDER (/bread, window.__BREAD__.mode 'builder')
  //
  // Compiled by scripts/compile-app.mjs into public/scripts/bread.js:
  //   (function(){ 'use strict'; core.mjs + bread-common.babel.js + THIS FILE })();
  // so core.mjs (priceDrop, patternDays, cycleRange, validateDropItems, …) and bread-common (t, tn, fmtAed,
  // Sheet, Stepper, Chip, AddressForm, api, …) are plain functions in scope. No imports, no exports.
  //
  // The 60-second flow (spec §10): six short sections on ONE scrolling page, every one pre-filled with a good
  // default, and a sticky bottom bar "8 deliveries · AED 342.20 · Continue" that walks the customer to the next
  // section that still needs them, then turns into "Pay AED X".
  //   1 Breads   steppers (default 3 Sangak + 2 Special, or ?breads=…, or the last web cart), extras collapsed
  //   2 How often  Every week · Every 2 weeks · Weekend breakfast · Pick dates (+ first-delivery date)
  //   3 When     the owner's delivery windows, with "few left" badges from /api/bread/availability
  //   4 Review   one row per date; tap → bottom sheet to change that date; issues fixed in one tap
  //   5 Where    Delivery (area + mandatory pin + door details) or Pickup; optional gift
  //   6 You & pay  name, UAE mobile, email, reminders, totals, trust lines, Pay → /api/bread/checkout
  //
  // Prices: computed INSTANTLY in the browser with core priceDrop over __BREAD__.catalog/settings (the bar never
  // waits for the network); POST /api/bread/quote (debounced 600 ms) is the authority — when a fresh server quote
  // matches the current draft its numbers and per-day issues win. Checkout re-quotes on the server anyway.
  //
  // Draft: localStorage 'sf-bread-draft' (24 h) — survives the language-switch reload and in-app webview kills.
  // ═══════════════════════════════════════════════════════════════════════════

  const {
    useState,
    useEffect,
    useRef,
    useCallback
  } = React;
  addI18n({
    'b.kicker': {
      en: 'Sangak Club',
      fa: 'باشگاه سنگک',
      ar: 'نادي السنكك'
    },
    'b.title': {
      en: 'Your bread, on your days',
      fa: 'نانِ تازه، همان روزهایی که می‌خواهید',
      ar: 'خبزك الطازج في الأيام التي تختارها'
    },
    'b.lead': {
      en: 'Choose your breads, days and time once — we bake them fresh and bring them to you, {weeks} at a time, until you stop.',
      fa: 'یک بار نان، روز و ساعت را انتخاب کنید — هر بار برای {weeks} برایتان تازه می‌پزیم و می‌آوریم، تا وقتی خودتان بخواهید.',
      ar: 'اختر الخبز والأيام والوقت مرة واحدة — ونخبزه طازجاً ونوصله إليك في دورات من {weeks} تستمر حتى توقفها بنفسك.'
    },
    'b.v1': {
      en: 'Bread {pct}% off',
      fa: 'نان {pct}٪ ارزان‌تر',
      ar: 'خصم {pct}٪ على الخبز'
    },
    'b.v2': {
      en: 'Cheap delivery, locked for your plan',
      fa: 'ارسالِ ارزان، ثابت تا آخرِ برنامه',
      ar: 'توصيل بسعر مخفّض وثابت طوال خطتك'
    },
    'b.v3': {
      en: 'Skip any day until {time} the evening before',
      fa: 'لغو یا تغییرِ هر روز تا {time}، یک روز قبل',
      ar: 'تخطَّ أي يوم حتى {time} من اليوم السابق'
    },
    'b.v4': {
      en: 'No automatic charges · pause or stop anytime',
      fa: 'بدون برداشتِ خودکار از کارت؛ توقفِ موقت یا لغو، هر وقت خواستید',
      ar: 'لا سحب تلقائي من بطاقتك · أوقف أو ألغِ متى شئت'
    },
    'b.earliest': {
      en: 'Order by {time} for the next morning. Earliest first delivery: {day}.',
      fa: 'برای صبحِ فردا تا {time} سفارش بدهید. زودترین تحویل: {day}.',
      ar: 'اطلب قبل {time} ليصلك صباح الغد. أقرب موعد لأول توصيلة: {day}.'
    },
    'b.s1': {
      en: 'Your breads',
      fa: 'نان‌هایتان',
      ar: 'خبزك'
    },
    'b.s1h': {
      en: 'For each delivery — you can change any single day later.',
      fa: 'برای هر نوبت — بعداً هر روز را جداگانه هم می‌توانید تغییر دهید.',
      ar: 'لكل توصيلة — ويمكنك تعديل أي يوم بمفرده لاحقاً.'
    },
    'b.s2': {
      en: 'How often?',
      fa: 'چند وقت یک بار؟',
      ar: 'كم مرة؟'
    },
    'b.s3': {
      en: 'What time?',
      fa: 'چه ساعتی؟',
      ar: 'في أي وقت؟'
    },
    'b.s4': {
      en: 'Your deliveries',
      fa: 'نوبت‌های تحویل',
      ar: 'توصيلاتك'
    },
    'b.s4h': {
      en: 'Tap a day to change its breads or time.',
      fa: 'برای تغییرِ نان یا ساعتِ هر روز، رویش بزنید.',
      ar: 'اضغط على أي يوم لتغيير خبزه أو وقته.'
    },
    'b.s4p': {
      en: 'Your pickups',
      fa: 'نوبت‌های تحویل حضوری',
      ar: 'مواعيد الاستلام'
    },
    'b.s5': {
      en: 'Where?',
      fa: 'کجا؟',
      ar: 'إلى أين؟'
    },
    'b.s6': {
      en: 'Your details & payment',
      fa: 'مشخصات و پرداخت',
      ar: 'بياناتك والدفع'
    },
    'b.perDrop': {
      en: '{breads} per delivery · {price}',
      fa: '{breads} در هر نوبت، {price}',
      ar: '{breads} في كل توصيلة · {price}'
    },
    'b.perLoaf': {
      en: 'about {price} per bread, delivered',
      fa: 'هر نان تا درِ خانه حدودِ {price}',
      ar: 'نحو {price} للرغيف شاملاً التوصيل'
    },
    'b.perLoafP': {
      en: 'about {price} per bread',
      fa: 'هر نان حدودِ {price}',
      ar: 'نحو {price} للرغيف'
    },
    'b.extrasT': {
      en: 'Make it breakfast',
      fa: 'صبحانه را کامل کنید',
      ar: 'اجعلها فطوراً كاملاً'
    },
    'b.extrasS': {
      en: 'Torshi, doogh, omelette… at menu price — no extra delivery',
      fa: 'ترشی، دوغ، املت… به قیمتِ منو — بدون هزینهٔ ارسالِ اضافه',
      ar: 'طرشي، لبن، أومليت… بسعر القائمة — دون رسوم توصيل إضافية'
    },
    'b.extrasN': {
      en: '{n} added',
      fa: '{n} مورد اضافه شد',
      ar: 'أُضيف {n}'
    },
    'b.optional': {
      en: 'optional',
      fa: 'اختیاری',
      ar: 'اختياري'
    },
    'b.f.weekly': {
      en: 'Every week',
      fa: 'هر هفته',
      ar: 'كل أسبوع'
    },
    'b.f.biweekly': {
      en: 'Every 2 weeks',
      fa: 'هر دو هفته',
      ar: 'كل أسبوعين'
    },
    'b.f.weekend': {
      en: 'Weekend breakfast',
      fa: 'صبحانهٔ آخر هفته',
      ar: 'فطور نهاية الأسبوع'
    },
    'b.f.dates': {
      en: 'Pick dates',
      fa: 'انتخاب تاریخ‌ها',
      ar: 'اختر التواريخ'
    },
    'b.f.datesS': {
      en: 'any days you like',
      fa: 'هر روزی که بخواهید',
      ar: 'أي أيام تريدها'
    },
    'b.f.weeklyS': {
      en: 'same days each week',
      fa: 'روزهای ثابت در هفته',
      ar: 'نفس الأيام كل أسبوع'
    },
    'b.f.biweeklyS': {
      en: 'every other week',
      fa: 'یک هفته در میان',
      ar: 'أسبوع نعم وأسبوع لا'
    },
    'b.days': {
      en: 'Which days?',
      fa: 'کدام روزها؟',
      ar: 'أي أيام؟'
    },
    'b.start': {
      en: 'First delivery',
      fa: 'اولین تحویل',
      ar: 'أول توصيلة'
    },
    'b.startP': {
      en: 'First pickup',
      fa: 'اولین تحویلِ حضوری',
      ar: 'أول استلام'
    },
    'b.cover': {
      en: 'Covers {from} – {to} · {weeks}',
      fa: 'از {from} تا {to} ({weeks})',
      ar: 'من {from} إلى {to} · {weeks}'
    },
    'b.startLater': {
      en: 'Start on another day',
      fa: 'شروع از روزی دیگر',
      ar: 'ابدأ في يوم آخر'
    },
    'b.startT': {
      en: 'Start your plan on…',
      fa: 'برنامه از چه روزی شروع شود؟',
      ar: 'متى تبدأ خطتك؟'
    },
    'b.startH': {
      en: 'Your first {weeks} start on the day you pick.',
      fa: 'دورهٔ اول ({weeks}) از روزی که انتخاب کنید شروع می‌شود.',
      ar: 'تبدأ دورتك الأولى ({weeks}) من اليوم الذي تختاره.'
    },
    'b.calH': {
      en: 'Tap the days you want bread.',
      fa: 'روزهایی را که نان می‌خواهید لمس کنید.',
      ar: 'اضغط على الأيام التي تريد فيها الخبز.'
    },
    'b.closedDay': {
      en: 'closed',
      fa: 'تعطیل',
      ar: 'مغلق'
    },
    'b.moved': {
      en: "It's past {time}, so your plan now starts {day}.",
      fa: 'از {time} گذشته؛ برنامه از {day} شروع می‌شود.',
      ar: 'تجاوزنا {time}، لذا تبدأ خطتك {day}.'
    },
    'b.blackout': {
      en: "We're closed on {days} — no delivery that day.",
      fa: '{days} تعطیل هستیم — آن روز تحویلی نداریم.',
      ar: 'نحن مغلقون يوم {days} — لا توصيل في ذلك اليوم.'
    },
    'b.winH': {
      en: 'Your bread arrives inside the window you pick — baked just before.',
      fa: 'نان در همین بازه به دستتان می‌رسد — درست پیش از آن پخته می‌شود.',
      ar: 'يصلك الخبز خلال الفترة التي تختارها — مخبوزاً قبلها مباشرة.'
    },
    'b.winHP': {
      en: 'Ready on the Club shelf by the end of the window you pick.',
      fa: 'تا پایانِ بازهٔ انتخابی روی قفسهٔ باشگاه آماده است.',
      ar: 'يكون جاهزاً على رفّ النادي بنهاية الفترة التي تختارها.'
    },
    'b.w.early': {
      en: 'Early breakfast',
      fa: 'صبحِ زود',
      ar: 'فطور مبكر'
    },
    'b.w.breakfast': {
      en: 'Breakfast',
      fa: 'صبحانه',
      ar: 'الفطور'
    },
    'b.w.late': {
      en: 'Late breakfast',
      fa: 'صبحانهٔ دیرتر',
      ar: 'فطور متأخر'
    },
    'b.w.lunch': {
      en: 'Lunch',
      fa: 'ناهار',
      ar: 'الغداء'
    },
    'b.w.afternoon': {
      en: 'Afternoon',
      fa: 'عصرانه',
      ar: 'العصر'
    },
    'b.w.dinner': {
      en: 'Dinner',
      fa: 'شام',
      ar: 'العشاء'
    },
    'b.few': {
      en: 'Few left',
      fa: 'جای کمی مانده',
      ar: 'أماكن قليلة'
    },
    'b.fullSome': {
      en: 'Full on some days',
      fa: 'بعضی روزها پر است',
      ar: 'ممتلئ في بعض الأيام'
    },
    'b.wkndNote': {
      en: 'Weekend mornings fill up first.',
      fa: 'صبح‌های آخر هفته زودتر پر می‌شوند.',
      ar: 'صباحات نهاية الأسبوع تمتلئ أولاً.'
    },
    'b.custom': {
      en: 'Changed',
      fa: 'تغییر داده‌شده',
      ar: 'معدَّل'
    },
    'b.addDay': {
      en: 'Add or remove days',
      fa: 'افزودن یا حذفِ روز',
      ar: 'أضف أو احذف أياماً'
    },
    'b.saveDay': {
      en: 'Save this day',
      fa: 'ذخیرهٔ این روز',
      ar: 'احفظ هذا اليوم'
    },
    'b.removeDay': {
      en: 'Remove this day',
      fa: 'حذفِ این روز',
      ar: 'احذف هذا اليوم'
    },
    'b.resetDay': {
      en: 'Back to my usual',
      fa: 'برگشت به حالتِ همیشگی',
      ar: 'العودة إلى المعتاد'
    },
    'b.removed': {
      en: '{day} removed',
      fa: '{day} حذف شد',
      ar: 'تم حذف {day}'
    },
    'b.noDays': {
      en: 'No deliveries yet — pick your days in step 2.',
      fa: 'هنوز روزی انتخاب نشده — روزها را در مرحلهٔ ۲ انتخاب کنید.',
      ar: 'لا توصيلات بعد — اختر أيامك في الخطوة 2.'
    },
    'b.areaFirst': {
      en: 'Delivery is added once you choose your area in step 5.',
      fa: 'هزینهٔ ارسال بعد از انتخابِ منطقه در مرحلهٔ ۵ اضافه می‌شود.',
      ar: 'تُضاف رسوم التوصيل بعد اختيار منطقتك في الخطوة 5.'
    },
    'b.thisDay': {
      en: 'This day',
      fa: 'این روز',
      ar: 'هذا اليوم'
    },
    'b.time': {
      en: 'Time',
      fa: 'ساعت',
      ar: 'الوقت'
    },
    'b.extras': {
      en: 'Extras',
      fa: 'افزودنی‌ها',
      ar: 'الإضافات'
    },
    'b.change': {
      en: 'Change',
      fa: 'تغییر',
      ar: 'تغيير'
    },
    'b.removeA': {
      en: 'Remove {day}',
      fa: 'حذفِ {day}',
      ar: 'احذف {day}'
    },
    'b.i.min_breads': {
      en: '{area} needs at least {breads} per delivery.',
      fa: 'برای {area} دست‌کم {breads} در هر نوبت لازم است.',
      ar: 'التوصيل إلى {area} يتطلب {breads} على الأقل في كل مرة.'
    },
    'b.i.min_breads0': {
      en: 'At least {breads} per delivery.',
      fa: 'دست‌کم {breads} در هر نوبت.',
      ar: '{breads} على الأقل في كل توصيلة.'
    },
    'b.i.min_pickup': {
      en: 'Pickup needs at least {breads} each time.',
      fa: 'برای تحویلِ حضوری دست‌کم {breads} در هر نوبت لازم است.',
      ar: 'الاستلام يتطلب {breads} على الأقل في كل مرة.'
    },
    'b.i.max_breads': {
      en: 'At most {breads} per delivery.',
      fa: 'حداکثر {breads} در هر نوبت.',
      ar: '{breads} كحد أقصى لكل توصيلة.'
    },
    'b.i.too_many_extras': {
      en: 'At most {n} of each extra.',
      fa: 'از هر افزودنی حداکثر {n} عدد.',
      ar: '{n} كحد أقصى من كل إضافة.'
    },
    'b.i.unavailable': {
      en: "{item} isn't available right now.",
      fa: '{item} فعلاً موجود نیست.',
      ar: '{item} غير متوفر حالياً.'
    },
    'b.i.capacity': {
      en: '{time} is fully booked that day.',
      fa: 'بازهٔ {time} آن روز پر است.',
      ar: 'فترة {time} محجوزة بالكامل ذلك اليوم.'
    },
    'b.i.day_full': {
      en: 'The oven is fully booked that day.',
      fa: 'تنورِ آن روز کاملاً رزرو شده.',
      ar: 'الفرن محجوز بالكامل ذلك اليوم.'
    },
    'b.i.too_soon': {
      en: 'Too late for this day — orders close at {time} the evening before.',
      fa: 'برای این روز دیر شده — سفارش تا {time}، یک روز قبل بسته می‌شود.',
      ar: 'فات الأوان لهذا اليوم — تُغلق الطلبات {time} من اليوم السابق.'
    },
    'b.i.blackout': {
      en: "We're closed that day.",
      fa: 'آن روز تعطیل هستیم.',
      ar: 'نحن مغلقون ذلك اليوم.'
    },
    'b.i.too_far': {
      en: 'Too far ahead to plan.',
      fa: 'این روز هنوز خیلی دور است.',
      ar: 'هذا اليوم بعيد جداً للتخطيط.'
    },
    'b.i.outside_cycle': {
      en: 'Outside this plan’s {weeks}.',
      fa: 'خارج از بازهٔ این برنامه است.',
      ar: 'خارج مدة هذه الخطة.'
    },
    'b.i.bad_window': {
      en: 'Pick a delivery time.',
      fa: 'ساعتِ تحویل را انتخاب کنید.',
      ar: 'اختر وقت التوصيل.'
    },
    'b.i.unknown_item': {
      en: 'Something here is no longer on the menu.',
      fa: 'یکی از اقلام دیگر در منو نیست.',
      ar: 'أحد الأصناف لم يعد في القائمة.'
    },
    'b.i.area_required': {
      en: 'Choose your area — it sets the delivery price.',
      fa: 'منطقه را انتخاب کنید — هزینهٔ ارسال به آن بستگی دارد.',
      ar: 'اختر منطقتك — سعر التوصيل يعتمد عليها.'
    },
    'b.i.no_weekdays': {
      en: 'Pick at least one day of the week.',
      fa: 'دست‌کم یک روز از هفته را انتخاب کنید.',
      ar: 'اختر يوماً واحداً على الأقل من الأسبوع.'
    },
    'b.i.min_drops': {
      en: 'A plan needs at least {deliveries}.',
      fa: 'هر برنامه دست‌کم {deliveries} لازم دارد.',
      ar: 'الحد الأدنى للخطة: {deliveries}.'
    },
    'b.i.max_plan_aed': {
      en: 'A plan can be up to {price} — remove a day or a few breads.',
      fa: 'سقفِ هر برنامه {price} است — یک روز یا چند نان کم کنید.',
      ar: 'الحد الأقصى للخطة {price} — احذف يوماً أو بضعة أرغفة.'
    },
    'b.i.fulfillment_unavailable': {
      en: 'That option is paused right now — choose the other one.',
      fa: 'این گزینه فعلاً متوقف است — گزینهٔ دیگر را انتخاب کنید.',
      ar: 'هذا الخيار متوقف حالياً — اختر الخيار الآخر.'
    },
    'b.i.bad_date': {
      en: 'Pick a start date.',
      fa: 'تاریخِ شروع را انتخاب کنید.',
      ar: 'اختر تاريخ البدء.'
    },
    'b.i.generic': {
      en: 'This day needs a change — tap it to fix.',
      fa: 'این روز نیاز به تغییر دارد — برای اصلاح لمسش کنید.',
      ar: 'هذا اليوم يحتاج تعديلاً — اضغط عليه لإصلاحه.'
    },
    'b.i.genericPlan': {
      en: 'Something in this plan needs a change — check the steps above, or message us on WhatsApp.',
      fa: 'چیزی در این برنامه نیاز به تغییر دارد — مراحلِ بالا را بررسی کنید، یا در واتس‌اپ پیام بدهید.',
      ar: 'شيء ما في هذه الخطة يحتاج تعديلاً — راجع الخطوات أعلاه، أو راسلنا على واتساب.'
    },
    'b.fix.min': {
      en: 'Make it {n}',
      fa: 'افزایش به {n}',
      ar: 'اجعلها {n}'
    },
    'b.fix.slot': {
      en: 'Switch to {time}',
      fa: 'تغییر به {time}',
      ar: 'انتقل إلى {time}'
    },
    'b.fix.removeItem': {
      en: 'Remove it',
      fa: 'حذفش کن',
      ar: 'احذفه'
    },
    'b.fix.weekly': {
      en: 'Make it every week',
      fa: 'هر هفته بشود',
      ar: 'اجعلها كل أسبوع'
    },
    'b.fix.area': {
      en: 'Choose area',
      fa: 'انتخابِ منطقه',
      ar: 'اختر المنطقة'
    },
    'b.fix.start': {
      en: 'Start {day}',
      fa: 'شروع از {day}',
      ar: 'ابدأ {day}'
    },
    'b.ful.delivery': {
      en: 'Delivery',
      fa: 'ارسال به درِ خانه',
      ar: 'توصيل إلى الباب'
    },
    'b.ful.deliveryS': {
      en: 'fee by area',
      fa: 'هزینه بسته به منطقه',
      ar: 'الرسوم حسب المنطقة'
    },
    'b.ful.pickup': {
      en: 'Pickup',
      fa: 'تحویلِ حضوری',
      ar: 'استلام بنفسك'
    },
    'b.ful.pickupS': {
      en: 'Jumeirah 1 · free',
      fa: 'جمیرا ۱، رایگان',
      ar: 'جميرا 1 · مجاناً'
    },
    'b.pickupNote': {
      en: 'Collect it from the Club shelf at our Jumeirah 1 restaurant — ready by the end of your window, kept for 2 hours.',
      fa: 'از قفسهٔ باشگاه در رستورانِ ما در جمیرا ۱ بردارید — تا پایانِ بازهٔ انتخابی آماده است و تا ۲ ساعت بعد نگهش می‌داریم.',
      ar: 'استلمه من رفّ النادي في مطعمنا في جميرا 1 — يكون جاهزاً بنهاية الفترة المختارة ونحتفظ به لمدة ساعتين.'
    },
    'b.giftT': {
      en: "It's a gift — deliver to someone else",
      fa: 'هدیه است — به کسِ دیگری برسانید',
      ar: 'إنها هدية — أوصلها لشخص آخر'
    },
    'b.giftS': {
      en: 'You keep the plan link and reminders; they only hear from the rider.',
      fa: 'لینکِ برنامه و یادآوری‌ها برای شماست؛ گیرنده فقط با پیک در تماس است.',
      ar: 'يبقى رابط الخطة والتذكيرات لديك، والمستلم يتواصل معه المندوب فقط.'
    },
    'b.giftName': {
      en: 'Their name',
      fa: 'نامِ گیرنده',
      ar: 'اسم المستلم'
    },
    'b.giftPhone': {
      en: 'Their mobile — the rider calls it',
      fa: 'موبایلِ گیرنده — پیک با این شماره تماس می‌گیرد',
      ar: 'جوال المستلم — يتصل به المندوب'
    },
    'b.giftNote': {
      en: 'A note with the first delivery',
      fa: 'یادداشتی همراهِ اولین تحویل',
      ar: 'رسالة مع أول توصيلة'
    },
    'b.giftErrName': {
      en: 'Add their name so the rider asks for the right person.',
      fa: 'نامِ گیرنده را بنویسید تا پیک سراغِ همان فرد برود.',
      ar: 'أضف اسم المستلم ليسأل المندوب عن الشخص الصحيح.'
    },
    'b.name': {
      en: 'Your name',
      fa: 'نامِ شما',
      ar: 'اسمك'
    },
    'b.nameErr': {
      en: 'Add your name so we know who the bread is for.',
      fa: 'نامتان را بنویسید تا بدانیم نان برای کیست.',
      ar: 'أضف اسمك لنعرف لمن الخبز.'
    },
    'b.linkTo': {
      en: 'Your plan link goes to WhatsApp {phone}',
      fa: 'لینکِ برنامه به واتس‌اپِ {phone} می‌آید',
      ar: 'سيصلك رابط خطتك على واتساب {phone}'
    },
    'b.linkCheck': {
      en: "Check it — it's your way back to the plan.",
      fa: 'دقت کنید — راهِ برگشت به برنامه همین است.',
      ar: 'تأكد منه — فهو طريقك للعودة إلى خطتك.'
    },
    'b.email': {
      en: 'Email',
      fa: 'ایمیل',
      ar: 'البريد الإلكتروني'
    },
    'b.emailS': {
      en: 'a backup copy of your plan link',
      fa: 'نسخهٔ پشتیبانِ لینکِ برنامه',
      ar: 'نسخة احتياطية من رابط خطتك'
    },
    'b.emailErr': {
      en: "That email doesn't look right — fix it or leave it empty.",
      fa: 'ایمیل درست به نظر نمی‌رسد — اصلاحش کنید یا خالی بگذارید.',
      ar: 'البريد الإلكتروني غير صحيح — صحّحه أو اتركه فارغاً.'
    },
    'b.remind': {
      en: 'WhatsApp reminders',
      fa: 'یادآوری در واتس‌اپ',
      ar: 'تذكيرات واتساب'
    },
    'b.r.auto': {
      en: 'Only the key ones',
      fa: 'فقط مهم‌ها',
      ar: 'المهمة فقط'
    },
    'b.r.autoS': {
      en: 'first & last delivery (each one if 2 or fewer a week)',
      fa: 'پیش از اولین و آخرین تحویل (اگر هفته‌ای ۲ نوبت یا کمتر، هر نوبت)',
      ar: 'قبل أول وآخر توصيلة (وكل توصيلة إن كانت مرتين أسبوعياً أو أقل)'
    },
    'b.r.each': {
      en: 'Before every delivery',
      fa: 'پیش از هر تحویل',
      ar: 'قبل كل توصيلة'
    },
    'b.r.eachS': {
      en: 'the evening before',
      fa: 'عصرِ روزِ قبل',
      ar: 'مساء اليوم السابق'
    },
    'b.r.none': {
      en: 'No reminders',
      fa: 'بدون یادآوری',
      ar: 'بلا تذكيرات'
    },
    'b.r.noneS': {
      en: 'only confirmations and pay links',
      fa: 'فقط پیام‌های تأیید و لینکِ پرداخت',
      ar: 'رسائل التأكيد وروابط الدفع فقط'
    },
    'b.optin': {
      en: 'Tell me about new breads and offers on WhatsApp (rarely)',
      fa: 'خبرِ نان‌های تازه و پیشنهادها را در واتس‌اپ به من بدهید (به‌ندرت)',
      ar: 'أخبروني بالخبز الجديد والعروض على واتساب (نادراً)'
    },
    'b.t.goods': {
      en: 'Bread & extras',
      fa: 'نان و افزودنی‌ها',
      ar: 'الخبز والإضافات'
    },
    'b.t.fee': {
      en: 'Delivery · {deliveries}',
      fa: 'ارسال ({deliveries})',
      ar: 'التوصيل · {deliveries}'
    },
    'b.t.vat': {
      en: 'VAT 5%',
      fa: 'مالیات بر ارزش افزوده ۵٪',
      ar: 'ضريبة القيمة المضافة 5٪'
    },
    'b.t.total': {
      en: 'Total for your first {weeks}',
      fa: 'جمعِ کل برای دورهٔ اول ({weeks})',
      ar: 'الإجمالي للدورة الأولى ({weeks})'
    },
    'b.t.save': {
      en: 'You save {price} compared with ordering each time',
      fa: '{price} صرفه‌جویی نسبت به سفارشِ تک‌تک',
      ar: 'توفّر {price} مقارنةً بالطلب في كل مرة'
    },
    'b.t.checking': {
      en: "Checking today's prices…",
      fa: 'در حالِ بررسیِ قیمت‌ها…',
      ar: 'جارٍ التحقق من الأسعار…'
    },
    'b.trust1': {
      en: 'No automatic charges — every {weeks} we send a pay link, and your card is charged only when you tap Pay',
      fa: 'بدون برداشتِ خودکار — هر {weeks} لینکِ پرداخت می‌فرستیم و فقط وقتی «پرداخت» را بزنید از کارتتان برداشت می‌شود',
      ar: 'لا سحب تلقائي من بطاقتك — نرسل لك رابط دفع كل {weeks}، ولا يُخصم شيء إلا عندما تضغط «ادفع»'
    },
    'b.trust2': {
      en: 'Skip or change any day until {time} the evening before',
      fa: 'لغو یا تغییرِ هر روز تا {time}، یک روز قبل',
      ar: 'تخطَّ أو عدّل أي يوم حتى {time} من اليوم السابق'
    },
    'b.trust3': {
      en: 'Pause up to {max} or stop anytime — unused balance goes back to your card',
      fa: 'توقفِ موقت تا {max} یا لغو هر وقت خواستید — موجودیِ استفاده‌نشده به کارتتان برمی‌گردد',
      ar: 'أوقف خطتك مؤقتاً لمدة تصل إلى {max} أو ألغِها متى شئت — يُعاد الرصيد غير المستخدم إلى بطاقتك'
    },
    'b.pay': {
      en: 'Pay {price}',
      fa: 'پرداخت {price}',
      ar: 'ادفع {price}'
    },
    'b.payNote': {
      en: 'Secure card page by Network International · this payment covers your first {weeks}.',
      fa: 'پرداختِ امن با کارت در صفحهٔ نتورک اینترنشنال؛ این پرداخت برای دورهٔ اول ({weeks}) است.',
      ar: 'صفحة دفع آمنة من نتورك إنترناشيونال · هذه الدفعة تغطي دورتك الأولى ({weeks}).'
    },
    'b.keepT': {
      en: 'Every {weeks}, until you stop',
      fa: 'هر {weeks}، تا وقتی خودتان بخواهید',
      ar: 'كل {weeks}، حتى توقفها بنفسك'
    },
    'b.keepNote': {
      en: 'Your plan continues every {weeks} until you stop it. {days} before each new {weeks} we send a pay link on WhatsApp — your card is charged only when you tap Pay. Not paid by {time} the evening before the first delivery? The plan pauses; resume anytime. Pause up to {max} or stop anytime.',
      fa: 'برنامه‌تان هر {weeks} ادامه دارد تا خودتان لغوش کنید. {days} پیش از شروعِ هر دورهٔ تازه، لینکِ پرداخت را در واتس‌اپ می‌فرستیم — فقط وقتی «پرداخت» را بزنید از کارتتان برداشت می‌شود. اگر تا {time}ِ روزِ قبل از اولین تحویل پرداخت نشود، برنامه موقتاً متوقف می‌شود و هر وقت خواستید می‌توانید آن را از سر بگیرید. می‌توانید برنامه را تا {max} موقتاً متوقف کنید یا هر زمان لغوش کنید.',
      ar: 'تستمر خطتك كل {weeks} حتى توقفها بنفسك. قبل {days} من بداية كل دورة جديدة نرسل لك رابط دفع على واتساب — ولا يُخصم شيء من بطاقتك إلا عندما تضغط «ادفع». إن لم تدفع قبل {time} من اليوم السابق لأول توصيلة، تتوقف الخطة مؤقتاً ويمكنك استئنافها متى شئت. ويمكنك إيقافها مؤقتاً لمدة تصل إلى {max} أو إلغاؤها في أي وقت.'
    },
    'b.paying': {
      en: 'Opening the secure card page…',
      fa: 'در حالِ باز کردنِ صفحهٔ امنِ پرداخت…',
      ar: 'جارٍ فتح صفحة الدفع الآمنة…'
    },
    'b.payStuck': {
      en: 'Taking too long?',
      fa: 'طول کشید؟',
      ar: 'يستغرق وقتاً طويلاً؟'
    },
    'b.openPlan': {
      en: 'Open my plan',
      fa: 'باز کردنِ برنامه‌ام',
      ar: 'افتح خطتي'
    },
    'b.e.fix': {
      en: 'A few things need a quick fix — they’re marked in red.',
      fa: 'چند مورد نیاز به اصلاح دارد — با رنگِ قرمز مشخص شده‌اند.',
      ar: 'بعض الأمور تحتاج تعديلاً سريعاً — مُعلَّمة بالأحمر.'
    },
    'b.e.days': {
      en: 'Some days changed while you were choosing — they’re marked in step 4.',
      fa: 'بعضی روزها در این فاصله تغییر کرد — در مرحلهٔ ۴ مشخص شده‌اند.',
      ar: 'تغيّرت بعض الأيام أثناء اختيارك — تجدها مُعلَّمة في الخطوة 4.'
    },
    'b.e.payDown': {
      en: 'Card payments are paused for a moment. Try again in a few minutes — or WhatsApp us.',
      fa: 'پرداخت با کارت لحظاتی در دسترس نیست. چند دقیقهٔ دیگر دوباره امتحان کنید — یا در واتس‌اپ پیام بدهید.',
      ar: 'الدفع بالبطاقة متوقف للحظات. حاول بعد دقائق — أو راسلنا على واتساب.'
    },
    'b.e.payCheck': {
      en: 'We’re still checking your last payment. If you just paid, open your plan to see it — otherwise try again in a minute.',
      fa: 'هنوز در حالِ بررسیِ پرداختِ قبلیِ شما هستیم. اگر همین الان پرداخت کردید، برنامه‌تان را باز کنید — وگرنه یک دقیقهٔ دیگر دوباره امتحان کنید.',
      ar: 'ما زلنا نتحقق من دفعتك الأخيرة. إن كنت قد دفعت للتو، افتح خطتك لتتأكد — وإلا فحاول بعد دقيقة.'
    },
    'b.e.cid': {
      en: 'Your earlier checkout here used a different mobile number. Start a fresh checkout to continue with this one.',
      fa: 'پرداختِ قبلیِ شما در این صفحه با شمارهٔ موبایلِ دیگری بود. برای ادامه با این شماره، یک پرداختِ تازه شروع کنید.',
      ar: 'استخدمت عملية الدفع السابقة هنا رقم جوال مختلفاً. ابدأ عملية دفع جديدة للمتابعة بهذا الرقم.'
    },
    'b.e.cidBtn': {
      en: 'Start a fresh checkout',
      fa: 'شروعِ پرداختِ تازه',
      ar: 'ابدأ عملية دفع جديدة'
    },
    'b.full.t': {
      en: 'The Club is full right now',
      fa: 'ظرفیتِ باشگاه فعلاً پر است',
      ar: 'النادي مكتمل حالياً'
    },
    'b.full.s': {
      en: "We keep it small so every loaf comes fresh from the oven. Message us on WhatsApp and we'll hold the next spot for you.",
      fa: 'باشگاه را کوچک نگه می‌داریم تا هر نان تازه از تنور برسد. در واتس‌اپ پیام بدهید تا جای بعدی را برایتان نگه داریم.',
      ar: 'نُبقي النادي صغيراً ليصلك كل رغيف طازجاً من الفرن. راسلنا على واتساب وسنحجز لك المكان التالي.'
    },
    'b.off.t': {
      en: 'Sangak Club opens soon',
      fa: 'باشگاه سنگک به‌زودی باز می‌شود',
      ar: 'نادي السنكك يفتح قريباً'
    },
    'b.off.s': {
      en: 'Want to be among the first? Message us on WhatsApp.',
      fa: 'می‌خواهید جزوِ اولین‌ها باشید؟ در واتس‌اپ پیام بدهید.',
      ar: 'تريد أن تكون من الأوائل؟ راسلنا على واتساب.'
    },
    'b.down.t': {
      en: 'Sangak Club is resting for a moment',
      fa: 'باشگاه سنگک لحظاتی در دسترس نیست',
      ar: 'نادي السنكك متوقف للحظات'
    },
    'b.down.s': {
      en: 'Please try again a little later — or order bread from the menu.',
      fa: 'کمی بعد دوباره سر بزنید — یا نان را از منو سفارش دهید.',
      ar: 'حاول مجدداً بعد قليل — أو اطلب الخبز من القائمة.'
    },
    'b.menu': {
      en: 'Go to the menu',
      fa: 'رفتن به منو',
      ar: 'إلى القائمة'
    },
    'b.waJoin': {
      en: "Hi! I'd like to join Sangak Club.",
      fa: 'سلام! می‌خواهم عضوِ باشگاه سنگک بشوم.',
      ar: 'مرحباً! أود الانضمام إلى نادي السنكك.'
    },
    'b.started': {
      en: "You started plan {ref} on this phone. Didn't finish paying? Just tap Pay again — it updates the same plan.",
      fa: 'برنامهٔ {ref} را روی همین گوشی شروع کرده‌اید. پرداخت تمام نشد؟ دوباره «پرداخت» را بزنید — همان برنامه به‌روز می‌شود.',
      ar: 'بدأت الخطة {ref} على هذا الهاتف. لم تُكمل الدفع؟ اضغط «ادفع» مجدداً — ستُحدَّث الخطة نفسها.'
    },
    'b.newPlan': {
      en: 'Start a separate new plan',
      fa: 'شروعِ یک برنامهٔ جدیدِ جداگانه',
      ar: 'ابدأ خطة جديدة منفصلة'
    },
    'b.continue': {
      en: 'Continue',
      fa: 'ادامه',
      ar: 'متابعة'
    },
    'b.plusDel': {
      en: '+ delivery',
      fa: '+ ارسال',
      ar: '+ التوصيل'
    },
    'b.barSum': {
      en: '{deliveries} · {breads}',
      fa: '{deliveries}، {breads}',
      ar: '{deliveries} · {breads}'
    }
  });

  // ── Constants ────────────────────────────────────────────────────────────────
  const BB_S = bcSettings();
  const BB_CAT = bcCatalog();
  const BB_BREADS = BB_S.breads.filter(s => BB_CAT[s] && BB_CAT[s].kind !== 'extra');
  const BB_EXTRAS = BB_S.extras.filter(s => BB_CAT[s]);
  const BB_ORDER = BB_BREADS.concat(BB_EXTRAS);
  const BB_DRAFT_KEY = 'sf-bread-draft';
  const BB_DRAFT_TTL = 24 * 3600 * 1000;
  const BB_MARGIN = BB_S.checkout_margin_min;
  const BB_WEEKS = Math.max(1, Math.round(BB_S.cycle_days / 7));
  const BB_HAS_DEL = BB_S.fulfillments.indexOf('delivery') >= 0;
  const BB_HAS_PICK = BB_S.fulfillments.indexOf('pickup') >= 0;
  // FA: a middle dot next to Persian digits reads as a Persian zero (۴ · ۲۴ looks like ۴۲۴۰) — use the Persian comma.
  const BB_SEP = BC_LANG === 'fa' ? '، ' : ' · ';
  const BB_ITEM_REASONS = ['min_breads', 'max_breads', 'too_many_extras', 'unavailable', 'unknown_item'];
  const BB_DROP_REMOVE = ['day_full', 'too_soon', 'blackout', 'too_far', 'outside_cycle', 'bad_date'];
  const BB_EMAIL_RE = /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]{2,}$/; // = the server's EMAIL_RE (plans.ts)
  // Checkout refusals that are not plan problems: the contact/address field they belong to (→ its section), and
  // the checkout session itself (never shown as a plan issue — see pay()).
  const BB_FIELD_REASONS = {
    name: 6,
    phone: 6,
    email: 6,
    area_required: 5,
    area_mismatch: 5,
    address_line: 5,
    pin_required: 5,
    gift_name: 5,
    gift_phone: 5
  };
  const BB_SESSION_REASONS = ['checkout_id', 'checkout_expired', 'retry', 'bad_draft'];
  const BB_NOT_DUBAI_TZ = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone !== 'Asia/Dubai';
    } catch (e) {
      return false;
    }
  })();
  const BB_REDUCED_MOTION = (() => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  })();

  /** Weekday order of a week row: Saturday-first for FA, Monday-first for EN/AR. */
  function bbWeekOrder(L) {
    return L === 'fa' ? [6, 0, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5, 6, 0];
  }
  /** "7:30 PM" — the last moment to order for the next morning (cutoff minus the checkout margin). */
  function bbOrderByText() {
    return bcClock(minToHHMM(BB_S.cutoff_hour * 60 - BB_MARGIN));
  }
  /** The longest customer pause as words: "3 weeks" when whole weeks, else "20 days" (settings.max_pause_days). */
  function bbMaxPause() {
    const n = BB_S.max_pause_days;
    return n % 7 === 0 ? t('n.weeks', {
      n: n / 7
    }) : t('n.days', {
      n
    });
  }
  /** Open a saved plan without putting its token in the DOM (the SavedPlansCard handoff via sessionStorage). */
  function bbOpenPlan(e, ref) {
    const p = loadPlansLocal().find(x => x.ref === ref);
    if (!p) return; // the plan page falls back to the only saved plan, or its picker
    try {
      sessionStorage.setItem('sf-bread-token', p.token);
    } catch (err) {
      e.preventDefault();
      window.location.assign(planHref(p.token));
    }
  }
  /** Address refusals from a checkout → AddressForm error keys. */
  function bbSrvAddrErrs(f) {
    const e = {};
    if (f.area_required || f.area_mismatch) e.area = 'ad.err.area';
    if (f.pin_required) e.pin = 'ad.err.pin';
    if (f.address_line) e.line = 'ad.err.line';
    return e;
  }
  /** First plannable day: the later of the page's server value and the live device-clock value. */
  function bbEarliest(nowMs) {
    const local = earliestPlannableYmd(nowMs, BB_S, BB_MARGIN);
    const srv = BC && isValidYmd(BC.earliest) ? BC.earliest : null;
    return srv && srv > local ? srv : local;
  }
  /** Default weekly day: the first Friday or Saturday on/after the earliest day. */
  function bbDefaultWeekday(earliest) {
    for (let i = 0; i < 7; i++) {
      const w = ymdWeekday(ymdAdd(earliest, i));
      if (w === 5 || w === 6) return w;
    }
    return 5;
  }
  function bbDefaultSlot() {
    const w = BB_S.windows;
    return w.indexOf('09:00') >= 0 ? '09:00' : w[1] || w[0] || '09:00';
  }
  /** {slug: qty} → [{slug, qty}] in catalog order, qty > 0 only. */
  function bbItemsArr(map) {
    const m = map || {};
    const out = [];
    BB_ORDER.forEach(slug => {
      const q = Math.floor(Number(m[slug]) || 0);
      if (q > 0) out.push({
        slug,
        qty: q
      });
    });
    return out;
  }
  function bbBreadCount(map) {
    let n = 0;
    BB_BREADS.forEach(s => {
      n += Math.max(0, Math.floor(Number((map || {})[s]) || 0));
    });
    return n;
  }
  function bbExtraCount(map) {
    let n = 0;
    BB_EXTRAS.forEach(s => {
      if (Number((map || {})[s]) > 0) n++;
    });
    return n;
  }
  function bbSameItems(a, b) {
    return JSON.stringify(bbItemsArr(a)) === JSON.stringify(bbItemsArr(b));
  }
  function bbWeekdays(d) {
    return d.freq === 'weekend' ? [5, 6] : Array.isArray(d.weekdays) ? d.weekdays : [];
  }
  function bbWinKey(slot) {
    const m = hhmmToMin(slot);
    if (m <= 8 * 60) return 'b.w.early';
    if (m <= 9 * 60 + 30) return 'b.w.breakfast';
    if (m <= 11 * 60 + 30) return 'b.w.late';
    if (m <= 14 * 60 + 30) return 'b.w.lunch';
    if (m <= 18 * 60) return 'b.w.afternoon';
    return 'b.w.dinner';
  }
  function bbMonthShort(ymd, L) {
    try {
      return new Intl.DateTimeFormat(bcLocale(L || BC_LANG), {
        timeZone: 'UTC',
        month: 'short'
      }).format(new Date(ymd + 'T12:00:00Z'));
    } catch (e) {
      return ymd.slice(5, 7);
    }
  }
  /** Scroll a section (or element) into view. Some in-app webviews (and background tabs) never run the smooth
   *  animation — if the page has not moved at all after 700 ms, jump instead. */
  function bbScrollEl(el, block) {
    if (!el) return;
    const b = block || 'start';
    try {
      el.scrollIntoView({
        behavior: BB_REDUCED_MOTION ? 'auto' : 'smooth',
        block: b
      });
    } catch (e) {
      return;
    }
    if (BB_REDUCED_MOTION) return;
    // Arrived = the target sits where we asked (or the page cannot scroll further). Stalled = the page has stopped
    // moving (a customer who is scrolling by hand keeps it moving, so we never fight them).
    const arrived = () => {
      const r = el.getBoundingClientRect(),
        H = window.innerHeight;
      if (b === 'center') {
        const c = (r.top + r.bottom) / 2;
        return c > H * 0.12 && c < H * 0.78;
      }
      const atEnd = H + window.scrollY >= document.documentElement.scrollHeight - 2;
      return r.top > -4 && r.top < 60 || atEnd && r.top >= -4;
    };
    setTimeout(() => {
      const y1 = window.scrollY;
      setTimeout(() => {
        try {
          if (window.scrollY === y1 && !arrived()) el.scrollIntoView({
            block: b
          });
        } catch (e) {}
      }, 150);
    }, 850);
  }
  function bbScrollTo(id) {
    try {
      bbScrollEl(document.getElementById(id));
    } catch (e) {}
  }
  /** After errors are revealed: bring the first error message of a section into the middle of the screen. */
  function bbScrollToFirstError(secId) {
    setTimeout(() => {
      try {
        const sec = document.getElementById(secId);
        const err = sec && sec.querySelector('.bc-err, .bc-issue');
        if (err) bbScrollEl(err.closest('.bc-field, .bc-pin, .bc-row, .bc-issue') || err, 'center');else bbScrollEl(sec);
      } catch (e) {}
    }, 60);
  }
  function bbUrlParams() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return new URLSearchParams('');
    }
  }

  // ── Draft: defaults, prefill, persistence ───────────────────────────────────
  function bbEmptyAddr() {
    return {
      area_key: '',
      line: '',
      unit: '',
      handover: 'door',
      ring_bell: false,
      gate_note: '',
      notes: '',
      lat: null,
      lng: null,
      pin_ok: true
    };
  }
  function bbDefaultItems() {
    const m = {};
    BB_ORDER.forEach(s => {
      m[s] = 0;
    });
    if (BB_BREADS[0]) m[BB_BREADS[0]] = 3;
    if (BB_BREADS[1]) m[BB_BREADS[1]] = 2;
    return m;
  }
  /** "?breads=sangak:4,sangak-x:2" → items map, or null. */
  function bbItemsFromParam(v) {
    if (!v) return null;
    const m = bbDefaultItems();
    BB_ORDER.forEach(s => {
      m[s] = 0;
    });
    let any = false;
    String(v).split(',').forEach(pair => {
      const p = pair.split(':');
      let slug = (p[0] || '').trim();
      if (slug === 'bread-add') slug = 'sangak';
      const q = Math.floor(Number(p[1]));
      if (BB_CAT[slug] && Number.isFinite(q) && q > 0) {
        m[slug] = Math.min(BB_CAT[slug].kind === 'extra' ? BB_S.max_extras_qty : BB_S.max_breads_drop, (m[slug] || 0) + q);
        any = true;
      }
    });
    return any && bbBreadCount(m) > 0 ? m : null;
  }
  /** The breads of the last web cart (sf-last-cart, written by app.babel.js after a placed order). */
  function bbItemsFromLastCart() {
    try {
      const j = JSON.parse(localStorage.getItem('sf-last-cart') || 'null');
      if (!j || !Array.isArray(j.lines) || !j.at || Date.now() - j.at > 180 * 86400000) return null;
      const m = bbDefaultItems();
      BB_ORDER.forEach(s => {
        m[s] = 0;
      });
      j.lines.forEach(l => {
        if (!l || typeof l.id !== 'string') return;
        const slug = l.id === 'bread-add' ? 'sangak' : l.id;
        const q = Math.floor(Number(l.qty) || 0);
        if (q > 0 && BB_CAT[slug] && BB_CAT[slug].kind !== 'extra') m[slug] = Math.min(BB_S.max_breads_drop, (m[slug] || 0) + q);
      });
      return bbBreadCount(m) > 0 ? m : null;
    } catch (e) {
      return null;
    }
  }
  /** Name, phone and the newest saved address of this device (written by the main checkout). Never sent anywhere else. */
  function bbSavedCustomer() {
    const out = {
      name: '',
      phone: '',
      addr: null
    };
    try {
      const c = JSON.parse(localStorage.getItem('shaunfood-customer') || '{}') || {};
      if (typeof c.name === 'string') out.name = c.name.slice(0, 60);
      const e = canonUaeMobile(c.phone || localStorage.getItem('shaunfood-customer-phone') || '');
      if (e) out.phone = e.slice(4, 6) + ' ' + e.slice(6, 9) + ' ' + e.slice(9); // '50 123 4567'
    } catch (e) {}
    try {
      const list = JSON.parse(localStorage.getItem('sf-addr-v1') || '[]');
      const x = Array.isArray(list) ? list.find(a => a && typeof a.area === 'string' && bcFindArea(a.area)) : null;
      if (x) {
        const p = x.addressParts || {};
        const main = p.type === 'villa' ? [p.villa && 'Villa ' + p.villa, p.street].filter(Boolean).join(', ') : p.type === 'office' ? [p.company, p.floor && 'Floor ' + p.floor].filter(Boolean).join(', ') : [p.building].filter(Boolean).join(', ');
        const ok = x.coords && Number.isFinite(Number(x.coords.lat)) && Number.isFinite(Number(x.coords.lng));
        out.addr = Object.assign(bbEmptyAddr(), {
          area_key: x.area,
          line: cleanLine(main, 160),
          unit: p.type === 'apt' ? cleanLine([p.flat && 'Flat ' + p.flat, p.floor && 'Floor ' + p.floor].filter(Boolean).join(', '), 40) : '',
          handover: x.dropoff === 'door' || x.dropoff === 'nobell' ? 'door' : 'hand',
          ring_bell: x.dropoff === 'hand' || x.dropoff === 'call',
          lat: ok ? Number(x.coords.lat) : null,
          lng: ok ? Number(x.coords.lng) : null,
          pin_ok: !ok // a saved pin needs one confirming tap ("is it still right?")
        });
      } else {
        const k = localStorage.getItem('shaunfood-delivery-area');
        if (k && bcFindArea(k)) out.addr = Object.assign(bbEmptyAddr(), {
          area_key: k
        });
      }
    } catch (e) {}
    return out;
  }
  function bbFreshDraft(earliest) {
    const cust = bbSavedCustomer();
    return {
      v: 1,
      at: Date.now(),
      cid: uuid(),
      items: bbItemsFromLastCart() || bbDefaultItems(),
      freq: 'weekly',
      weekdays: [bbDefaultWeekday(earliest)],
      dates: [],
      extra: [],
      removed: [],
      start: null,
      slot: bbDefaultSlot(),
      over: {},
      ful: BB_HAS_DEL ? 'delivery' : 'pickup',
      addr: cust.addr || bbEmptyAddr(),
      gift: {
        on: false,
        name: '',
        phone: '',
        note: ''
      },
      name: cust.name,
      phone: cust.phone,
      email: '',
      remind: 'auto',
      optin: false
    };
  }
  function bbLoadDraft() {
    try {
      const j = JSON.parse(localStorage.getItem(BB_DRAFT_KEY) || 'null');
      if (!j || j.v !== 1 || !j.at || Date.now() - j.at > BB_DRAFT_TTL) return null;
      return j;
    } catch (e) {
      return null;
    }
  }
  /** Sanitise a stored/merged draft against today's catalog + settings (the owner may have changed both). */
  function bbSanitize(d0, earliest) {
    const base = bbFreshDraft(earliest);
    const d = Object.assign({}, base, d0 || {});
    const items = {};
    BB_ORDER.forEach(s => {
      const q = Math.floor(Number((d.items || {})[s]) || 0);
      items[s] = Math.max(0, q);
    });
    if (BB_BREADS.length) d.items = items; // empty catalog ("back soon"): leave the stored choices untouched
    if (['weekly', 'biweekly', 'weekend', 'dates'].indexOf(d.freq) < 0) d.freq = 'weekly';
    d.weekdays = Array.isArray(d.weekdays) ? d.weekdays.map(Number).filter(x => x >= 0 && x <= 6).filter((x, i, a) => a.indexOf(x) === i) : base.weekdays;
    ['dates', 'extra', 'removed'].forEach(k => {
      d[k] = Array.isArray(d[k]) ? d[k].filter(isValidYmd).filter((x, i, a) => a.indexOf(x) === i) : [];
    });
    if (d.start && !isValidYmd(d.start)) d.start = null;
    if (!isValidWindow(d.slot, BB_S)) d.slot = bbDefaultSlot();
    const over = {};
    Object.keys(d.over || {}).forEach(day => {
      const o = d.over[day];
      if (!isValidYmd(day) || !o || typeof o !== 'object') return;
      const e = {};
      if (o.items && typeof o.items === 'object') {
        const m = {};
        BB_ORDER.forEach(s => {
          m[s] = Math.max(0, Math.floor(Number(o.items[s]) || 0));
        });
        e.items = m;
      }
      if (o.slot && isValidWindow(o.slot, BB_S)) e.slot = o.slot;
      if (e.items || e.slot) over[day] = e;
    });
    d.over = over;
    if (BB_S.fulfillments.indexOf(d.ful) < 0) d.ful = BB_S.fulfillments[0] || 'delivery';
    d.addr = Object.assign(bbEmptyAddr(), d.addr || {});
    if (d.addr.area_key && !bcFindArea(d.addr.area_key)) d.addr.area_key = '';
    d.gift = Object.assign({
      on: false,
      name: '',
      phone: '',
      note: ''
    }, d.gift || {});
    if (['auto', 'each', 'none'].indexOf(d.remind) < 0) d.remind = 'auto';
    ['name', 'phone', 'email'].forEach(k => {
      if (typeof d[k] !== 'string') d[k] = '';
    });
    if (typeof d.cid !== 'string' || d.cid.length < 8) d.cid = uuid();
    if (typeof d.ref !== 'string' || !/^SC-[A-Z0-9]{3,12}$/.test(d.ref)) delete d.ref;
    return d;
  }
  /** Stored draft (24 h) → else defaults; then explicit link parameters win (ads, the /success upsell, QR cards). */
  function bbInitDraft(earliest) {
    const q = bbUrlParams();
    let d = bbLoadDraft() || bbFreshDraft(earliest);
    const fromParam = bbItemsFromParam(q.get('breads'));
    if (fromParam) {
      d = Object.assign({}, d, {
        items: fromParam,
        over: {}
      });
    }
    const f = q.get('freq');
    if (f && ['weekly', 'biweekly', 'weekend', 'dates'].indexOf(f) >= 0) d = Object.assign({}, d, {
      freq: f,
      removed: [],
      extra: []
    });
    const days = q.get('days');
    if (days) {
      const wd = days.split(',').map(Number).filter(x => Number.isInteger(x) && x >= 0 && x <= 6);
      if (wd.length) d = Object.assign({}, d, {
        weekdays: wd,
        freq: d.freq === 'dates' || d.freq === 'weekend' ? 'weekly' : d.freq
      });
    }
    if (q.get('preset') === 'try2') d = Object.assign({}, d, {
      freq: 'biweekly',
      weekdays: [5],
      removed: [],
      extra: []
    });
    const slot = q.get('slot');
    if (slot && isValidWindow(slot, BB_S)) d = Object.assign({}, d, {
      slot
    });
    const ful = q.get('ful');
    if (ful === 'pickup' || ful === 'delivery') d = Object.assign({}, d, {
      ful
    });
    const area = q.get('area');
    if (area && bcFindArea(area) && !(d.addr && d.addr.area_key)) d = Object.assign({}, d, {
      addr: Object.assign(bbEmptyAddr(), d.addr || {}, {
        area_key: area
      })
    });
    return bbSanitize(d, earliest);
  }

  // ── The plan, derived from the draft (pure) ─────────────────────────────────
  /**
   * Everything the screen needs, recomputed on every render (≤ 28 drops — cheap):
   *   days, drops (local price + local issues), totals, plan-level issues, and the §5 API draft + its key.
   */
  function bbCompute(d, nowMs, cap) {
    const s = BB_S;
    const earliest = bbEarliest(nowMs);
    const moved = !!(d.start && d.start < earliest);
    const weekly = d.freq !== 'dates';
    let start = d.start && d.start >= earliest ? d.start : earliest;
    if ((!d.start || moved) && weekly) {
      // Automatic start = the first delivery day, so the 4 weeks cover real deliveries ("Covers Fri 23 Oct – Thu 19 Nov").
      const wds = bbWeekdays(d);
      for (let i = 0; i < 7 && wds.length; i++) {
        const y = ymdAdd(earliest, i);
        if (wds.indexOf(ymdWeekday(y)) >= 0 && !isBlackout(y, s)) {
          start = y;
          break;
        }
      }
    }
    const cyc = cycleRange(start, s);
    const area = d.ful === 'delivery' ? bcFindArea(d.addr && d.addr.area_key) : null;
    const band = area ? area.band : 0;
    const bandForMin = d.ful === 'pickup' ? 0 : band || BANDS[0];
    const feeUnknown = d.ful === 'delivery' && !area;
    const patDays = weekly ? patternDays(cyc.start, cyc.end, {
      weekdays: bbWeekdays(d),
      every_weeks: d.freq === 'biweekly' ? 2 : 1,
      anchor: cyc.start
    }, null, s, 0) : [];
    let days;
    if (weekly) {
      days = patDays.filter(x => d.removed.indexOf(x) < 0);
      d.extra.forEach(x => {
        if (x >= cyc.start && x <= cyc.end && days.indexOf(x) < 0) days.push(x);
      });
    } else {
      days = d.dates.filter(x => x >= cyc.start && x <= cyc.end);
    }
    const blackout = days.filter(x => isBlackout(x, s));
    days = days.filter(x => !isBlackout(x, s)).sort();
    const drops = days.map(day => {
      const o = d.over[day] || null;
      const itemsMap = o && o.items ? o.items : d.items;
      const items = bbItemsArr(itemsMap);
      const slot = o && o.slot || d.slot;
      const p = priceDrop({
        items,
        fulfillment: d.ful,
        band: bandForMin || 15,
        s,
        catalog: BB_CAT,
        feeAed: feeUnknown ? 0 : undefined
      });
      const issues = [];
      const custom = !!(o && o.items);
      if (custom) bbItemIssues(validateDropItems(items, d.ful, bandForMin || 15, s, BB_CAT), d.ful, bandForMin).forEach(x => issues.push(x));
      const pd = plannableDay(day, nowMs, s, BB_MARGIN);
      if (!pd.ok) issues.push({
        reason: pd.reason
      });
      const c = cap && cap[day] && cap[day][slot];
      if (c && (Number(c.left_drops) < 1 || Number(c.left_breads) < p.breads)) {
        const alt = bbNearestSlotWithRoom(day, slot, p.breads, cap);
        issues.push({
          reason: 'capacity',
          suggestion: alt ? {
            slot: alt
          } : undefined,
          local: true
        });
      }
      return {
        day,
        slot,
        items,
        itemsMap,
        custom,
        slotCustom: !!(o && o.slot),
        p,
        issues
      };
    });
    const tot = sumDrops(drops.map(x => x.p));
    const baseItems = bbItemsArr(d.items);
    const baseIssues = bbItemIssues(validateDropItems(baseItems, d.ful, bandForMin || 15, s, BB_CAT), d.ful, bandForMin);
    const planIssues = [];
    if (weekly && !bbWeekdays(d).length) planIssues.push({
      reason: 'no_weekdays'
    });else if (!days.length) planIssues.push({
      reason: 'no_drops'
    });else if (days.length < s.min_drops_new_plan) planIssues.push({
      reason: 'min_drops',
      suggestion: {
        min: s.min_drops_new_plan
      }
    });
    if (tot.total_fils > s.max_plan_aed * 100) planIssues.push({
      reason: 'max_plan_aed',
      suggestion: {
        max: s.max_plan_aed
      }
    });

    // §5 Draft for /api/bread/quote and /api/bread/checkout.
    const api = {
      fulfillment: d.ful,
      start,
      pattern: {
        mode: weekly ? 'weekly' : 'dates',
        weekdays: weekly ? bbWeekdays(d).slice().sort() : [],
        every_weeks: d.freq === 'biweekly' ? 2 : 1,
        items: baseItems,
        slot: d.slot
      },
      dates: []
    };
    if (d.ful === 'delivery' && area) api.area_key = area.key;
    const entry = day => {
      const o = d.over[day];
      const e = {
        day
      };
      if (o && o.items) e.items = bbItemsArr(o.items);
      if (o && o.slot) e.slot = o.slot;
      return e;
    };
    if (weekly) {
      patDays.forEach(x => {
        if (d.removed.indexOf(x) >= 0 && !isBlackout(x, s)) api.dates.push({
          day: x,
          skip: true
        });
      });
      days.forEach(x => {
        if (d.over[x] || patDays.indexOf(x) < 0) api.dates.push(entry(x));
      });
    } else {
      api.dates = days.map(entry);
    }
    return {
      earliest,
      moved,
      start,
      cyc,
      area,
      band,
      feeUnknown,
      weekly,
      patDays,
      days,
      blackout,
      drops,
      tot,
      baseItems,
      baseIssues,
      planIssues,
      api,
      key: JSON.stringify(api)
    };
  }
  /** core validateDropItems codes → issue objects ({reason, suggestion}). */
  function bbItemIssues(codes, ful, band) {
    const out = [];
    (codes || []).forEach(c => {
      if (c === 'min_breads') out.push({
        reason: 'min_breads',
        suggestion: {
          min: minBreads(ful, band || 15, BB_S)
        }
      });else if (c === 'max_breads') out.push({
        reason: 'max_breads',
        suggestion: {
          max: BB_S.max_breads_drop
        }
      });else if (c === 'too_many_extras') out.push({
        reason: 'too_many_extras',
        suggestion: {
          max: BB_S.max_extras_qty
        }
      });else if (String(c).indexOf('unavailable:') === 0) out.push({
        reason: 'unavailable',
        suggestion: {
          slug: String(c).slice(12)
        }
      });else out.push({
        reason: String(c)
      });
    });
    return out;
  }
  /** Nearest window (ties → the later one) with room for `breads` on `day`, from the availability map. */
  function bbNearestSlotWithRoom(day, slot, breads, cap) {
    const m = hhmmToMin(slot);
    const cands = BB_S.windows.filter(w => w !== slot).sort((a, b) => Math.abs(hhmmToMin(a) - m) - Math.abs(hhmmToMin(b) - m) || hhmmToMin(b) - hhmmToMin(a));
    for (const w of cands) {
      const c = cap && cap[day] && cap[day][w];
      if (!c || Number(c.left_drops) >= 1 && Number(c.left_breads) >= breads) return w;
    }
    return null;
  }
  function bbDedupe(list) {
    const seen = {};
    return list.filter(x => {
      const k = x.reason;
      if (seen[k]) return false;
      seen[k] = 1;
      return true;
    });
  }

  // ── Issue line (text + one-tap fix) ─────────────────────────────────────────
  function bbIssueText(is, ctx) {
    const sg = is.suggestion || {};
    const breads = n => t('n.breads', {
      n
    });
    switch (is.reason) {
      case 'min_breads':
        if (ctx.ful === 'pickup') return t('b.i.min_pickup', {
          breads: breads(sg.min || BB_S.min_breads_pickup)
        });
        if (ctx.area) return t('b.i.min_breads', {
          area: bcAreaName(BC_LANG, ctx.area),
          breads: breads(sg.min || 3)
        });
        return t('b.i.min_breads0', {
          breads: breads(sg.min || 3)
        });
      case 'max_breads':
        return t('b.i.max_breads', {
          breads: breads(sg.max || BB_S.max_breads_drop)
        });
      case 'too_many_extras':
        return t('b.i.too_many_extras', {
          n: sg.max || BB_S.max_extras_qty
        });
      case 'unavailable':
        return t('b.i.unavailable', {
          item: itemName(sg.slug || '', {
            long: true
          })
        });
      case 'capacity':
        return t('b.i.capacity', {
          time: fmtWindowText(ctx.slot || BB_S.windows[0])
        });
      case 'day_full':
        return t('b.i.day_full');
      case 'too_soon':
        return t('b.i.too_soon', {
          time: bbOrderByText()
        });
      case 'blackout':
        return t('b.i.blackout');
      case 'too_far':
        return t('b.i.too_far');
      case 'outside_cycle':
        return t('b.i.outside_cycle', {
          weeks: t('n.weeks', {
            n: BB_WEEKS
          })
        });
      case 'bad_window':
        return t('b.i.bad_window');
      case 'unknown_item':
        return t('b.i.unknown_item');
      case 'area_required':
        return t('b.i.area_required');
      case 'no_weekdays':
        return t('b.i.no_weekdays');
      case 'no_drops':
        return t('b.noDays');
      case 'min_drops':
        return t('b.i.min_drops', {
          deliveries: t('n.deliveries', {
            n: sg.min || BB_S.min_drops_new_plan
          })
        });
      case 'max_plan_aed':
        return tn('b.i.max_plan_aed', {
          price: fmtAed(sg.max || BB_S.max_plan_aed)
        });
      case 'fulfillment_unavailable':
        return t('b.i.fulfillment_unavailable');
      case 'bad_date':
        return t('b.i.bad_date');
      default:
        return t(ctx.row ? 'b.i.generic' : 'b.i.genericPlan');
    }
  }
  function IssueLine({
    issue,
    ctx,
    fix,
    fixLabel
  }) {
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-issue",
      role: "alert"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-issue-ic",
      "aria-hidden": "true"
    }, "!"), /*#__PURE__*/React.createElement("span", {
      className: "bc-issue-txt"
    }, bbIssueText(issue, ctx)), fix ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-issue-fix",
      onClick: fix
    }, fixLabel) : null);
  }

  // ── Section shell ────────────────────────────────────────────────────────────
  function BbSec({
    n,
    id,
    title,
    hint,
    ok,
    children
  }) {
    return /*#__PURE__*/React.createElement("section", {
      id: id,
      className: "bc-sec",
      "aria-labelledby": id + '-h',
      "data-sec": n
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-sec-top"
    }, /*#__PURE__*/React.createElement("span", {
      className: 'bc-sec-num' + (ok ? ' ok' : ''),
      "aria-hidden": "true"
    }, ok ? '✓' : digits(n)), /*#__PURE__*/React.createElement("h3", {
      id: id + '-h',
      className: "bc-sec-title"
    }, title)), hint ? /*#__PURE__*/React.createElement("p", {
      className: "bc-sec-hint"
    }, hint) : null, children);
  }

  // ── Item rows (breads with Club price; extras at menu price) ────────────────
  function BbItemRow({
    slug,
    qty,
    max,
    onChange,
    compact
  }) {
    const c = BB_CAT[slug] || {};
    const bread = c.kind !== 'extra';
    const menu = Number(c.price) || 0;
    const unit = bread ? filsToAed(subUnitFils(toFils(menu), BB_S.bread_discount_pct)) : menu;
    const name = itemName(slug, {
      long: true
    });
    const desc = I18N['item.' + slug + '.desc'] ? t('item.' + slug + '.desc') : '';
    const off = c.available === false;
    return /*#__PURE__*/React.createElement("div", {
      className: 'bc-item' + (compact ? ' bc-item--sm' : '') + (qty > 0 ? ' has-qty' : '') + (off ? ' is-off' : '')
    }, !compact && c.image ? /*#__PURE__*/React.createElement("img", {
      className: "bc-item-img",
      src: c.image,
      alt: "",
      width: "56",
      height: "56",
      loading: "lazy",
      decoding: "async",
      onError: e => {
        e.currentTarget.style.display = 'none';
      }
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: "bc-item-txt"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-item-name"
    }, name), desc && !compact ? /*#__PURE__*/React.createElement("div", {
      className: "bc-item-desc"
    }, desc) : null, /*#__PURE__*/React.createElement("div", {
      className: "bc-item-price"
    }, bread && unit < menu ? /*#__PURE__*/React.createElement("s", {
      className: "bc-item-menu",
      "aria-label": t('item.menuPrice')
    }, fmtAed(menu)) : null, tn('item.each', {
      price: fmtAed(unit, {
        cls: bread ? 'bc-item-club' : ''
      })
    })), off ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err"
    }, t('b.i.unavailable', {
      item: name
    })) : null), /*#__PURE__*/React.createElement(Stepper, {
      value: qty,
      min: 0,
      max: off ? qty : max,
      onChange: onChange,
      name: name,
      compact: compact
    }));
  }
  /** Breads + collapsible extras for an items map. Used by step 1 (the usual) and the per-day sheet. */
  function BbItemsEditor({
    items,
    onChange,
    compact,
    extrasOpen,
    setExtrasOpen
  }) {
    const set = (slug, q) => onChange(Object.assign({}, items, {
      [slug]: q
    }));
    const breads = bbBreadCount(items);
    const nExtras = bbExtraCount(items);
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-items"
    }, BB_BREADS.map(slug => /*#__PURE__*/React.createElement(BbItemRow, {
      key: slug,
      slug: slug,
      qty: items[slug] || 0,
      compact: compact,
      max: Math.max(items[slug] || 0, BB_S.max_breads_drop - (breads - (items[slug] || 0))),
      onChange: q => set(slug, q)
    })), BB_EXTRAS.length ? /*#__PURE__*/React.createElement("div", {
      className: 'bc-extras' + (extrasOpen ? ' open' : '')
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-extras-toggle",
      "aria-expanded": !!extrasOpen,
      onClick: () => setExtrasOpen(!extrasOpen)
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-extras-t"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-extras-h"
    }, t('b.extrasT'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-opt"
    }, "(", t('b.optional'), ")")), /*#__PURE__*/React.createElement("span", {
      className: "bc-extras-s"
    }, nExtras ? t('b.extrasN', {
      n: nExtras
    }) : t('b.extrasS'))), /*#__PURE__*/React.createElement("span", {
      className: "bc-extras-caret",
      "aria-hidden": "true"
    }, "\u25BE")), extrasOpen ? /*#__PURE__*/React.createElement("div", {
      className: "bc-extras-list"
    }, BB_EXTRAS.map(slug => /*#__PURE__*/React.createElement(BbItemRow, {
      key: slug,
      slug: slug,
      qty: items[slug] || 0,
      compact: true,
      max: BB_S.max_extras_qty,
      onChange: q => set(slug, q)
    }))) : null) : null);
  }

  // ── Window chips ─────────────────────────────────────────────────────────────
  function BbWindows({
    value,
    onChange,
    badges
  }) {
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-chips--2 bc-wins",
      role: "radiogroup",
      "aria-label": t('b.time')
    }, BB_S.windows.map(w => {
      const b = badges ? badges[w] : null;
      return /*#__PURE__*/React.createElement(Chip, {
        key: w,
        radio: true,
        on: value === w,
        onClick: () => onChange(w),
        sub: t(bbWinKey(w)),
        badge: b === 'full' ? t('b.fullSome') : b === 'few' ? t('b.few') : null,
        badgeKind: b === 'full' ? 'full' : ''
      }, fmtWindow(w));
    }));
  }

  // ── Calendar of one cycle (pick dates / add-remove days) ────────────────────
  function BbCal({
    start,
    selected,
    onToggle,
    nowMs
  }) {
    const L = BC_LANG;
    const cyc = cycleRange(start, BB_S);
    const order = bbWeekOrder(L);
    const lead = order.indexOf(ymdWeekday(start));
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let i = 0; i < BB_S.cycle_days; i++) cells.push(ymdAdd(cyc.start, i));
    while (cells.length % 7) cells.push(null);
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-cal"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-cal-head",
      "aria-hidden": "true"
    }, order.map(wd => /*#__PURE__*/React.createElement("span", {
      key: wd,
      className: "bc-cal-wd"
    }, bcWeekdayName(wd, L, L === 'en' ? 'short' : 'narrow')))), /*#__PURE__*/React.createElement("div", {
      className: "bc-cal-grid"
    }, cells.map((day, i) => {
      if (!day) return /*#__PURE__*/React.createElement("span", {
        key: 'x' + i,
        className: "bc-cal-blank",
        "aria-hidden": "true"
      });
      const closed = isBlackout(day, BB_S);
      const ok = plannableDay(day, nowMs, BB_S, BB_MARGIN).ok;
      const on = selected.indexOf(day) >= 0;
      const first = i === lead || day.slice(8) === '01';
      return /*#__PURE__*/React.createElement("button", {
        key: day,
        type: "button",
        className: 'bc-cal-d' + (on ? ' on' : '') + (closed ? ' closed' : ''),
        "aria-pressed": on,
        disabled: !ok && !on,
        "aria-label": fmtDay(day, L, {
          weekday: 'long',
          month: 'long'
        }) + (closed ? ' · ' + t('b.closedDay') : ''),
        onClick: () => onToggle(day)
      }, first ? /*#__PURE__*/React.createElement("span", {
        className: "bc-cal-m"
      }, bbMonthShort(day, L)) : null, /*#__PURE__*/React.createElement("span", {
        className: "bc-cal-n"
      }, digits(Number(day.slice(8)), L)));
    })));
  }

  // ── Per-day sheet ────────────────────────────────────────────────────────────
  // Rendered only while open and keyed by the day, so its state always starts from that day's values.
  function BbDaySheet({
    drop,
    d,
    ctx,
    onSave,
    onRemove,
    onReset,
    onClose
  }) {
    const [items, setItems] = useState(() => Object.assign({}, drop.itemsMap));
    const [slot, setSlot] = useState(drop.slot);
    const [extrasOpen, setExtrasOpen] = useState(() => bbExtraCount(drop.itemsMap) > 0);
    const arr = bbItemsArr(items);
    const p = priceDrop({
      items: arr,
      fulfillment: d.ful,
      band: ctx.bandForMin || 15,
      s: BB_S,
      catalog: BB_CAT,
      feeAed: ctx.feeUnknown ? 0 : undefined
    });
    const errs = bbItemIssues(validateDropItems(arr, d.ful, ctx.bandForMin || 15, BB_S, BB_CAT), d.ful, ctx.bandForMin);
    const minB = minBreads(d.ful, ctx.bandForMin || 15, BB_S);
    const bump = () => {
      const add = minB - bbBreadCount(items);
      if (add <= 0) return;
      const slug = BB_BREADS.find(s => (items[s] || 0) > 0) || BB_BREADS[0];
      setItems(Object.assign({}, items, {
        [slug]: (items[slug] || 0) + add
      }));
    };
    return /*#__PURE__*/React.createElement(Sheet, {
      open: true,
      onClose: onClose,
      title: fmtDay(drop.day, BC_LANG, {
        weekday: 'long',
        month: 'long'
      }),
      className: "bc-day-sheet",
      footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Btn, {
        kind: "ghost",
        onClick: () => onRemove(drop.day)
      }, t('b.removeDay')), /*#__PURE__*/React.createElement(Btn, {
        className: "bc-grow",
        disabled: errs.length > 0,
        onClick: () => onSave(drop.day, items, slot)
      }, t('b.saveDay')))
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-day-sum"
    }, /*#__PURE__*/React.createElement("span", null, p.breads > 0 ? t('n.breads', {
      n: p.breads
    }) : '—'), /*#__PURE__*/React.createElement("span", {
      className: "bc-day-price"
    }, fmtAed(filsToAed(p.total_fils)))), errs.map((e, i) => /*#__PURE__*/React.createElement(IssueLine, {
      key: i,
      issue: e,
      ctx: ctx,
      fix: e.reason === 'min_breads' ? bump : null,
      fixLabel: t('b.fix.min', {
        n: minB
      })
    })), /*#__PURE__*/React.createElement(BbItemsEditor, {
      items: items,
      onChange: setItems,
      compact: true,
      extrasOpen: extrasOpen,
      setExtrasOpen: setExtrasOpen
    }), /*#__PURE__*/React.createElement("div", {
      className: "bc-label bc-label--sp"
    }, t('b.time'), " ", BB_NOT_DUBAI_TZ ? /*#__PURE__*/React.createElement("span", {
      className: "bc-opt"
    }, t('c.dubaiTime')) : null), /*#__PURE__*/React.createElement(BbWindows, {
      value: slot,
      onChange: setSlot,
      badges: ctx.dayBadges ? ctx.dayBadges(drop.day, p.breads) : null
    }), drop.custom || drop.slotCustom ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-btn bc-btn--link bc-reset",
      onClick: () => onReset(drop.day)
    }, t('b.resetDay')) : null);
  }

  // ── Start-date sheet ─────────────────────────────────────────────────────────
  function BbStartSheet({
    open,
    earliest,
    start,
    nowMs,
    onPick,
    onClose
  }) {
    if (!open) return null;
    const opts = [];
    let y = earliest;
    for (let i = 0; i < 40 && opts.length < 14; i++) {
      if (plannableDay(y, nowMs, BB_S, BB_MARGIN).ok) opts.push(y);
      y = ymdAdd(y, 1);
    }
    return /*#__PURE__*/React.createElement(Sheet, {
      open: open,
      onClose: onClose,
      title: t('b.startT')
    }, /*#__PURE__*/React.createElement("p", {
      className: "bc-p bc-small"
    }, t('b.startH', {
      weeks: t('n.weeks', {
        n: BB_WEEKS
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-chips--3 bc-starts",
      role: "radiogroup",
      "aria-label": t('b.startT')
    }, opts.map(day => /*#__PURE__*/React.createElement(Chip, {
      key: day,
      radio: true,
      on: day === start,
      onClick: () => onPick(day),
      sub: BC_LANG === 'fa' ? fmtDay(day, 'fa', {
        weekday: false,
        persian: true
      }) : null
    }, fmtDay(day)))));
  }

  // ── Days sheet (add / remove single days inside the cycle) ──────────────────
  function BbDaysSheet({
    open,
    start,
    selected,
    nowMs,
    onToggle,
    onClose
  }) {
    if (!open) return null;
    return /*#__PURE__*/React.createElement(Sheet, {
      open: open,
      onClose: onClose,
      title: t('b.addDay'),
      footer: /*#__PURE__*/React.createElement(Btn, {
        className: "bc-grow",
        onClick: onClose
      }, t('c.close'))
    }, /*#__PURE__*/React.createElement("p", {
      className: "bc-p bc-small"
    }, t('b.calH')), /*#__PURE__*/React.createElement(BbCal, {
      start: start,
      selected: selected,
      nowMs: nowMs,
      onToggle: onToggle
    }));
  }

  // ── Gift fields ──────────────────────────────────────────────────────────────
  function BbGift({
    gift,
    onChange,
    showErr,
    srv
  }) {
    const g = gift || {};
    const sv = srv || {}; // refusals from the last checkout (cleared as soon as a field changes)
    const set = patch => onChange(Object.assign({}, g, patch));
    const phoneOk = !!phoneE164(g.phone) && !sv.gift_phone;
    const nameBad = cleanLine(g.name, 60).length < 2 || !!sv.gift_name;
    return /*#__PURE__*/React.createElement("div", {
      className: 'bc-gift' + (g.on ? ' on' : '')
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!g.on,
      onChange: e => set({
        on: e.target.checked
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "bc-check-box",
      "aria-hidden": "true"
    }, "\u2713"), /*#__PURE__*/React.createElement("span", {
      className: "bc-check-txt"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-check-t"
    }, "\uD83C\uDF81 ", t('b.giftT')), /*#__PURE__*/React.createElement("span", {
      className: "bc-check-s"
    }, t('b.giftS')))), g.on ? /*#__PURE__*/React.createElement("div", {
      className: "bc-gift-f"
    }, /*#__PURE__*/React.createElement("div", {
      className: 'bc-field' + (showErr && nameBad ? ' is-err' : '')
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-gift-name"
    }, t('b.giftName'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-req",
      "aria-hidden": "true"
    }, "*")), /*#__PURE__*/React.createElement("input", {
      id: "bc-gift-name",
      className: "bc-input",
      type: "text",
      maxLength: 60,
      autoComplete: "off",
      value: g.name || '',
      onChange: e => set({
        name: e.target.value
      })
    }), showErr && nameBad ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t('b.giftErrName')) : null), /*#__PURE__*/React.createElement("div", {
      className: 'bc-field' + (showErr && !phoneOk ? ' is-err' : '')
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-gift-phone"
    }, t('b.giftPhone'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-req",
      "aria-hidden": "true"
    }, "*")), /*#__PURE__*/React.createElement(PhoneInput, {
      id: "bc-gift-phone",
      value: g.phone,
      onChange: v => set({
        phone: v
      }),
      invalid: showErr && !phoneOk,
      describedBy: "bc-gift-phone-err"
    }), showErr && !phoneOk ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      id: "bc-gift-phone-err",
      role: "alert"
    }, bcPhoneErr()) : null), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-gift-note"
    }, t('b.giftNote'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-opt"
    }, t('c.optional'))), /*#__PURE__*/React.createElement("input", {
      id: "bc-gift-note",
      className: "bc-input",
      type: "text",
      maxLength: 140,
      value: g.note || '',
      onChange: e => set({
        note: e.target.value
      })
    }))) : null);
  }

  // ── Closed states (full / not open yet / data missing) ──────────────────────
  function BbClosed({
    kind,
    onFind
  }) {
    const wa = bcWaLink(t('b.waJoin'));
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-app bc-builder"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-card bc-closed"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-kicker"
    }, t('b.kicker')), /*#__PURE__*/React.createElement("h1", {
      className: "bc-h2"
    }, t('b.' + kind + '.t')), /*#__PURE__*/React.createElement("p", {
      className: "bc-p"
    }, t('b.' + kind + '.s')), /*#__PURE__*/React.createElement("div", {
      className: "bc-closed-acts"
    }, kind !== 'down' && wa ? /*#__PURE__*/React.createElement("a", {
      className: "bc-btn bc-btn--primary",
      href: wa,
      target: "_blank",
      rel: "noopener"
    }, t('c.wa')) : null, /*#__PURE__*/React.createElement("a", {
      className: "bc-btn bc-btn--ghost",
      href: '/?lang=' + BC_LANG
    }, t('b.menu')))), /*#__PURE__*/React.createElement(SavedPlansCard, {
      onFind: onFind
    }));
  }

  // ═══ The builder ═════════════════════════════════════════════════════════════
  function BreadBuilder() {
    const L = BC_LANG;
    const [now, setNow] = useState(bcNow());
    const [d, setD] = useState(() => bbInitDraft(bbEarliest(bcNow())));
    const [cap, setCap] = useState(null); // { from, to, cap }
    const [quote, setQuote] = useState(null); // { key, q } | { key, err }
    const [quoteBusy, setQuoteBusy] = useState(false);
    const [coIssues, setCoIssues] = useState(null); // { key, issues:[{day, reason, suggestion}] } from a refused checkout
    const [srvField, setSrvField] = useState(null); // { key, f:{name:1,…} } contact/address refusals (BB_FIELD_REASONS)
    const [findOpen, setFindOpen] = useState(() => bbUrlParams().get('find') === '1');
    const [sheet, setSheet] = useState(null); // { kind:'day', day } | { kind:'start' } | { kind:'days' }
    const [extrasOpen, setExtrasOpen] = useState(() => bbExtraCount(d.items) > 0);
    const [flag, setFlag] = useState({}); // sections whose errors are visible
    const [seen, setSeen] = useState({}); // sections that have been on screen
    const [typing, setTyping] = useState(false);
    const [paying, setPaying] = useState(false);
    const [stalled, setStalled] = useState(false);
    const [payErr, setPayErr] = useState('');
    const [emailBlur, setEmailBlur] = useState(false);
    const [state, setState] = useState(() => {
      if (!BC || !BB_BREADS.length) return 'down';
      if (BB_S.enabled === false) return 'off';
      if (BC.full === true || BB_S.accepting_new === false) return 'full';
      return 'open';
    });
    const [toastNode, showToast] = useToast();
    const lastTarget = useRef(0);
    const redirectingRef = useRef(false);
    const leftRef = useRef(false);
    const planUrlRef = useRef('');
    const payGen = useRef(0);
    const quoteSeq = useRef(0);
    const trackedSteps = useRef({});

    // Clock: the earliest day moves at the cutoff; a page left open overnight must notice.
    useEffect(() => {
      const id = setInterval(() => setNow(bcNow()), 60000);
      return () => clearInterval(id);
    }, []);
    const up = useCallback(patch => setD(p => Object.assign({}, p, typeof patch === 'function' ? patch(p) : patch)), []);
    const c = bbCompute(d, now, cap && cap.cap);
    const bandForMin = d.ful === 'pickup' ? 0 : c.band || BANDS[0];

    // Persist the draft (24 h) — survives the language-switch reload and an in-app webview being killed.
    // Only while the builder is open: a "back soon" page (empty catalog) must never overwrite a good draft.
    useEffect(() => {
      if (state !== 'open') return undefined;
      const id = setTimeout(() => {
        try {
          localStorage.setItem(BB_DRAFT_KEY, JSON.stringify(Object.assign({}, d, {
            at: Date.now()
          })));
        } catch (e) {}
      }, 300);
      return () => clearTimeout(id);
    }, [d, state]);

    // First view: analytics + first-touch attribution capture (ads land here directly).
    useEffect(() => {
      sfTrack('bread_view', {
        lang: L,
        state
      });
      bcAttribution();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Availability for the whole cycle (window badges + instant capacity issues). Re-read when the cycle moves.
    const capFrom = c.cyc.start,
      capTo = c.cyc.end;
    const loadCap = useCallback(() => {
      if (state !== 'open') return;
      api('/api/bread/availability', {
        from: capFrom,
        to: capTo
      }).then(r => {
        if (r && r.ok && r.cap && typeof r.cap === 'object') setCap({
          from: capFrom,
          to: capTo,
          cap: r.cap
        });
      });
    }, [capFrom, capTo, state]);
    useEffect(() => {
      loadCap();
    }, [loadCap]);

    // Server quote — debounced 600 ms; only the newest answer counts.
    useEffect(() => {
      if (state !== 'open' || !c.days.length) {
        setQuoteBusy(false);
        return undefined;
      }
      const key = c.key,
        body = {
          draft: c.api
        };
      setQuoteBusy(true);
      const id = setTimeout(() => {
        const my = ++quoteSeq.current;
        api('/api/bread/quote', body).then(r => {
          if (my !== quoteSeq.current) return;
          setQuoteBusy(false);
          if (r && r.ok && r.quote) setQuote({
            key,
            q: r.quote
          });else setQuote({
            key,
            err: r && r.code || 'server'
          });
        });
      }, 600);
      return () => clearTimeout(id);
    }, [c.key, state]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sticky bar hides while the keyboard is up (any text field focused).
    useEffect(() => {
      let tm = 0;
      const isField = el => el && (el.tagName === 'INPUT' && ['checkbox', 'radio', 'button', 'submit'].indexOf(el.type) < 0 || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');
      const onIn = e => {
        if (isField(e.target)) {
          clearTimeout(tm);
          setTyping(true);
        }
      };
      const onOut = e => {
        if (isField(e.target)) {
          clearTimeout(tm);
          tm = setTimeout(() => setTyping(false), 120);
        }
      };
      document.addEventListener('focusin', onIn);
      document.addEventListener('focusout', onOut);
      return () => {
        document.removeEventListener('focusin', onIn);
        document.removeEventListener('focusout', onOut);
        clearTimeout(tm);
      };
    }, []);

    // Which sections have been on screen (step analytics + "Pay" only after step 6 was seen).
    useEffect(() => {
      if (state !== 'open' || typeof IntersectionObserver === 'undefined') {
        setSeen({
          1: 1,
          2: 1,
          3: 1,
          4: 1,
          5: 1,
          6: 1
        });
        return undefined;
      }
      const io = new IntersectionObserver(ents => {
        ents.forEach(en => {
          if (!en.isIntersecting) return;
          const n = Number(en.target.getAttribute('data-sec'));
          if (!n) return;
          setSeen(s => s[n] ? s : Object.assign({}, s, {
            [n]: 1
          }));
          if (!trackedSteps.current[n]) {
            trackedSteps.current[n] = 1;
            sfTrack('bread_step_' + n, {});
          }
        });
      }, {
        threshold: 0.25
      });
      const els = document.querySelectorAll('.bc-builder .bc-sec[data-sec]');
      Array.prototype.forEach.call(els, el => io.observe(el));
      return () => io.disconnect();
    }, [state]);

    // Card-page return recovery (copied in spirit from app.babel.js CheckoutModal bfcache rescue): a page
    // restored from the back-forward cache after we sent the customer to pay must LEAVE for the plan page,
    // which knows the payment's true state — never sit on a frozen "Opening the card page…" overlay.
    useEffect(() => {
      const go = () => {
        const u = planUrlRef.current;
        redirectingRef.current = false;
        leftRef.current = false;
        if (u) window.location.replace(u);
      };
      const onShow = () => {
        if (redirectingRef.current) go();
      };
      const onHide = () => {
        if (redirectingRef.current) leftRef.current = true;
      };
      const onBack = () => {
        if (redirectingRef.current && leftRef.current) go();
      };
      const onVis = () => {
        if (document.visibilityState === 'visible') onBack();
      };
      window.addEventListener('pageshow', onShow);
      window.addEventListener('pagehide', onHide);
      window.addEventListener('focus', onBack);
      document.addEventListener('visibilitychange', onVis);
      return () => {
        window.removeEventListener('pageshow', onShow);
        window.removeEventListener('pagehide', onHide);
        window.removeEventListener('focus', onBack);
        document.removeEventListener('visibilitychange', onVis);
      };
    }, []);
    useEffect(() => {
      if (!paying) {
        setStalled(false);
        return undefined;
      }
      const id = setTimeout(() => setStalled(true), 8000);
      return () => clearTimeout(id);
    }, [paying]);

    // ── merge server issues into the local picture ──
    const fresh = quote && quote.key === c.key && quote.q ? quote.q : null;
    const coFresh = coIssues && coIssues.key === c.key ? coIssues.issues : null;
    const dropView = c.drops.map(x => {
      const sq = fresh && Array.isArray(fresh.drops) ? fresh.drops.find(y => y.day === x.day) : null;
      let issues = x.issues.slice();
      const add = list => (list || []).forEach(is => {
        if (!is || !is.reason) return;
        if (!x.custom && BB_ITEM_REASONS.indexOf(is.reason) >= 0) return; // the usual's item problems live in step 1
        issues.push({
          reason: is.reason,
          suggestion: is.suggestion
        });
      });
      if (sq) add(sq.issues);
      if (coFresh) add(coFresh.filter(is => is.day === x.day));
      issues = bbDedupe(issues);
      const total = sq && Number.isFinite(Number(sq.total)) ? Number(sq.total) : filsToAed(x.p.total_fils);
      return Object.assign({}, x, {
        issues,
        total
      });
    });
    let planIssues = c.planIssues.slice();
    if (fresh && Array.isArray(fresh.issues)) fresh.issues.forEach(is => {
      if (is && is.reason && is.reason !== 'area_required' && BB_ITEM_REASONS.indexOf(is.reason) < 0) planIssues.push(is);
    });
    if (coFresh) coFresh.filter(is => !is.day).forEach(is => {
      if (is.reason !== 'area_required' && BB_ITEM_REASONS.indexOf(is.reason) < 0 && !BB_FIELD_REASONS[is.reason] && BB_SESSION_REASONS.indexOf(is.reason) < 0) planIssues.push(is);
    });
    planIssues = bbDedupe(planIssues);
    const baseIssues = bbDedupe(c.baseIssues.concat(fresh && Array.isArray(fresh.issues) ? fresh.issues.filter(is => is && BB_ITEM_REASONS.indexOf(is.reason) >= 0) : []));

    // Totals: the fresh server quote wins; otherwise the instant local price.
    const loc = c.tot;
    const T = fresh && fresh.totals ? {
      drops: Number(fresh.totals.drops) || 0,
      breads: Number(fresh.totals.breads) || 0,
      goods: Number(fresh.totals.goods) || 0,
      fee: Number(fresh.totals.fee) || 0,
      vat: Number(fresh.totals.vat) || 0,
      total: Number(fresh.totals.total) || 0,
      savings: Number(fresh.totals.savings) || 0,
      per_loaf: Number(fresh.totals.per_loaf) || 0,
      due: fresh.due != null && Number.isFinite(Number(fresh.due)) ? Number(fresh.due) : Number(fresh.totals.total) || 0
    } : {
      drops: loc.drops,
      breads: loc.breads,
      goods: filsToAed(loc.goods_fils),
      fee: filsToAed(loc.fee_fils),
      vat: filsToAed(loc.vat_fils),
      total: filsToAed(loc.total_fils),
      savings: 0,
      per_loaf: loc.breads ? Math.round(loc.total_fils / loc.breads) / 100 : 0,
      due: filsToAed(loc.total_fils)
    };
    const ctx = {
      ful: d.ful,
      area: c.area,
      bandForMin,
      feeUnknown: c.feeUnknown,
      dayBadges: (day, breads) => bbBadgesFor([day], breads, cap && cap.cap)
    };

    // ── section validity ── (a checkout's contact/address refusal counts until any of those fields changes)
    const fieldKey = JSON.stringify([d.ful, d.name, d.phone, d.email, d.addr, d.gift]);
    const sf = srvField && srvField.key === fieldKey ? srvField.f : {};
    const addrErrs = d.ful === 'delivery' ? Object.assign(bbSrvAddrErrs(sf), addressErrors(d.addr)) : {};
    const giftOk = !d.gift.on || cleanLine(d.gift.name, 60).length >= 2 && !!phoneE164(d.gift.phone) && !sf.gift_name && !sf.gift_phone;
    const nameOk = cleanLine(d.name, 60).length >= 2 && !sf.name;
    const phoneOk = !!phoneE164(d.phone) && !sf.phone;
    const emailOk = (!d.email.trim() || BB_EMAIL_RE.test(d.email.trim())) && !sf.email;
    const emailBad = !emailOk && (emailBlur || !!flag[6]); // never red while the customer is still typing it
    const rowsBad = dropView.some(x => x.issues.length > 0);
    const ok = {
      1: baseIssues.length === 0,
      2: !planIssues.some(x => ['no_weekdays', 'no_drops', 'min_drops'].indexOf(x.reason) >= 0),
      3: isValidWindow(d.slot, BB_S),
      4: c.days.length > 0 && !rowsBad && planIssues.length === 0,
      5: (d.ful === 'pickup' || Object.keys(addrErrs).length === 0) && giftOk,
      6: nameOk && phoneOk && emailOk
    };
    const allOk = ok[1] && ok[2] && ok[3] && ok[4] && ok[5] && ok[6];
    const canPay = allOk && !!seen[6];

    // ── mutations ──
    const setItems = items => up({
      items
    });
    const setFreq = freq => up(p => {
      const patch = {
        freq,
        removed: [],
        extra: []
      };
      if (freq === 'dates' && !p.dates.length) {
        // Seed the calendar with what the customer was looking at, so "Pick dates" starts from their plan.
        patch.dates = c.days.slice();
      }
      if ((freq === 'weekly' || freq === 'biweekly') && p.freq === 'weekend') patch.weekdays = [5, 6];
      if ((freq === 'weekly' || freq === 'biweekly') && !(patch.weekdays || p.weekdays).length) patch.weekdays = [bbDefaultWeekday(c.earliest)];
      return patch;
    });
    const toggleWeekday = wd => up(p => {
      const cur = bbWeekdays(p).slice();
      const i = cur.indexOf(wd);
      if (i >= 0) cur.splice(i, 1);else cur.push(wd);
      return {
        weekdays: cur.sort(),
        freq: p.freq === 'weekend' ? 'weekly' : p.freq,
        removed: [],
        extra: []
      };
    });
    const toggleDay = day => up(p => {
      if (p.freq === 'dates') {
        const has = p.dates.indexOf(day) >= 0;
        return {
          dates: has ? p.dates.filter(x => x !== day) : p.dates.concat([day]).sort()
        };
      }
      if (c.patDays.indexOf(day) >= 0) {
        const rm = p.removed.indexOf(day) >= 0;
        return {
          removed: rm ? p.removed.filter(x => x !== day) : p.removed.concat([day])
        };
      }
      const ex = p.extra.indexOf(day) >= 0;
      return {
        extra: ex ? p.extra.filter(x => x !== day) : p.extra.concat([day]).sort()
      };
    });
    const removeDay = day => {
      toggleDay(day);
      setSheet(null);
      showToast(t('b.removed', {
        day: fmtDay(day)
      }), {
        action: () => toggleDay(day),
        label: t('c.undo')
      });
    };
    const setDaySlot = (day, slot) => up(p => ({
      over: Object.assign({}, p.over, {
        [day]: Object.assign({}, p.over[day] || {}, {
          slot
        })
      })
    }));
    const saveDay = (day, items, slot) => {
      up(p => {
        const over = Object.assign({}, p.over);
        const e = {};
        if (!bbSameItems(items, p.items)) e.items = Object.assign({}, items);
        if (slot !== p.slot) e.slot = slot;
        if (e.items || e.slot) over[day] = e;else delete over[day];
        return {
          over
        };
      });
      setSheet(null);
    };
    const resetDay = day => {
      up(p => {
        const over = Object.assign({}, p.over);
        delete over[day];
        return {
          over
        };
      });
      setSheet(null);
    };
    const bumpBase = () => {
      const minB = minBreads(d.ful, bandForMin || 15, BB_S);
      const add = minB - bbBreadCount(d.items);
      if (add <= 0) return;
      const slug = BB_BREADS.find(s => (d.items[s] || 0) > 0) || BB_BREADS[0];
      setItems(Object.assign({}, d.items, {
        [slug]: (d.items[slug] || 0) + add
      }));
    };
    const setStart = day => {
      up({
        start: day,
        removed: [],
        extra: [],
        over: {}
      });
      setSheet(null);
    };

    // One-tap fixes for a row issue.
    const rowFix = (x, is) => {
      if (is.reason === 'capacity' || is.reason === 'bad_window') {
        const sl = is.suggestion && is.suggestion.slot;
        if (sl && isValidWindow(sl, BB_S)) return {
          fn: () => setDaySlot(x.day, sl),
          label: t('b.fix.slot', {
            time: fmtWindowText(sl)
          })
        };
        return {
          fn: () => setSheet({
            kind: 'day',
            day: x.day
          }),
          label: t('b.change')
        };
      }
      if (is.reason === 'unavailable' && is.suggestion && is.suggestion.slug) {
        const slug = is.suggestion.slug;
        return {
          fn: () => up(p => {
            const o = p.over[x.day];
            const items = Object.assign({}, o && o.items || p.items, {
              [slug]: 0
            });
            return {
              over: Object.assign({}, p.over, {
                [x.day]: Object.assign({}, o || {}, {
                  items
                })
              })
            };
          }),
          label: t('b.fix.removeItem')
        };
      }
      if (BB_DROP_REMOVE.indexOf(is.reason) >= 0) return {
        fn: () => removeDay(x.day),
        label: t('b.removeDay')
      };
      return {
        fn: () => setSheet({
          kind: 'day',
          day: x.day
        }),
        label: t('b.change')
      };
    };
    const planFix = is => {
      if (is.reason === 'min_drops') {
        if (d.freq === 'biweekly') return {
          fn: () => setFreq('weekly'),
          label: t('b.fix.weekly')
        };
        return {
          fn: () => setSheet({
            kind: 'days'
          }),
          label: t('b.addDay')
        };
      }
      if (is.reason === 'area_required') return {
        fn: () => bbScrollTo('bc-f-area'),
        label: t('b.fix.area')
      };
      if (is.reason === 'fulfillment_unavailable') return {
        fn: () => bbScrollTo('bc-s5'),
        label: t('b.s5')
      };
      return null;
    };

    // Continue: walk to the first section that still needs the customer; on a second tap there, show its errors.
    const next = () => {
      for (let n = 1; n <= 6; n++) {
        if (!ok[n] || n === 6 && !seen[6]) {
          const reveal = lastTarget.current === n && !ok[n];
          if (reveal || n <= 4) setFlag(f => Object.assign({}, f, {
            [n]: 1
          }));
          lastTarget.current = n;
          // We just brought step 6 on screen ourselves — count it as seen even if IntersectionObserver never
          // fires (some webviews), so the bar can never get stuck on "Continue".
          if (n === 6) setSeen(s => s[6] ? s : Object.assign({}, s, {
            6: 1
          }));
          if (reveal) bbScrollToFirstError('bc-s' + n);else bbScrollTo('bc-s' + n);
          return;
        }
      }
      pay();
    };

    // A new checkout_id and no "you started plan X" note (setD, not up(): up() merges, so `delete n.ref` would not stick).
    const freshCheckout = () => {
      const nc = uuid();
      setD(p => {
        const n = Object.assign({}, p, {
          cid: nc
        });
        delete n.ref;
        return n;
      });
      setCoIssues(null);
      setPayErr('');
      return nc;
    };

    // cidOverride/retried: the one automatic resubmit after 'checkout_expired' (with a fresh checkout_id).
    const pay = (cidOverride, retried) => {
      if (paying) return;
      if (!allOk) {
        setFlag({
          1: 1,
          2: 1,
          3: 1,
          4: 1,
          5: 1,
          6: 1
        });
        setPayErr('b.e.fix');
        for (let n = 1; n <= 6; n++) {
          if (!ok[n]) {
            lastTarget.current = n;
            bbScrollToFirstError('bc-s' + n);
            break;
          }
        }
        return;
      }
      setPayErr('');
      const q = bbUrlParams();
      const entry = cleanLine(q.get('entry') || q.get('src') || 'bread', 40).replace(/[^a-z0-9_-]/gi, '').slice(0, 40) || 'bread';
      const attribution = bcAttribution();
      const from = q.get('from');
      const body = {
        checkout_id: typeof cidOverride === 'string' && cidOverride || d.cid,
        draft: c.api,
        customer: {
          name: cleanLine(d.name, 60),
          phone: phoneE164(d.phone),
          email: d.email.trim() || undefined
        },
        address: d.ful === 'delivery' ? {
          area_key: d.addr.area_key,
          line: cleanLine(d.addr.line, 160),
          unit: cleanLine(d.addr.unit, 40),
          handover: d.addr.handover || 'door',
          ring_bell: !!d.addr.ring_bell,
          gate_note: cleanLine(d.addr.gate_note, 120),
          notes: cleanLine(d.addr.notes, 200),
          lat: Number(d.addr.lat),
          lng: Number(d.addr.lng)
        } : undefined,
        gift: d.gift.on ? {
          name: cleanLine(d.gift.name, 60),
          phone: phoneE164(d.gift.phone),
          note: cleanLine(d.gift.note, 140)
        } : undefined,
        remind: d.remind,
        marketing_opt_in: !!d.optin,
        lang: L,
        entry,
        attribution: Object.assign({}, attribution || {}, from ? {
          from: cleanLine(from, 40)
        } : {})
      };
      if (!retried) sfTrack('bread_checkout', {
        value: T.due,
        currency: 'AED',
        deliveries: T.drops,
        breads: T.breads,
        fulfillment: d.ful
      });
      const gen = ++payGen.current;
      setPaying(true);
      api('/api/bread/checkout', body, {
        timeout: 30000
      }).then(r => {
        if (gen !== payGen.current) return;
        if (r && r.ok && r.ref && r.token) {
          // Save the plan link on this device BEFORE leaving for the bank (in-app browsers lose everything else).
          savePlanLocal({
            ref: r.ref,
            token: r.token,
            name: cleanLine(d.name, 60)
          });
          try {
            sessionStorage.setItem('sf-bread-token', r.token);
          } catch (e) {}
          planUrlRef.current = r.plan_url || planHref(r.token);
          // Keep the draft (with its checkout_id): a customer who backs out of the card page and pays again from
          // here gets the SAME plan re-quoted with a fresh payment session (§5 checkout reuse), not a second plan.
          try {
            localStorage.setItem(BB_DRAFT_KEY, JSON.stringify(Object.assign({}, d, {
              cid: body.checkout_id,
              at: Date.now(),
              ref: r.ref
            })));
          } catch (e) {}
          if (r.paid || !r.payment_url) {
            window.location.href = planUrlRef.current;
            return;
          }
          redirectingRef.current = true;
          window.location.href = r.payment_url;
          return;
        }
        setPaying(false);
        const code = r && r.code || 'server';
        if (code === 'full') {
          setState('full');
          return;
        }
        if (code === 'disabled') {
          setState('off');
          return;
        }
        if (code === 'validation' || code === 'capacity' || code === 'too_soon') {
          const issues = Array.isArray(r.issues) ? r.issues.filter(x => x && x.reason) : [];
          const has = re => issues.some(x => x.reason === re);
          // The checkout session was refused, not the plan. Expired (cancelled / superseded / timed-out pending plan):
          // resubmit once with a fresh id. 'checkout_id' (that id's plan has another phone) is never rotated
          // automatically — its plan could still get paid; the customer chooses. retry / bad_draft: just tap Pay again.
          if (has('checkout_expired') && !retried) {
            pay(freshCheckout(), true);
            return;
          }
          if (has('checkout_id')) {
            setPayErr('b.e.cid');
            return;
          }
          if (!issues.length || issues.some(x => BB_SESSION_REASONS.indexOf(x.reason) >= 0)) {
            setPayErr('c.server');
            return;
          }
          // Contact / address refusals belong to their field in step 5 or 6, not to the plan.
          const fieldIs = issues.filter(x => BB_FIELD_REASONS[x.reason]);
          if (fieldIs.length) {
            const f = {};
            fieldIs.forEach(x => {
              f[x.reason] = 1;
            });
            setSrvField({
              key: fieldKey,
              f
            });
            setFlag({
              1: 1,
              2: 1,
              3: 1,
              4: 1,
              5: 1,
              6: 1
            });
            setPayErr('b.e.fix');
            bbScrollToFirstError(fieldIs.some(x => BB_FIELD_REASONS[x.reason] === 5) ? 'bc-s5' : 'bc-s6');
            return;
          }
          setCoIssues({
            key: c.key,
            issues
          });
          setFlag({
            1: 1,
            2: 1,
            3: 1,
            4: 1,
            5: 1,
            6: 1
          });
          loadCap();
          // Step 2 shows only "how often" problems; every other plan or day issue is listed in step 4.
          const onlyFreq = issues.every(x => !x.day && ['no_weekdays', 'no_drops', 'min_drops'].indexOf(x.reason) >= 0);
          setPayErr(issues.some(x => x.day) ? 'b.e.days' : 'b.e.fix');
          bbScrollTo(onlyFreq ? 'bc-s2' : 'bc-s4');
          return;
        }
        if (code === 'payment_unavailable' && /checking/i.test(String(r && r.message || ''))) {
          setPayErr('b.e.payCheck');
          return;
        }
        setPayErr(code === 'network' ? 'c.network' : code === 'rate_limited' ? 'c.rate' : code === 'payment_unavailable' ? 'b.e.payDown' : 'c.server');
      });
    };
    const cancelPaying = () => {
      if (planUrlRef.current) {
        redirectingRef.current = false;
        window.location.href = planUrlRef.current;
        return;
      }
      payGen.current++;
      setPaying(false);
    };
    if (state !== 'open') {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BbClosed, {
        kind: state,
        onFind: () => setFindOpen(true)
      }), /*#__PURE__*/React.createElement(FindPlanSheet, {
        open: findOpen,
        onClose: () => setFindOpen(false)
      }));
    }

    // ── render ──
    const weeksTxt = t('n.weeks', {
      n: BB_WEEKS
    });
    const baseBreads = bbBreadCount(d.items);
    const basePrice = priceDrop({
      items: c.baseItems,
      fulfillment: d.ful,
      band: bandForMin || 15,
      s: BB_S,
      catalog: BB_CAT,
      feeAed: c.feeUnknown ? 0 : undefined
    });
    const minB = minBreads(d.ful, bandForMin || 15, BB_S);
    const winBadges = bbBadgesFor(c.drops.filter(x => !x.slotCustom).map(x => x.day), baseBreads, cap && cap.cap);
    const weekendDays = [5, 6].map(w => bcWeekdayName(w, L)).join(' + ');
    const freqs = [['weekly', 'b.f.weekly', t('b.f.weeklyS')], ['biweekly', 'b.f.biweekly', t('b.f.biweeklyS')], ['weekend', 'b.f.weekend', weekendDays], ['dates', 'b.f.dates', t('b.f.datesS')]];
    const sel = c.days;
    const sheetDrop = sheet && sheet.kind === 'day' ? c.drops.find(x => x.day === sheet.day) : null;
    const ctaLabel = canPay ? tn('b.pay', {
      price: fmtAed(T.due)
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, t('b.continue'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-arrow",
      "aria-hidden": "true"
    }, "\u2192"));
    return /*#__PURE__*/React.createElement("div", {
      className: "bc-app bc-builder"
    }, /*#__PURE__*/React.createElement("header", {
      className: "bc-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-kicker"
    }, t('b.kicker')), /*#__PURE__*/React.createElement("h1", {
      className: "bc-title"
    }, t('b.title')), /*#__PURE__*/React.createElement("p", {
      className: "bc-lead"
    }, t('b.lead', {
      weeks: weeksTxt
    })), /*#__PURE__*/React.createElement("ul", {
      className: "bc-values"
    }, /*#__PURE__*/React.createElement("li", null, t('b.v1', {
      pct: BB_S.bread_discount_pct
    })), /*#__PURE__*/React.createElement("li", null, t('b.v2')), /*#__PURE__*/React.createElement("li", null, t('b.v3', {
      time: bcHourWord(BB_S.cutoff_hour)
    })), /*#__PURE__*/React.createElement("li", null, t('b.v4')))), /*#__PURE__*/React.createElement(SavedPlansCard, {
      onFind: () => setFindOpen(true)
    }), d.ref ? /*#__PURE__*/React.createElement("div", {
      className: "bc-note bc-started",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", null, tn('b.started', {
      ref: /*#__PURE__*/React.createElement("bdi", {
        dir: "ltr",
        className: "ltr-num"
      }, d.ref)
    })), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-btn bc-btn--link",
      onClick: freshCheckout
    }, t('b.newPlan'))) : null, /*#__PURE__*/React.createElement(BbSec, {
      n: 1,
      id: "bc-s1",
      title: t('b.s1'),
      hint: t('b.s1h'),
      ok: ok[1]
    }, /*#__PURE__*/React.createElement(BbItemsEditor, {
      items: d.items,
      onChange: setItems,
      extrasOpen: extrasOpen,
      setExtrasOpen: setExtrasOpen
    }), /*#__PURE__*/React.createElement("div", {
      className: "bc-perdrop",
      "aria-live": "polite"
    }, /*#__PURE__*/React.createElement("span", null, tn('b.perDrop', {
      breads: t('n.breads', {
        n: baseBreads
      }),
      price: fmtAed(filsToAed(basePrice.total_fils))
    })), !c.feeUnknown && baseBreads > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "bc-perloaf"
    }, tn(d.ful === 'pickup' ? 'b.perLoafP' : 'b.perLoaf', {
      price: fmtAed(Math.round(basePrice.total_fils / baseBreads) / 100)
    })) : null), baseIssues.map((is, i) => /*#__PURE__*/React.createElement(IssueLine, {
      key: i,
      issue: is,
      ctx: ctx,
      fix: is.reason === 'min_breads' ? bumpBase : null,
      fixLabel: t('b.fix.min', {
        n: minB
      })
    }))), /*#__PURE__*/React.createElement(BbSec, {
      n: 2,
      id: "bc-s2",
      title: t('b.s2'),
      ok: ok[2]
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-chips--2 bc-freq",
      role: "radiogroup",
      "aria-label": t('b.s2')
    }, freqs.map(([k, key, sub]) => /*#__PURE__*/React.createElement(Chip, {
      key: k,
      radio: true,
      on: d.freq === k,
      onClick: () => setFreq(k),
      sub: sub
    }, t(key)))), c.weekly ? /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-label",
      id: "bc-wd-l"
    }, t('b.days')), /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-wd",
      role: "group",
      "aria-labelledby": "bc-wd-l"
    }, bbWeekOrder(L).map(wd => /*#__PURE__*/React.createElement(Chip, {
      key: wd,
      on: bbWeekdays(d).indexOf(wd) >= 0,
      onClick: () => toggleWeekday(wd)
    }, bcWeekdayName(wd, L))))) : /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-label"
    }, t('b.calH')), /*#__PURE__*/React.createElement(BbCal, {
      start: c.start,
      selected: sel,
      nowMs: now,
      onToggle: toggleDay
    })), /*#__PURE__*/React.createElement("div", {
      className: "bc-start"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-start-l"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-start-k"
    }, t(d.ful === 'pickup' ? 'b.startP' : 'b.start')), /*#__PURE__*/React.createElement("span", {
      className: "bc-start-v"
    }, c.days[0] ? fmtDay(c.days[0], L, {
      weekday: 'long',
      month: 'long'
    }) : fmtDay(c.start, L, {
      weekday: 'long',
      month: 'long'
    })), /*#__PURE__*/React.createElement("span", {
      className: "bc-start-c"
    }, t('b.cover', {
      from: fmtDay(c.cyc.start),
      to: fmtDay(c.cyc.end),
      weeks: weeksTxt
    }))), /*#__PURE__*/React.createElement(Btn, {
      kind: "ghost",
      className: "bc-start-btn",
      onClick: () => setSheet({
        kind: 'start'
      })
    }, t('b.startLater'))), /*#__PURE__*/React.createElement("p", {
      className: "bc-note"
    }, t('b.earliest', {
      time: bbOrderByText(),
      day: fmtDay(c.earliest)
    })), c.moved ? /*#__PURE__*/React.createElement("p", {
      className: "bc-note bc-note--warn",
      role: "status"
    }, t('b.moved', {
      time: bbOrderByText(),
      day: fmtDay(c.start)
    })) : null, c.blackout.length ? /*#__PURE__*/React.createElement("p", {
      className: "bc-note"
    }, t('b.blackout', {
      days: c.blackout.map(x => fmtDay(x)).join(L === 'en' ? ', ' : '، ')
    })) : null, planIssues.filter(is => ['no_weekdays', 'no_drops', 'min_drops'].indexOf(is.reason) >= 0).map((is, i) => {
      const f = planFix(is);
      return /*#__PURE__*/React.createElement(IssueLine, {
        key: i,
        issue: is,
        ctx: ctx,
        fix: f && f.fn,
        fixLabel: f && f.label
      });
    })), /*#__PURE__*/React.createElement(BbSec, {
      n: 3,
      id: "bc-s3",
      title: /*#__PURE__*/React.createElement(React.Fragment, null, t('b.s3'), " ", BB_NOT_DUBAI_TZ ? /*#__PURE__*/React.createElement("span", {
        className: "bc-opt"
      }, t('c.dubaiTime')) : null),
      hint: t(d.ful === 'pickup' ? 'b.winHP' : 'b.winH'),
      ok: ok[3]
    }, /*#__PURE__*/React.createElement(BbWindows, {
      value: d.slot,
      onChange: slot => up({
        slot
      }),
      badges: winBadges
    }), c.days.some(x => BB_S.weekend_days.indexOf(ymdWeekday(x)) >= 0) ? /*#__PURE__*/React.createElement("p", {
      className: "bc-small"
    }, t('b.wkndNote')) : null), /*#__PURE__*/React.createElement(BbSec, {
      n: 4,
      id: "bc-s4",
      title: t(d.ful === 'pickup' ? 'b.s4p' : 'b.s4'),
      hint: c.days.length ? t('b.s4h') : null,
      ok: ok[4]
    }, c.days.length ? /*#__PURE__*/React.createElement("div", {
      className: "bc-rows-h"
    }, /*#__PURE__*/React.createElement("span", null, t('n.deliveries', {
      n: c.days.length
    })), /*#__PURE__*/React.createElement("span", {
      className: "bc-rows-r"
    }, fmtDay(c.cyc.start), " \u2013 ", fmtDay(c.cyc.end))) : null, /*#__PURE__*/React.createElement("ul", {
      className: "bc-rows"
    }, dropView.map(x => /*#__PURE__*/React.createElement("li", {
      key: x.day,
      className: 'bc-row' + (x.issues.length ? ' has-issue' : '') + (x.custom || x.slotCustom ? ' is-custom' : '')
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-row-in"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-row-main",
      onClick: () => setSheet({
        kind: 'day',
        day: x.day
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-row-l1"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-row-day"
    }, /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true"
    }, fmtDay(x.day)), /*#__PURE__*/React.createElement("span", {
      className: "sf-vh"
    }, fmtDay(x.day, L, {
      weekday: 'long',
      month: 'long'
    })), x.custom || x.slotCustom ? /*#__PURE__*/React.createElement("span", {
      className: "bc-badge bc-row-badge"
    }, t('b.custom')) : null), /*#__PURE__*/React.createElement("span", {
      className: "bc-row-price"
    }, fmtAed(x.total))), /*#__PURE__*/React.createElement("span", {
      className: "bc-row-l2"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-row-meta"
    }, fmtWindow(x.slot), BB_SEP, itemsSummary(x.items)), /*#__PURE__*/React.createElement("span", {
      className: "bc-row-edit"
    }, t('b.change'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-arrow",
      "aria-hidden": "true"
    }, "\u203A")))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-row-rm",
      onClick: () => removeDay(x.day),
      "aria-label": t('b.removeA', {
        day: fmtDay(x.day, L, {
          weekday: 'long',
          month: 'long'
        })
      })
    }, "\u2715")), x.issues.map((is, i) => {
      const f = rowFix(x, is);
      return /*#__PURE__*/React.createElement(IssueLine, {
        key: i,
        issue: is,
        ctx: Object.assign({}, ctx, {
          slot: x.slot,
          row: true
        }),
        fix: f.fn,
        fixLabel: f.label
      });
    })))), !c.days.length ? /*#__PURE__*/React.createElement("p", {
      className: "bc-empty"
    }, t('b.noDays')) : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-btn bc-btn--ghost bc-addday",
      onClick: () => setSheet({
        kind: 'days'
      })
    }, "\uFF0B ", t('b.addDay')), c.feeUnknown && c.days.length ? /*#__PURE__*/React.createElement("p", {
      className: "bc-small"
    }, t('b.areaFirst')) : null, planIssues.filter(is => ['no_weekdays', 'no_drops', 'min_drops'].indexOf(is.reason) < 0 || flag[4]).map((is, i) => {
      const f = planFix(is);
      return /*#__PURE__*/React.createElement(IssueLine, {
        key: 'p' + i,
        issue: is,
        ctx: ctx,
        fix: f && f.fn,
        fixLabel: f && f.label
      });
    })), /*#__PURE__*/React.createElement(BbSec, {
      n: 5,
      id: "bc-s5",
      title: t('b.s5'),
      ok: ok[5]
    }, BB_HAS_DEL && BB_HAS_PICK ? /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-chips--2 bc-ful",
      role: "radiogroup",
      "aria-label": t('b.s5')
    }, /*#__PURE__*/React.createElement(Chip, {
      radio: true,
      on: d.ful === 'delivery',
      onClick: () => up({
        ful: 'delivery'
      }),
      sub: t('b.ful.deliveryS')
    }, "\uD83D\uDEF5 ", t('b.ful.delivery')), /*#__PURE__*/React.createElement(Chip, {
      radio: true,
      on: d.ful === 'pickup',
      onClick: () => up({
        ful: 'pickup'
      }),
      sub: t('b.ful.pickupS')
    }, "\uD83E\uDD56 ", t('b.ful.pickup'))) : null, d.ful === 'delivery' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AddressForm, {
      value: d.addr,
      onChange: a => up({
        addr: a
      }),
      errors: addrErrs,
      showErrors: !!flag[5]
    }), c.area && baseIssues.some(is => is.reason === 'min_breads') ? /*#__PURE__*/React.createElement(IssueLine, {
      issue: {
        reason: 'min_breads',
        suggestion: {
          min: minB
        }
      },
      ctx: ctx,
      fix: bumpBase,
      fixLabel: t('b.fix.min', {
        n: minB
      })
    }) : null, /*#__PURE__*/React.createElement(BbGift, {
      gift: d.gift,
      onChange: g => up({
        gift: g
      }),
      showErr: !!flag[5],
      srv: sf
    })) : /*#__PURE__*/React.createElement("p", {
      className: "bc-note"
    }, t('b.pickupNote'))), /*#__PURE__*/React.createElement(BbSec, {
      n: 6,
      id: "bc-s6",
      title: t('b.s6'),
      ok: ok[6] && allOk
    }, /*#__PURE__*/React.createElement("div", {
      className: 'bc-field' + (flag[6] && !nameOk ? ' is-err' : '')
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-name"
    }, t('b.name'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-req",
      "aria-hidden": "true"
    }, "*")), /*#__PURE__*/React.createElement("input", {
      id: "bc-name",
      className: 'bc-input' + (flag[6] && !nameOk ? ' is-err' : ''),
      type: "text",
      autoComplete: "name",
      maxLength: 60,
      value: d.name,
      onChange: e => up({
        name: e.target.value
      }),
      "aria-invalid": flag[6] && !nameOk ? 'true' : undefined
    }), flag[6] && !nameOk ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t('b.nameErr')) : null), /*#__PURE__*/React.createElement("div", {
      className: 'bc-field' + (flag[6] && !phoneOk ? ' is-err' : '')
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-phone"
    }, t('ph.label'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-req",
      "aria-hidden": "true"
    }, "*")), /*#__PURE__*/React.createElement(PhoneInput, {
      id: "bc-phone",
      value: d.phone,
      onChange: v => up({
        phone: v
      }),
      invalid: flag[6] && !phoneOk,
      describedBy: "bc-phone-err",
      onBlur: () => {
        if (d.phone && !phoneOk) setFlag(f => Object.assign({}, f, {
          6: 1
        }));
      }
    }), flag[6] && !phoneOk ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      id: "bc-phone-err",
      role: "alert"
    }, bcPhoneErr()) : null, phoneOk ? /*#__PURE__*/React.createElement("div", {
      className: "bc-linkto"
    }, /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true"
    }, "\uD83D\uDCAC"), " ", /*#__PURE__*/React.createElement("span", null, tn('b.linkTo', {
      phone: fmtPhone(phoneE164(d.phone))
    }), ". ", t('b.linkCheck'))) : null), /*#__PURE__*/React.createElement("div", {
      className: 'bc-field' + (emailBad ? ' is-err' : '')
    }, /*#__PURE__*/React.createElement("label", {
      className: "bc-label",
      htmlFor: "bc-email"
    }, t('b.email'), " ", /*#__PURE__*/React.createElement("span", {
      className: "bc-opt"
    }, "(", t('b.optional'), BB_SEP, t('b.emailS'), ")")), /*#__PURE__*/React.createElement("input", {
      id: "bc-email",
      className: 'bc-input bc-ltr' + (emailBad ? ' is-err' : ''),
      type: "email",
      inputMode: "email",
      autoComplete: "email",
      dir: "ltr",
      maxLength: 120,
      value: d.email,
      onChange: e => up({
        email: e.target.value
      }),
      onBlur: () => setEmailBlur(true),
      "aria-invalid": emailBad ? 'true' : undefined
    }), emailBad ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err",
      role: "alert"
    }, t('b.emailErr')) : null), /*#__PURE__*/React.createElement("div", {
      className: "bc-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-label",
      id: "bc-rem-l"
    }, t('b.remind')), /*#__PURE__*/React.createElement("div", {
      className: "bc-chips bc-remind",
      role: "radiogroup",
      "aria-labelledby": "bc-rem-l"
    }, ['auto', 'each', 'none'].map(k => /*#__PURE__*/React.createElement(Chip, {
      key: k,
      radio: true,
      on: d.remind === k,
      onClick: () => up({
        remind: k
      }),
      sub: t('b.r.' + k + 'S')
    }, t('b.r.' + k))))), /*#__PURE__*/React.createElement("label", {
      className: "bc-check bc-check--sm"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!d.optin,
      onChange: e => up({
        optin: e.target.checked
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "bc-check-box",
      "aria-hidden": "true"
    }, "\u2713"), /*#__PURE__*/React.createElement("span", {
      className: "bc-check-txt"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-check-s"
    }, t('b.optin')))), /*#__PURE__*/React.createElement("div", {
      className: "bc-totals",
      "aria-live": "polite"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-tot-row"
    }, /*#__PURE__*/React.createElement("span", null, t('b.t.goods')), /*#__PURE__*/React.createElement("span", null, fmtAed(T.goods))), d.ful === 'delivery' ? /*#__PURE__*/React.createElement("div", {
      className: "bc-tot-row"
    }, /*#__PURE__*/React.createElement("span", null, t('b.t.fee', {
      deliveries: t('n.deliveries', {
        n: T.drops
      })
    })), /*#__PURE__*/React.createElement("span", null, c.feeUnknown ? /*#__PURE__*/React.createElement("span", {
      className: "bc-tot-q"
    }, t('b.plusDel')) : T.fee > 0 ? fmtAed(T.fee) : t('c.free'))) : null, /*#__PURE__*/React.createElement("div", {
      className: "bc-tot-row"
    }, /*#__PURE__*/React.createElement("span", null, t('b.t.vat')), /*#__PURE__*/React.createElement("span", null, fmtAed(T.vat))), /*#__PURE__*/React.createElement("div", {
      className: "bc-tot-row bc-tot-sum"
    }, /*#__PURE__*/React.createElement("span", null, t('b.t.total', {
      weeks: weeksTxt
    })), /*#__PURE__*/React.createElement("span", null, fmtAed(T.total))), T.savings > 0 ? /*#__PURE__*/React.createElement("div", {
      className: "bc-tot-save"
    }, tn('b.t.save', {
      price: fmtAed(T.savings)
    })) : null, quoteBusy ? /*#__PURE__*/React.createElement("div", {
      className: "bc-tot-busy"
    }, /*#__PURE__*/React.createElement(Spinner, {
      small: true
    }), " ", /*#__PURE__*/React.createElement("span", null, t('b.t.checking'))) : null), /*#__PURE__*/React.createElement("div", {
      className: "bc-keep"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-keep-t"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-keep-ic",
      "aria-hidden": "true"
    }, "\u21BB"), t('b.keepT', {
      weeks: weeksTxt
    })), /*#__PURE__*/React.createElement("p", {
      className: "bc-keep-s"
    }, t('b.keepNote', {
      weeks: weeksTxt,
      days: t('n.days', {
        n: BB_S.renewal_open_days_before
      }),
      time: bcHourWord(BB_S.cutoff_hour),
      max: bbMaxPause()
    }))), /*#__PURE__*/React.createElement("ul", {
      className: "bc-trust"
    }, /*#__PURE__*/React.createElement("li", null, t('b.trust1', {
      weeks: weeksTxt
    })), /*#__PURE__*/React.createElement("li", null, t('b.trust2', {
      time: bcHourWord(BB_S.cutoff_hour)
    })), /*#__PURE__*/React.createElement("li", null, t('b.trust3', {
      max: bbMaxPause()
    }))), payErr ? /*#__PURE__*/React.createElement("div", {
      className: "bc-err bc-payerr",
      role: "alert"
    }, t(payErr), payErr === 'b.e.cid' ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bc-btn bc-btn--link bc-payerr-act",
      onClick: freshCheckout
    }, t('b.e.cidBtn')) : null, payErr === 'b.e.payCheck' ? /*#__PURE__*/React.createElement("a", {
      className: "bc-btn bc-btn--link bc-payerr-act",
      href: '/bread/plan?lang=' + L,
      onClick: e => bbOpenPlan(e, d.ref)
    }, t('b.openPlan')) : null) : null, /*#__PURE__*/React.createElement(Btn, {
      className: "bc-paybtn",
      busy: paying,
      onClick: () => {
        setFlag({
          1: 1,
          2: 1,
          3: 1,
          4: 1,
          5: 1,
          6: 1
        });
        pay();
      }
    }, tn('b.pay', {
      price: fmtAed(T.due)
    })), /*#__PURE__*/React.createElement("p", {
      className: "bc-small bc-paynote"
    }, "\uD83D\uDD12 ", t('b.payNote', {
      weeks: weeksTxt
    }))), /*#__PURE__*/React.createElement("div", {
      className: 'bc-bar' + (typing || sheet || findOpen || paying ? ' is-hidden' : ''),
      role: "region",
      "aria-label": t('b.kicker')
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-bar-txt",
      "aria-live": "polite"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bc-bar-l1"
    }, t('b.barSum', {
      deliveries: t('n.deliveries', {
        n: c.days.length
      }),
      breads: t('n.breads', {
        n: loc.breads
      })
    })), /*#__PURE__*/React.createElement("span", {
      className: "bc-bar-l2"
    }, fmtAed(T.due), c.feeUnknown && c.days.length ? /*#__PURE__*/React.createElement("span", {
      className: "bc-bar-plus"
    }, " ", t('b.plusDel')) : null)), /*#__PURE__*/React.createElement(Btn, {
      className: "bc-bar-btn",
      busy: paying,
      onClick: next
    }, ctaLabel)), sheetDrop ? /*#__PURE__*/React.createElement(BbDaySheet, {
      key: sheetDrop.day,
      drop: sheetDrop,
      d: d,
      ctx: ctx,
      onClose: () => setSheet(null),
      onSave: saveDay,
      onRemove: removeDay,
      onReset: resetDay
    }) : null, /*#__PURE__*/React.createElement(BbStartSheet, {
      open: !!sheet && sheet.kind === 'start',
      earliest: c.earliest,
      start: c.start,
      nowMs: now,
      onPick: setStart,
      onClose: () => setSheet(null)
    }), /*#__PURE__*/React.createElement(BbDaysSheet, {
      open: !!sheet && sheet.kind === 'days',
      start: c.start,
      selected: sel,
      nowMs: now,
      onToggle: toggleDay,
      onClose: () => setSheet(null)
    }), /*#__PURE__*/React.createElement(FindPlanSheet, {
      open: findOpen,
      onClose: () => setFindOpen(false)
    }), paying ? /*#__PURE__*/React.createElement("div", {
      className: "bc-pay-ov",
      role: "alertdialog",
      "aria-modal": "true",
      "aria-live": "assertive",
      "aria-label": t('b.paying')
    }, /*#__PURE__*/React.createElement("div", {
      className: "bc-pay-box"
    }, /*#__PURE__*/React.createElement(Spinner, null), /*#__PURE__*/React.createElement("p", {
      className: "bc-pay-t"
    }, t('b.paying')), stalled ? /*#__PURE__*/React.createElement("div", {
      className: "bc-pay-stuck"
    }, /*#__PURE__*/React.createElement("span", null, t('b.payStuck')), /*#__PURE__*/React.createElement(Btn, {
      kind: "ghost",
      onClick: cancelPaying
    }, planUrlRef.current ? t('b.openPlan') : t('c.cancel'))) : null)) : null, toastNode);
  }

  /** Window badges for a set of days: 'full' if any day has no room for `breads`, 'few' if ≤ 2 drops left somewhere. */
  function bbBadgesFor(days, breads, cap) {
    if (!cap || !days.length) return null;
    const out = {};
    BB_S.windows.forEach(w => {
      let full = false,
        few = false,
        known = false;
      days.forEach(day => {
        const c = cap[day] && cap[day][w];
        if (!c) return;
        known = true;
        if (Number(c.left_drops) < 1 || Number(c.left_breads) < breads) full = true;else if (Number(c.left_drops) <= 2 || Number(c.left_breads) < breads * 2) few = true;
      });
      if (known) out[w] = full ? 'full' : few ? 'few' : null;
    });
    return out;
  }

  // ── Styles (builder only; every class bc- prefixed; tokens from PaperLayout's :root) ──
  const BB_CSS = `
.bc-builder{padding-bottom:calc(104px + env(safe-area-inset-bottom,0px));--bc-toast-bottom:92px}
.bc-head{text-align:center;padding:10px 0 4px}
.bc-kicker{font-family:"IM Fell DW Pica SC","IM Fell English",Georgia,serif;font-size:13px;letter-spacing:.22em;text-transform:uppercase;color:var(--seal,#8a1c1c)}
html[dir="rtl"] .bc-kicker{font-family:"Vazirmatn",sans-serif;font-size:14px;font-weight:700;text-transform:none}
.bc-title{font-family:"IM Fell English",Georgia,serif;font-weight:400;font-size:30px;line-height:1.12;margin:4px 0 6px;color:var(--ink,#221408)}
html[dir="rtl"] .bc-title{font-family:"Vazirmatn",sans-serif;font-weight:800;font-size:23px;line-height:1.45}
html[lang="ar"] .bc-title{font-family:"Amiri","Vazirmatn",serif;font-weight:700;font-size:27px}
.bc-lead{margin:0 auto 10px;max-width:520px;color:var(--ink-soft,#553418);font-size:17px}
html[dir="rtl"] .bc-lead{font-size:15px}
.bc-values{list-style:none;margin:0 auto;padding:0;display:flex;flex-wrap:wrap;justify-content:center;gap:6px;max-width:560px}
.bc-values li{display:inline-flex;align-items:center;gap:6px;padding:4px 11px;border:1px solid rgba(160,123,47,.55);border-radius:999px;background:rgba(255,250,238,.7);font-size:14.5px;line-height:1.3;color:var(--ink,#221408)}
html[dir="rtl"] .bc-values li{font-size:13.5px}
.bc-values li::before{content:"✓";color:var(--gold,#a07b2f);font-weight:800}
.bc-builder .bc-saved, .bc-builder .bc-find-line{margin-top:10px}
/* sections */
.bc-sec{padding:18px 0 6px;margin-top:14px;border-top:1px solid rgba(70,40,15,.28);scroll-margin-top:10px}
.bc-sec-top{display:flex;align-items:center;gap:10px}
.bc-sec-num{flex:0 0 30px;width:30px;height:30px;border-radius:50%;border:1.5px solid var(--seal,#8a1c1c);color:var(--seal,#8a1c1c);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:15px;font-family:"Cormorant Garamond",Georgia,serif;background:#fffaf0}
html[dir="rtl"] .bc-sec-num{font-family:"Vazirmatn",sans-serif;font-size:14px}
.bc-sec-num.ok{background:var(--gold,#a07b2f);border-color:var(--gold,#a07b2f);color:#fff}
.bc-sec-title{margin:0;font-family:"IM Fell English",Georgia,serif;font-weight:400;font-size:23px;line-height:1.2;color:var(--ink,#221408)}
html[dir="rtl"] .bc-sec-title{font-family:"Vazirmatn",sans-serif;font-weight:700;font-size:18px}
html[lang="ar"] .bc-sec-title{font-family:"Amiri","Vazirmatn",serif;font-size:21px}
.bc-sec-title .bc-opt{font-family:"Cormorant Garamond",Georgia,serif;font-size:15px}
html[dir="rtl"] .bc-sec-title .bc-opt{font-family:"Vazirmatn",sans-serif;font-size:13px;font-weight:400}
.bc-sec-hint{margin:4px 0 4px 40px;color:var(--ink-soft,#553418);font-size:15.5px;line-height:1.4}
html[dir="rtl"] .bc-sec-hint{margin:4px 40px 4px 0;font-size:14px}
.bc-label--sp{margin-top:16px}
.bc-note--warn{border-inline-start-color:var(--seal,#8a1c1c)}
/* items */
.bc-items{margin-top:8px}
.bc-item{display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px dotted rgba(70,40,15,.3)}
.bc-item-img{flex:0 0 56px;width:56px;height:56px;border-radius:10px;object-fit:cover;border:1px solid rgba(70,40,15,.2);background:#e2d2ad}
.bc-item-txt{flex:1 1 auto;min-width:0}
.bc-item-name{font-size:19px;font-weight:700;line-height:1.2}
html[dir="rtl"] .bc-item-name{font-size:16px}
.bc-item-desc{font-size:14.5px;color:var(--ink-soft,#553418);line-height:1.3;margin-top:1px}
html[dir="rtl"] .bc-item-desc{font-size:13px}
.bc-item-price{margin-top:3px;font-size:15px;color:var(--ink,#221408)}
html[dir="rtl"] .bc-item-price{font-size:13.5px}
.bc-item-menu{color:var(--ink-soft,#553418);margin-inline-end:6px;text-decoration-thickness:1.5px}
.bc-item-menu .bc-aed{font-size:.86em}
.bc-item-club{color:var(--seal,#8a1c1c);font-weight:700}
.bc-item--sm{padding:6px 0}
.bc-item--sm .bc-item-name{font-size:17px;font-weight:600}
html[dir="rtl"] .bc-item--sm .bc-item-name{font-size:15px}
.bc-item.is-off{opacity:.7}
.bc-extras{margin-top:8px;border:1px solid rgba(70,40,15,.22);border-radius:12px;background:rgba(255,250,238,.55)}
.bc-extras-toggle{display:flex;align-items:center;gap:10px;width:100%;min-height:52px;padding:8px 12px;border:0;background:transparent;color:var(--ink,#221408);text-align:start;cursor:pointer}
.bc-extras-t{flex:1 1 auto;display:flex;flex-direction:column;min-width:0}
.bc-extras-h{font-weight:700;font-size:17px}
html[dir="rtl"] .bc-extras-h{font-size:15px}
.bc-extras-s{font-size:14px;color:var(--ink-soft,#553418);line-height:1.3}
html[dir="rtl"] .bc-extras-s{font-size:12.5px}
.bc-extras-caret{flex:0 0 auto;font-size:16px;color:var(--ink-soft,#553418);transition:transform .15s}
.bc-extras.open .bc-extras-caret{transform:rotate(180deg)}
.bc-extras-list{padding:0 12px 4px}
.bc-extras-list .bc-item:last-child{border-bottom:0}
.bc-perdrop{display:flex;flex-wrap:wrap;justify-content:space-between;gap:2px 12px;margin-top:10px;font-size:16px;font-weight:600}
html[dir="rtl"] .bc-perdrop{font-size:14px}
.bc-perloaf{font-weight:400;color:var(--ink-soft,#553418)}
/* issues */
.bc-issue{display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;margin-top:8px;padding:8px 10px;border-radius:10px;background:rgba(138,28,28,.07);border:1px solid rgba(138,28,28,.32);color:var(--seal-deep,#5b1010);font-size:15px;line-height:1.35}
html[dir="rtl"] .bc-issue{font-size:13.5px}
.bc-issue-ic{flex:0 0 20px;width:20px;height:20px;border-radius:50%;background:var(--seal,#8a1c1c);color:#fff;font-weight:800;font-size:13px;display:flex;align-items:center;justify-content:center}
.bc-issue-txt{flex:1 1 160px;min-width:0;font-weight:600}
.bc-issue-fix{flex:0 0 auto;min-height:44px;padding:6px 14px;border-radius:999px;border:1.5px solid var(--seal,#8a1c1c);background:#fffaf0;color:var(--seal,#8a1c1c);font-weight:700;font-size:14.5px;cursor:pointer;margin-inline-start:auto}
/* frequency + weekdays */
.bc-freq{margin-top:10px}
.bc-freq .bc-chip{min-height:58px}
.bc-wd{display:grid;grid-template-columns:repeat(4,1fr);gap:6px}
.bc-wd .bc-chip{padding:6px 4px;font-size:15px}
html[dir="rtl"] .bc-wd .bc-chip{font-size:13.5px}
.bc-start{display:flex;align-items:center;flex-wrap:wrap;gap:8px 12px;margin-top:14px;padding:10px 12px;border-radius:12px;background:rgba(255,250,238,.75);border:1px solid rgba(70,40,15,.22)}
.bc-start-l{flex:1 1 180px;display:flex;flex-direction:column;min-width:0}
.bc-start-k{font-size:13.5px;color:var(--ink-soft,#553418)}
.bc-start-v{font-size:18px;font-weight:700;line-height:1.3}
html[dir="rtl"] .bc-start-v{font-size:15.5px}
.bc-start-c{font-size:14px;color:var(--ink-soft,#553418)}
html[dir="rtl"] .bc-start-c{font-size:12.5px}
.bc-start-btn{min-height:44px;padding:8px 14px;font-size:15px}
.bc-starts .bc-chip{font-size:14.5px;padding:6px 4px}
html[dir="rtl"] .bc-starts{grid-template-columns:1fr 1fr}
html[dir="rtl"] .bc-starts .bc-chip{font-size:13.5px}
/* calendar */
.bc-cal{margin-top:4px}
.bc-cal-head,.bc-cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:2px}
.bc-cal-wd{text-align:center;font-size:12.5px;color:var(--ink-soft,#553418);padding:2px 0 4px;font-weight:600}
.bc-cal-blank{min-height:46px}
.bc-cal-d{position:relative;min-height:46px;min-width:0;padding:0;border:1px solid rgba(70,40,15,.25);border-radius:8px;background:#fffaf0;color:var(--ink,#221408);font:inherit;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1.05;touch-action:manipulation}
.bc-cal-n{font-size:17px;font-weight:600}
.bc-cal-m{font-size:10.5px;color:var(--ink-soft,#553418);font-weight:700;text-transform:uppercase;letter-spacing:.03em}
.bc-cal-d.on{background:var(--ink,#221408);border-color:var(--ink,#221408);color:var(--paper-1,#ece0c4)}
.bc-cal-d.on .bc-cal-m{color:#f5dca0}
.bc-cal-d:disabled{opacity:.35;cursor:not-allowed;background:transparent}
.bc-cal-d.closed{text-decoration:line-through}
/* windows */
.bc-wins{margin-top:10px}
.bc-wins .bc-chip{min-height:62px;padding:6px 8px}
.bc-wins .bc-chip-main{font-weight:700;font-size:16px}
html[dir="rtl"] .bc-wins .bc-chip-main{font-size:14px}
/* review rows */
.bc-rows-h{display:flex;justify-content:space-between;gap:8px;margin:8px 0 4px;font-weight:700;font-size:15.5px}
.bc-rows-r{font-weight:400;color:var(--ink-soft,#553418)}
html[dir="rtl"] .bc-rows-h{font-size:14px}
.bc-rows{list-style:none;margin:0;padding:0}
.bc-row{margin:0 0 8px;padding:0;border:1px solid rgba(70,40,15,.25);border-radius:12px;background:rgba(255,250,238,.85)}
.bc-row.has-issue{border-color:rgba(138,28,28,.55);padding-bottom:8px}
.bc-row.has-issue .bc-issue{margin:6px 8px 0}
.bc-row-in{display:flex;align-items:stretch}
.bc-row-main{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:2px;padding:10px 12px;border:0;background:transparent;color:var(--ink,#221408);text-align:start;cursor:pointer;font:inherit;min-height:60px;border-radius:12px}
.bc-row-l1,.bc-row-l2{display:flex;justify-content:space-between;align-items:baseline;gap:8px}
.bc-row-day{font-weight:700;font-size:17.5px}
html[dir="rtl"] .bc-row-day{font-size:15px}
.bc-row-badge{margin-inline-start:8px;vertical-align:2px}
.bc-row-price{font-weight:700;flex:0 0 auto}
.bc-row-meta{font-size:15px;color:var(--ink-soft,#553418);min-width:0}
html[dir="rtl"] .bc-row-meta{font-size:13px}
.bc-row-edit{flex:0 0 auto;color:var(--seal,#8a1c1c);font-weight:700;font-size:14.5px}
html[dir="rtl"] .bc-row-edit{font-size:13px}
.bc-row-rm{flex:0 0 46px;width:46px;border:0;border-inline-start:1px dotted rgba(70,40,15,.3);background:transparent;color:var(--ink-soft,#553418);font-size:17px;cursor:pointer;border-radius:0 12px 12px 0}
html[dir="rtl"] .bc-row-rm{border-radius:12px 0 0 12px}
.bc-row-rm:active{background:rgba(138,28,28,.08)}
.bc-empty{margin:8px 0;padding:12px;border:1px dashed rgba(70,40,15,.35);border-radius:12px;text-align:center;color:var(--ink-soft,#553418)}
.bc-addday{width:100%;margin-top:4px}
/* where */
.bc-ful{margin:10px 0 4px}
.bc-ful .bc-chip{min-height:58px}
.bc-check{display:flex;align-items:flex-start;gap:10px;margin-top:16px;padding:10px 12px;border:1px solid rgba(70,40,15,.25);border-radius:12px;background:rgba(255,250,238,.6);cursor:pointer;position:relative}
.bc-check input{position:absolute;opacity:0;width:1px;height:1px;pointer-events:none}
.bc-check-box{flex:0 0 26px;width:26px;height:26px;border:1.5px solid rgba(70,40,15,.55);border-radius:7px;background:#fffaf0;color:transparent;display:flex;align-items:center;justify-content:center;font-weight:800;margin-top:1px}
.bc-check input:checked + .bc-check-box{background:var(--ink,#221408);border-color:var(--ink,#221408);color:var(--paper-1,#ece0c4)}
.bc-check input:focus-visible + .bc-check-box{outline:3px solid rgba(160,123,47,.85);outline-offset:2px}
.bc-check-txt{display:flex;flex-direction:column;min-width:0}
.bc-check-t{font-weight:700}
.bc-check-s{font-size:14.5px;color:var(--ink-soft,#553418);line-height:1.35}
html[dir="rtl"] .bc-check-s{font-size:13px}
.bc-check--sm{margin-top:12px;border-style:dashed;background:transparent}
.bc-gift-f{padding:0 2px}
/* pay */
.bc-linkto{margin-top:8px;padding:8px 10px;border-radius:10px;background:rgba(47,107,42,.08);border:1px solid rgba(47,107,42,.3);font-size:15px;line-height:1.4}
html[dir="rtl"] .bc-linkto{font-size:13.5px}
.bc-ltr{direction:ltr;text-align:left}
html[dir="rtl"] .bc-ltr{text-align:right}
.bc-remind{display:grid;grid-template-columns:1fr;gap:6px}
.bc-remind .bc-chip{flex-direction:column;align-items:flex-start;text-align:start;min-height:52px;padding:8px 12px}
.bc-totals{margin-top:16px;padding:12px 14px;border-radius:12px;background:#fffaf0;border:1px solid rgba(70,40,15,.3);box-shadow:0 1px 0 rgba(70,40,15,.08)}
.bc-tot-row{display:flex;justify-content:space-between;gap:12px;padding:3px 0;font-size:16px}
html[dir="rtl"] .bc-tot-row{font-size:14px}
.bc-tot-sum{margin-top:6px;padding-top:8px;border-top:1px solid rgba(70,40,15,.35);font-weight:800;font-size:19px}
html[dir="rtl"] .bc-tot-sum{font-size:16px}
.bc-tot-q{color:var(--ink-soft,#553418);font-size:14px}
.bc-tot-save{margin-top:6px;color:#2f5a22;font-weight:700;font-size:15px}
html[dir="rtl"] .bc-tot-save{font-size:13.5px}
.bc-tot-busy{margin-top:6px;display:flex;align-items:center;gap:8px;font-size:14px;color:var(--ink-soft,#553418)}
.bc-tot-busy .bc-spin--sm{border-color:rgba(138,28,28,.25);border-top-color:var(--seal,#8a1c1c)}
.bc-keep{margin-top:12px;padding:10px 12px;border-radius:12px;border:1px solid rgba(160,123,47,.55);background:rgba(250,240,212,.7)}
.bc-keep-t{display:flex;align-items:center;gap:8px;font-weight:800;font-size:16px;color:var(--ink,#221408)}
html[dir="rtl"] .bc-keep-t{font-size:14.5px}
.bc-keep-ic{flex:0 0 auto;color:var(--gold,#a07b2f);font-size:17px;line-height:1}
.bc-keep-s{margin:4px 0 0;font-size:14.5px;line-height:1.45;color:var(--ink-soft,#553418)}
html[dir="rtl"] .bc-keep-s{font-size:13px;line-height:1.6}
.bc-trust{list-style:none;margin:12px 0 0;padding:0}
.bc-trust li{position:relative;padding:3px 0;padding-inline-start:24px;font-size:15.5px;line-height:1.4}
html[dir="rtl"] .bc-trust li{font-size:13.5px}
.bc-trust li::before{content:"✓";position:absolute;inset-inline-start:2px;top:3px;color:var(--gold,#a07b2f);font-weight:800}
.bc-payerr{margin-top:12px;padding:10px 12px;border-radius:10px;background:rgba(138,28,28,.07);border:1px solid rgba(138,28,28,.32)}
.bc-payerr-act{display:flex;width:fit-content;margin-top:2px;padding:4px 0}
.bc-paybtn{width:100%;margin-top:14px;min-height:54px;font-size:18px}
.bc-paynote{text-align:center}
/* sticky bar */
.bc-bar{position:fixed;left:0;right:0;bottom:0;z-index:1000;display:flex;align-items:center;gap:12px;padding:8px 16px calc(8px + env(safe-area-inset-bottom,0px));background:rgba(236,224,196,.97);border-top:1.5px solid rgba(70,40,15,.4);box-shadow:0 -6px 18px rgba(34,20,8,.14);transition:transform .2s ease;-webkit-backdrop-filter:none;backdrop-filter:none}
.bc-bar.is-hidden{transform:translateY(110%);pointer-events:none}
.bc-bar-txt{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;line-height:1.2;color:var(--ink,#221408)}
.bc-bar-l1{font-size:14px;color:var(--ink-soft,#553418);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
html[dir="rtl"] .bc-bar-l1{font-size:12.5px}
.bc-bar-l2{font-size:19px;font-weight:800;white-space:nowrap}
.bc-bar-plus{font-size:12.5px;font-weight:600;color:var(--ink-soft,#553418)}
.bc-bar-btn{flex:0 0 auto;min-height:48px;padding:10px 18px;max-width:62%;white-space:nowrap}
html[dir="rtl"] .bc-bar-btn{font-size:15px}
@media (min-width:700px){.bc-bar{left:50%;right:auto;width:640px;transform:translateX(-50%);border-radius:16px 16px 0 0;border-left:1.5px solid rgba(70,40,15,.4);border-right:1.5px solid rgba(70,40,15,.4)}.bc-bar.is-hidden{transform:translate(-50%,110%)}}
/* paying overlay */
.bc-pay-ov{position:fixed;inset:0;z-index:1500;background:rgba(236,224,196,.94);display:flex;align-items:center;justify-content:center;padding:24px}
.bc-pay-box{max-width:340px;text-align:center;display:flex;flex-direction:column;align-items:center;gap:10px}
.bc-pay-t{margin:0;font-size:18px;font-weight:700}
.bc-pay-stuck{display:flex;flex-direction:column;align-items:center;gap:8px;color:var(--ink-soft,#553418)}
/* day sheet */
.bc-day-sum{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font-weight:700;font-size:16px;padding:4px 0 6px;border-bottom:1px solid rgba(70,40,15,.2)}
.bc-day-price{font-size:18px}
.bc-reset{margin-top:8px}
.bc-sheet-foot .bc-btn--ghost{flex:0 0 auto;padding:10px 14px;font-size:15px}
/* closed states */
.bc-started{display:flex;flex-direction:column;align-items:flex-start;gap:2px;margin-top:10px}
.bc-started .bc-btn--link{min-height:44px;padding:4px 0}
.bc-day-sheet .bc-sheet-title{font-family:"Cormorant Garamond",Georgia,serif;font-weight:700;font-size:22px}
html[dir="rtl"] .bc-day-sheet .bc-sheet-title{font-family:"Vazirmatn",sans-serif;font-size:18px}
.bc-closed{text-align:center;padding:22px 18px}
.bc-closed .bc-kicker{margin-bottom:6px}
.bc-closed-acts{display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:6px}
@media (max-width:380px){
  .bc-title{font-size:26px}
  html[dir="rtl"] .bc-title{font-size:21px}
  .bc-bar{gap:8px;padding-left:12px;padding-right:12px}
  .bc-bar-btn{padding:10px 14px}
  .bc-bar-l2{font-size:17px}
  .bc-item{gap:8px}
  .bc-item-img{flex-basis:48px;width:48px;height:48px}
}
@media (prefers-reduced-motion:reduce){.bc-bar{transition:none}}
`;
  injectCss('bc-builder-css', BB_CSS);

  // ── Mount ────────────────────────────────────────────────────────────────────
  (function mountBreadBuilder() {
    const el = document.getElementById('bread-root');
    if (!el) return;
    ReactDOM.createRoot(el).render(/*#__PURE__*/React.createElement(ErrorBoundary, null, /*#__PURE__*/React.createElement(BreadBuilder, null)));
  })();
})();